import type {
  ShellNavTab,
  ShellIntakeDescriptor,
  ShellAssistantDescriptor,
  ShellIntakeField,
  ShellGuidanceAnchor,
  ShellGuidanceDescriptor,
  ShellGuidanceRecipe,
  ShellGuidanceSection,
  ShellGuidanceStep,
  ShellGuidanceTopic,
} from './kernel-contract.js';
import { orderSeamContributions } from './seam-order.js';
import { guidanceProseRoles } from './guidance-prose.js';

/**
 * CAOS-267 / CAOS-306 — the installed-app metadata source.
 *
 * The kernel's `hand-to-shell` boot decision tells the host WHICH shell mastered and WHICH packages
 * inject into its seams, but it does not enumerate the org's installed APPS (the App Launcher's
 * list) or an app's object tabs. The kernel already SERVES that data through its metadata retrieve
 * endpoint (`metadata/retrieve` → the full component set for the active generation); this module is
 * the thin same-origin read of it, projected to exactly what the shell frame needs to render the
 * real workspace:
 *   • the installed apps (with their labels, icons, and tab sets),
 *   • the tab catalog (tab key → label + target object), the package labels, and
 *   • (CAOS-306) the OBJECT CATALOG — each object's display labels, name field, list-view columns,
 *     page-layout sections, and the typed field dictionary + value sets — so the list views render
 *     real columns and record pages render real field sections with humanized labels, formatted
 *     values, and status badges.
 *
 * Same-origin and fail-soft: it goes through the host worker's `/api/*` proxy (which injects the
 * session bearer from the cookie), and returns EMPTY metadata on any failure so a metadata hiccup
 * degrades the frame to "no apps" rather than blanking the whole shell.
 */

/** The metadata retrieve route, same-origin, behind the host BFF proxy (worker.ts strips `/api`). */
export const METADATA_RETRIEVE_PATH = '/api/metadata/retrieve';

/** An installed app, projected for the launcher + the app-scoped nav strip. */
export interface AppMeta {
  readonly key: string;
  readonly label: string;
  readonly icon?: string;
  /** The app's object tabs, resolved to `{ key, label, object }` against the tab catalog. */
  readonly tabs: readonly ShellNavTab[];
  /** The tab the app opens on by default (its key), when declared. */
  readonly landingTab?: string;
  /**
   * CAOS-694/CAOS-342 — the app's own SETTINGS destination: a reference to a deployed `page`
   * component, reached only from the account menu's "Settings" entry (gated on administering
   * THIS app — see {@link SessionIdentity.administers}). Absent ⇒ this app has no settings
   * surface of its own; the entry does not appear for it.
   */
  readonly settingsPage?: string;
  /** Navigation shape: `standard` (single record in place) or `console` (record tabs), when declared. */
  readonly navStyle?: string;
  /**
   * Overflow split (CAOS-297): the first `overflowAfter` tabs stay pinned in the bar; the rest
   * collapse into a "More" overflow menu. Absent ⇒ every tab stays in the bar (today's behaviour).
   */
  readonly overflowAfter?: number;
  /** The app's one-line description, when declared. */
  readonly description?: string;
  /**
   * CAOS-292: whether the app is offered in the app switcher. Absent ⇒ listed (today's behaviour);
   * `false` ⇒ hidden from the switcher and the home cards, while its routes still resolve.
   */
  readonly showInAppSwitcher?: boolean;
  /**
   * CAOS-372: where the app sits in the switcher. Lower comes first; absent ⇒ 0 (the neutral
   * default most apps sit at). CAOS-465 — apps TIED at the same value (whether the declared 0 or
   * an explicit one) settle by their own key, never by which one happened to be installed first;
   * see `orderSeamContributions` in `./seam-order.js`.
   */
  readonly sortOrder?: number;
}

/** One typed field of an object, projected for list/record rendering (CAOS-306). */
export interface FieldMeta {
  /** The authored, object-qualified key, e.g. `account.billing_city`. */
  readonly key: string;
  /** The bare field api name the Data API record is keyed by, e.g. `billing_city`. */
  readonly apiName: string;
  /** The humanized field label, e.g. "Billing City". */
  readonly label: string;
  /**
   * CAOS-410 — administrator-facing documentation of the field's purpose, when authored. Read here
   * rather than only in Setup because it is the org's own account of what the field means, and a
   * surface that can say so beats one that can only show the label again.
   *
   * ── WHY THERE IS NO FIELD-LEVEL GATE ON THIS, WHEN THE KERNEL PUTS ONE ON THE SAME ATTRIBUTE ──
   * A review of the change that added this asked the right question: `data describe` withholds a
   * field's `description` from a caller who cannot READ that field (`api/data-routes.ts`), and this
   * copies it onto every field with no such check. The two are not inconsistent, and the difference
   * is the ROUTE, which is worth writing down because the asymmetry otherwise reads like an
   * oversight and invites somebody to "fix" whichever half they meet second.
   *
   *   • `data describe` is the DATA api. Ordinary users reach it to draw a record or a form, so it
   *     reports every field and marks the ones they may not read `readable: false`. A label already
   *     rides along regardless; a description often IS the sensitive part ("why the employee was let
   *     go" says what `term_reason` only hints at), so it rides with permission to read the field.
   *   • {@link METADATA_RETRIEVE_PATH}, which feeds THIS projection, is `metadata retrieve` —
   *     registered `requiredPermission: 'setup.access'` in `kernel/src/api/metadata-routes.ts`. It
   *     returns each stored component `body` VERBATIM, so this attribute was already on that wire
   *     the moment it existed in the schema, and only an administrator is ever handed it. It is not
   *     an unfiltered org-wide read of everyone's metadata.
   *
   * So a per-field gate here would be gating an administrator against themselves. What must NOT
   * happen is this value leaking onto a path an ordinary user reaches: `rendererField` in
   * `content-source.ts` forwards `helpText` and deliberately not this, and a contract test pins
   * that. Anything new that displays a `description` outside Setup needs its own answer to the
   * question above — it does not inherit one from here.
   */
  readonly description?: string;
  /**
   * CAOS-410 — the text a person sees beside this field's input, when authored.
   *
   * Unlike {@link description} this one is MEANT to reach whoever is filling the field in — that is
   * the whole of AC2 — so it is forwarded to the renderer for every reader of a form. It is form
   * furniture ("Hours, to two decimals"), written for somebody typing rather than about the record.
   */
  readonly helpText?: string;
  /** The storage type: Text | Number | Date | Select | Reference | AutoNumber | Formula | Boolean | … */
  readonly storageType: string;
  /** Whether the field is required on save (from the field's `required` flag). */
  readonly required: boolean;
  /** The format axis (currency | percent | email | phone | url | long | …), when declared. */
  readonly format?: string;
  /** For a Reference field, the target object key (e.g. `account`). */
  readonly referenceTo?: string;
  /** For a Select field, the value-set key (e.g. `vs_quote_status`). */
  readonly valueSet?: string;
  /** For a Formula field, the authored expression (e.g. `` `${record.first_name} ${record.last_name}` ``). */
  readonly formula?: string;
  /**
   * CAOS-365 — the platform keeps this one, not a person. A system field is placed and read like any
   * other value and is never offered for editing: there is no column behind it anybody can fill in.
   */
  readonly system?: boolean;
}

/**
 * CAOS-365 — the SYSTEM fields every record has: when it was created and when it last changed, and
 * CAOS-1704 who owns it.
 *
 * The platform has always recorded all three, but they are not authored fields, so a layout had no
 * way to place them and a record page could not say when it was made or whose it is. They are added
 * to every object's catalog so a layout can name them exactly as it names any other value.
 *
 * They are never editable: nobody writes them, the Engine says so too, and they carry no column a
 * person could fill in. A layout that places one gets a value, not a control.
 */
export const SYSTEM_FIELDS: readonly FieldMeta[] = [
  { key: 'created_at', apiName: 'created_at', label: 'Created', storageType: 'DateTime', required: false, system: true },
  { key: 'updated_at', apiName: 'updated_at', label: 'Last Modified', storageType: 'DateTime', required: false, system: true },
  // CAOS-1704 — the owner. Text rather than Reference because a Reference names an OBJECT and there
  // is no user object to name: the value is the principal's id, served as the identity it is. The
  // storage type says how to render it, not whether it may be edited — `assemblePage` returns
  // 'readonly' for any system field before it looks at the type at all.
  { key: 'owner_id', apiName: 'owner_id', label: 'Owner', storageType: 'Text', required: false, system: true },
];

const SYSTEM_FIELD_NAMES: ReadonlySet<string> = new Set(SYSTEM_FIELDS.map((f) => f.apiName));

/** The heading a record's system fields sit under, on an authored layout and on the default alike. */
export const SYSTEM_SECTION_LABEL = 'System Information';

/**
 * CAOS-360 — one field placed in a layout section, and whether this surface OFFERS it for editing.
 *
 * A layout entry is authored either as a bare field ref, which is every layout written before this,
 * or as `{ field, uiBehavior }`. Both are projected here; a bare one is `offeredForEdit: false`,
 * because an affordance nobody authored should not appear.
 *
 * `offeredForEdit` is never a grant. Permission is resolved separately, from the same access engine
 * the write boundary enforces with, and a field the caller may not write is not offered whatever
 * this says — see `record-write.ts`, whose first rule is that a form never offers what the boundary
 * would silently strip.
 */
export interface LayoutSectionField {
  /** The bare field api name, resolvable against {@link ObjectMeta.fields}. */
  readonly name: string;
  /** True when the layout asked for this field to be editable HERE. Permission still decides. */
  readonly offeredForEdit: boolean;
  /**
   * CAOS-1881 — true when the layout asked for this field to take the whole width of its section
   * rather than one cell of the section's grid. A bare entry is `false`, like `offeredForEdit`
   * above and for the same reason: the short spelling says nothing about presentation.
   *
   * Unlike `offeredForEdit` this is NOT subject to a second answer. Editing needs permission to
   * agree because offering an edit the write boundary would strip is a defect; how wide a value is
   * drawn is a presentation decision with nobody to ask, so what the layout says is what happens.
   */
  readonly fullWidth: boolean;
}

/** A page-layout section: a label, a column count, and the fields it lays out. */
export interface LayoutSection {
  readonly label: string;
  /**
   * How many columns it lays its fields out in — a count, or `'auto'` for "as many as fit the
   * width available" (CAOS-1884). Carried as the author wrote it rather than resolved to a number
   * here: `'auto'` has no number to resolve TO, because the answer depends on the width at paint
   * time and only the stylesheet knows that.
   */
  readonly columns: number | 'auto';
  /** The section's fields, in authored order. */
  readonly fields: readonly LayoutSectionField[];
  /**
   * CAOS-1726 — the template region the layout placed this section into, carried VERBATIM from
   * the manifest. `undefined` means the section named none and falls to the primary region.
   *
   * Carried, not resolved: this projection does not know the object's template, so it never
   * validates or reroutes the key here. Resolving it against the resolved blueprint (and the
   * fail-safe for an undeclared region) is the placement engine's job, which is also what lets
   * one projected layout render under whichever blueprint the bound page later names.
   */
  readonly region?: string;
}

/**
/**
 * CAOS-1726 — one PLATFORM-DRAWN panel the layout placed, and where it put it.
 *
 * The two panels a record surface draws from data no package declares: the activity timeline
 * (CAOS-367) and the field history (CAOS-402). A layout can now say which region each sits in;
 * absent from a layout's `panels` entirely means it said nothing, and the host draws them where
 * it always has.
 */
export interface RecordPanelMeta {
  readonly panel: 'activity' | 'history';
  readonly region?: string;
}

/**
 * CAOS-361 — one ATTACHED SET on a record surface: the child records that point AT this record,
 * rendered as a titled table on the record's Related tab.
 *
 * It is wired off the existing reference model rather than a parallel relationship system:
 * `relationshipField` is the CHILD's own Reference field that points back here, so the set resolves
 * to the query `<relationshipField> = <this record id>`. FLS governs every child row exactly as it
 * governs the child's own list.
 */
export interface RelatedListMeta {
  readonly id: string;
  /** The CHILD object whose records are listed (its key, e.g. `contact`). */
  readonly object: string;
  /** The CHILD's Reference field pointing at this record, as a bare api name (e.g. `account`). */
  readonly relationshipField: string;
  /** Heading override; absent ⇒ the child's plural label. */
  readonly label?: string;
  /** Ordered child columns (bare api names); absent ⇒ the child object's own list columns. */
  readonly columns?: readonly string[];
  /**
   * CAOS-1726 — the template region this attached set was placed into; `undefined` ⇒ none named.
   * `columns` above is the TABLE's columns and says nothing about placement.
   */
  readonly region?: string;
  /**
   * CAOS-407 — buttons drawn on every row, read through the SAME reader a record's own header
   * buttons go through, so the two cannot drift into two dialects of one idea.
   */
  readonly rowActions?: readonly RecordActionMeta[];
  /**
   * CAOS-407 — buttons over the rows a person has ticked. Narrowed to the `update` kind here as
   * well as in the schema: this host has to read metadata written for a kernel that did not have
   * the restriction, and "navigate twelve records somewhere" has no answer worth guessing at.
   */
  readonly selectionActions?: readonly Extract<RecordActionMeta, { readonly kind: 'update' }>[];
}

/**
 * CAOS-362 — one BUTTON on a record surface, in the record header. Three kinds, each of which
 * resolves to something the workspace can already do, so an authored action is never a button that
 * leads nowhere:
 *
 *   navigate — go to a path, with `{field}` placeholders filled from this record.
 *   update   — set one field to one value, through the SAME write boundary as editing it by hand.
 *   clone    — start a new record of this object with this one's values already in the form.
 *   create   — (CAOS-560) start a new record of a DIFFERENT object, prefilled from this one or
 *              from constants. The parent filled in on the child.
 *   component— (CAOS-560) open an authored component with this record in context: the escape
 *              hatch for a button the platform never anticipated.
 *
 * `appliesWhen` is a pure predicate over this record (`status == "Draft"`, clauses joined by `&&`),
 * so an action appears only on the records it means something for.
 */
/**
 * CAOS-406 — one field an `update` action sets, projected.
 *
 * The AUTHORING form has two spellings — `field`/`value` for a single assignment, `set` for any
 * number — and the projection collapses them to this one list, a single assignment becoming a list
 * of one. That is deliberate: the two spellings are a convenience for whoever writes the metadata,
 * and carrying that convenience into the host would mean every reader of an update action handling
 * both, forever, with the single-field path the one that stays tested and the other the one that
 * rots. There is one shape here, so there is one code path that applies it.
 */
export type UpdateAssignmentMeta = {
  readonly field: string;
  readonly value: unknown;
};

/**
 * CAOS-1616 — where an action goes, named as a record instead of written out as a route.
 *
 * `object` is the TARGET's object and `field` is the field on the record (or row) the action is
 * drawn against, holding that target's id. The address is composed at the point of use through the
 * same record-home map every other surface resolves against, so the app and tab it lands on are the
 * ones this workspace actually installed — which is the thing a written-out path cannot know.
 */
export type ActionDestinationMeta = {
  readonly object: string;
  readonly field: string;
};

/**
 * CAOS-561 — the standard verbs the workspace offers an affordance for, and therefore the only ones
 * an object's own action may stand in for. `create` is the New control a list draws; `edit` is a
 * record page converting a field in place when it is clicked.
 *
 * `delete` is not here. The Engine resolves and enforces a delete grant, but nothing in the
 * workspace draws a delete control and `data/describe` does not report the grant, so there would be
 * neither something to replace nor something to fall back to. The kernel refuses it at deploy; this
 * list is the same decision on this side of the wire.
 */
export const STANDARD_VERBS = ['create', 'edit'] as const;
export type StandardVerb = (typeof STANDARD_VERBS)[number];

export type RecordActionMeta = {
  readonly id: string;
  readonly label: string;
  readonly intent?: string;
  readonly appliesWhen?: string;
  /**
   * CAOS-561 — the standard verb this action stands in for, wherever the platform would have
   * offered it. Absent ⇒ an ordinary extra button.
   *
   * Only ever read off an action DEFINED ON THE OBJECT. `recordActionsFrom` takes the origin as an
   * argument and drops `replaces` from a surface-inline action, so the host cannot honour a
   * per-surface override even if one reached it — which is the whole substance of the rule, not a
   * belt-and-braces check on the deploy gate: an override configured once per place it appears is
   * the incumbent's defect, and the way to not have it is to have nowhere to put it.
   */
  readonly replaces?: StandardVerb;
} & (
  // CAOS-1616 — a navigate says where it goes in one of two ways, and they are written as two arms
  // rather than one arm with both optional so that a consumer NARROWS to a case instead of
  // reaching for a field that may not be there. Checking `to !== undefined` gives the other arm's
  // `destination` back typed, which means a consumer that handles only one of them fails to
  // compile rather than quietly doing nothing on the other.
  | { readonly kind: 'navigate'; readonly to: string; readonly destination?: undefined }
  | { readonly kind: 'navigate'; readonly to?: undefined; readonly destination: ActionDestinationMeta }
  | { readonly kind: 'update'; readonly set: readonly UpdateAssignmentMeta[]; readonly confirm?: string }
  | { readonly kind: 'clone' }
  | {
      readonly kind: 'create';
      readonly object: string;
      readonly prefill: readonly CreatePrefillMeta[];
      /**
       * CAOS-1706 — attach the new record to the record this button was pressed from, so it lands
       * on that record's timeline instead of existing unconnected to anything.
       *
       * Deliberately NOT a `prefill` entry, and the type says so by sitting beside that list rather
       * than inside it. Attachment is the system `parent_id` column, and `CreatePrefillMeta.field`
       * is a bare api name on the target object — a system column is not one, so it is not merely
       * inconvenient to express here, it is unsayable. The host half of that is visible one file
       * over: `content-source.ts` applies a prefill through `target.fields.get(entry.field)`, which
       * returns nothing for a system column, so an attachment smuggled in as a prefill would be
       * dropped silently and indistinguishably from a field the person may not edit.
       *
       * Absent ⇒ unattached, which is every existing create action's behaviour unchanged.
       */
      readonly attachToRecord?: boolean;
    }
  | {
      readonly kind: 'component';
      readonly component: string;
      readonly inputs?: Readonly<Record<string, unknown>>;
      readonly requires: readonly ActionRequirementMeta[];
    }
);

/**
 * CAOS-560 — one prefilled field on the record a `create` action makes. `field` is a BARE api name
 * on the TARGET object; the value is either a constant or the value of a bare field on THIS record.
 *
 * Both arrive object-qualified from the author (`contact.account`, `account.id`) and are reduced
 * to bare names here, the same normalisation `update.field` has always had — the projection speaks
 * bare api names throughout, and a surface that had to know which of its names were qualified would
 * be carrying the authoring format into the renderer.
 */
export type CreatePrefillMeta = { readonly field: string } & (
  | { readonly value: unknown }
  | { readonly from: string }
);

/**
 * CAOS-560 — one thing a `component` action needs the person to be able to do, so that an action
 * they could not complete is not offered rather than failing when pressed. `object` is a key;
 * `field` is a bare api name, and its absence means the whole object rather than one field.
 */
export interface ActionRequirementMeta {
  readonly can: 'create' | 'update';
  readonly object: string;
  readonly field?: string;
}

/**
 * CAOS-166 — the record surface ONE RECORD TYPE draws, when it names a layout of its own.
 *
 * The same four things {@link ObjectMeta} carries for the object's base layout, projected from the
 * layout the record type names instead. It is a separate shape rather than a partial override
 * because a per-type layout replaces the base one whole: a type that places two sections has two,
 * not two plus whatever the base placed. Mixing the two would make the rendered surface depend on
 * the base layout of a type that was authored to be independent of it.
 */
export interface RecordTypeLayoutMeta {
  /** The authored layout component key this type names (e.g. `quote_vessel_layout`). */
  readonly layoutKey: string;
  /** The record-page sections of that layout, in authored order. */
  readonly sections: readonly LayoutSection[];
  /** CAOS-361 — the attached sets that layout names. */
  readonly relatedLists: readonly RelatedListMeta[];
  /** CAOS-362 — the record header's buttons that layout places or defines. */
  readonly actions: readonly RecordActionMeta[];
  /** CAOS-1726 — the platform-drawn panel placements, ABSENT when that layout declared none. */
  readonly panels?: readonly RecordPanelMeta[];
}

/**
 * CAOS-166 — one of an object's record types, as the record surface needs it.
 *
 * `selector` is the value of the object's {@link ObjectMeta.recordTypeField} that maps a record to
 * this type, matched by EXACT string equality — the same rule the kernel's `resolveRecordType`
 * applies, deliberately, so a record cannot be one type on a screen and another on a save. A type
 * with no `selector` is never value-selected and so never matches a record.
 *
 * `layout` is ABSENT when this type names no layout, or names one that was never projected. Both
 * mean "draw the object's base layout", which is what a type that only narrows picklists or adds
 * required fields wants: having no surface of its own is the ordinary case, not a gap.
 */
export interface RecordTypeMeta {
  /** The record type's component key (its stable id). */
  readonly key: string;
  /** Human label, for the record header or a type picker. */
  readonly label?: string;
  /** The discriminator value that maps a record to this type. */
  readonly selector?: string;
  /** The surface this type draws, or absent ⇒ the object's base layout. */
  readonly layout?: RecordTypeLayoutMeta;
}

/**
 * CAOS-166 — the object AS IT APPLIES TO ONE RECORD: its own projection, or the surface the
 * record's type names instead.
 *
 * This is the whole of record-type layout selection, in one place on purpose. Every surface that
 * draws a record reads `sections`, `relatedLists`, `actions` and `panels` off an {@link ObjectMeta},
 * at six separate sites; routing per type by editing those sites would put the resolution rule in
 * six places and let them disagree about which layout a record is being drawn from. Instead the
 * rule is applied ONCE, here, and the surfaces keep reading exactly the field they always read.
 *
 * The match is EXACT string equality on `selector`, against the record's value for the object's
 * `recordTypeField`. That is deliberately the same rule as the kernel's `resolveRecordType`
 * (`kernel/src/presentation/record-type.ts`), because the kernel compiles a type's `requiredFields`
 * into the validation rules the SAVE path enforces: if a record resolved to one type on the screen
 * and another on the save, a person would be shown one set of required fields and refused for
 * another. A type carrying no `selector` is never value-selected, so it cannot match a record here.
 *
 * Returns the object UNCHANGED — the same reference, not a copy — whenever the record resolves to
 * no type, or to a type that names no layout of its own. That covers the ordinary case (an object
 * with no types at all) and the deliberate one (a type that only narrows picklists or adds required
 * fields), and it is what makes this safe to call on every record: an org that authors no record
 * types renders byte-identically to before, by the same code path rather than by a bypass.
 *
 * A per-type layout replaces the base one WHOLE. In particular the base layout's `panels` are
 * dropped when the type's layout declares none — because absent `panels` is not "inherit", it is
 * the platform's default tabbed behaviour, and a type that said nothing about panels has asked for
 * that default rather than for whatever the base layout happened to place.
 */
export function objectForRecord(object: ObjectMeta, record: Record<string, unknown>): ObjectMeta {
  const field = object.recordTypeField;
  const types = object.recordTypes;
  if (field === undefined || types === undefined || types.length === 0) return object;

  const value = record[field];
  // Only a string can select a type: the discriminator is a Select over a value set, and matching a
  // non-string would mean coercing — which is how a `0` or a `false` ends up selecting a type whose
  // selector merely stringifies the same way.
  if (typeof value !== 'string') return object;

  let matched: RecordTypeMeta | undefined;
  for (const t of types) {
    if (t.selector !== undefined && t.selector === value) {
      matched = t;
      break;
    }
  }
  const layout = matched?.layout;
  if (layout === undefined) return object;

  // `panels` is separated off rather than overwritten, so that a type's layout declaring none
  // leaves the key ABSENT instead of inheriting the base layout's placements.
  const { panels: basePanels, ...withoutPanels } = object;
  void basePanels;
  return {
    ...withoutPanels,
    sections: layout.sections,
    layoutKey: layout.layoutKey,
    // The type's layout IS an authored layout, so the synthesized-default flag is false even when
    // the object's own base layout was the platform default.
    layoutIsDefault: false,
    relatedLists: layout.relatedLists,
    actions: layout.actions,
    ...(layout.panels !== undefined ? { panels: layout.panels } : {}),
  };
}

/** The projected metadata of one object — everything the list + record surfaces need (CAOS-306). */
export interface ObjectMeta {
  readonly key: string;
  readonly label: string;
  readonly pluralLabel: string;
  /**
   * CAOS-386 — the short, fixed-width code an open record tab is labelled with instead of the
   * object spelled out ("ORD · 13502", not "ORDER · Order 13502"). Authored with the object;
   * undefined when the object has not been given one, in which case the tab falls back to a
   * short code derived from {@link label} ({@link fallbackTabCode}).
   */
  readonly tabCode?: string;
  /** The bare api name of the field that holds the record's display name (e.g. `name`, `quote_number`). */
  readonly nameField: string;
  /**
   * CAOS-1585 — HOW THIS OBJECT'S RECORDS TITLE THEMSELVES, as the object authors it: a template
   * over the record's own fields in the `{field}` form, with `{id}` and `{label}` reserved. See
   * `name-template.ts` for the grammar and {@link recordDisplayName} for what absence means.
   *
   * Undefined is the ordinary case and is NOT a gap: it means the renderer's documented default
   * applies, which is the behaviour every object had before this field existed. Nothing in any org
   * moves until somebody authors a template.
   */
  readonly nameTemplate?: string;
  /** The typed field dictionary, keyed by bare field api name. */
  readonly fields: ReadonlyMap<string, FieldMeta>;
  /** The default list columns (bare field api names), from the object's `listColumns`. */
  readonly listColumns: readonly string[];
  /** The record-page sections (from the object's page layout), or a single default section. */
  readonly sections: readonly LayoutSection[];
  /** The authored page-layout component key (e.g. `account_layout`), or undefined for a default layout. */
  readonly layoutKey?: string;
  /** True when {@link sections} is the synthesized single-section default (no page layout is authored). */
  readonly layoutIsDefault: boolean;
  /** CAOS-361 — the record's attached sets, in authored order. Empty ⇒ the record has a Details tab only. */
  readonly relatedLists: readonly RelatedListMeta[];
  /** CAOS-362 — the record header's buttons, in authored order. Empty ⇒ a record with no actions. */
  readonly actions: readonly RecordActionMeta[];
  /**
   * CAOS-166 — the DISCRIMINATOR field: the bare api name of the field whose value says which
   * record type a record is. Absent ⇒ this object is not record-type-driven, and every record
   * renders the base layout. Projected here because the record surface cannot resolve a type
   * without knowing which field to read, and nothing else in the host carried that.
   */
  readonly recordTypeField?: string;
  /**
   * CAOS-166 — this object's record types, in projection order. Empty/absent ⇒ no types are
   * authored. Present with no matching `selector` for a given record is the same outcome as absent
   * FOR THAT RECORD: the base layout, which is why the renderer treats both through one path.
   */
  readonly recordTypes?: readonly RecordTypeMeta[];
  /**
   * CAOS-405 — the highlights band: the `compactLayout` of the record page bound to this object, as
   * bare field api names in authored order, with the key of the page that named it. The first entry
   * is the record's title; the band draws the entries after it. Undefined when that page names none.
   * This screen has no page assignments, so when several record pages bind the object the first by
   * key supplies it — the tie-break the assignment engine applies at equal specificity.
   */
  readonly compactLayout?: { readonly pageKey: string; readonly fields: readonly string[] };
  /**
   * CAOS-369 — the field the record header badges, as the bound record page names it: a bare field
   * api name, with the key of the page that named it. Undefined when no record page names one, which
   * is a record header with no badge.
   *
   * Until this ticket the badge was chosen in the renderer by matching the field NAME — any field of
   * the object that carried a value set and happened to be called `status` or `stage`. Everything
   * else in that header was authored, so this was the one element an org could neither ask for nor
   * decline: a `phase` or `state` field got no badge, and an unrelated picklist called `status` got
   * one nobody asked for.
   *
   * Resolved by the same first-by-key rule as {@link compactLayout}, and for the same reason: this
   * screen has no page assignments, so when several record pages bind one object the first by key
   * supplies it — the tie-break the assignment engine applies at equal specificity. Keeping the two
   * rules identical is deliberate; a header whose band came from one page and whose badge came from
   * another would be two answers to "which page describes this record".
   */
  readonly statusField?: { readonly pageKey: string; readonly field: string };
  /**
   * CAOS-1882 — the field the record header reads as a quiet second line under the record's name.
   *
   * Resolved by the SAME first-by-key rule as {@link compactLayout} and {@link statusField}, off
   * the same index, for the reason written on those two: a header whose line came from one page
   * and whose badge came from another would be two answers to "which page describes this record".
   * Absent ⇒ no line.
   */
  readonly subtitleField?: { readonly pageKey: string; readonly field: string };
  /**
   * CAOS-1726 — where this layout puts the platform-drawn panels. ABSENT IS NOT EMPTY: a layout
   * that said nothing about its panels is not the same as one that placed none, and only the
   * first leaves today's tabbed behaviour alone.
   */
  readonly panels?: readonly RecordPanelMeta[];
  /**
   * CAOS-1726 — the blueprint this record's regions are read against: the template named by the
   * record page bound to this object, with that page's key.
   *
   * Resolved by the SAME first-by-key rule as {@link compactLayout} and {@link statusField}, off
   * the same `recordPageByObject` index, for the reason stated on those two: a surface whose band
   * came from one page and whose regions came from another would be two answers to "which page
   * describes this record". Undefined ⇒ the object has no record page, so nothing publishes a
   * region vocabulary for it and the layout's placements have nowhere to resolve.
   */
  readonly recordTemplate?: { readonly pageKey: string; readonly template: string };
}

/** One resolved value-set option (a Select field's allowed value + its badge intent). */
export interface ValueOption {
  readonly value: string;
  readonly label: string;
  readonly intent?: string;
  /**
   * CAOS-356 — what this value MEANS in a lifecycle: still live work, finished in our favour, or
   * finished against us. Absent on the many picklists that have no outcome (an industry, a country).
   * This is what lets a figure like win rate or open pipeline be measured without a surface knowing
   * the word "Accepted".
   */
  readonly outcome?: 'open' | 'won' | 'lost';
  /**
   * CAOS-640 — what this value takes to be DRAWN as something richer than a line of text. Each is
   * optional and each is carried STRAIGHT THROUGH from the authored value: this layer decides
   * nothing about presentation, it only stops discarding what the author wrote.
   */
  /** Where this value's picture lives — a location, never a component key. */
  readonly image?: string;
  /** A glyph from the shared icon vocabulary; one the surface cannot draw is skipped, not drawn blank. */
  readonly icon?: string;
  /** A line of explanation under the label — what this choice means, not merely its name. */
  readonly description?: string;
  /** The figure qualifying this choice (a price difference, a duration, a count). Formatted by the surface. */
  readonly amount?: number;
}

/**
 * CAOS-640 — how a value set asks to be presented. It belongs to the SET rather than to the field
 * or the screen, which is what makes a value set installed by a package render the way one authored
 * in Setup does. Anything the kernel does not close over reads as `dropdown`, so an unknown
 * presentation degrades to the list every value set already had.
 */
export type ValueSetPresentation = 'dropdown' | 'cards';

/**
 * CAOS-387 — one COMPONENT the org has installed, as the Component Library reads it.
 *
 * A component is authored metadata like everything else, so the library is a list of what this org
 * actually has rather than a list compiled into the workspace. An org with no library installed has
 * no components, and the library says so instead of showing a set nobody deployed.
 */
export interface ComponentMeta {
  /** The authored key, e.g. `caos.button` — the address the library opens it at. */
  readonly key: string;
  /** Its display name. Presentation only; it may be renamed without breaking a placement. */
  readonly label: string;
  /** What it is for, in one line. Every component is required to say. */
  readonly purpose: string;
  /** The library group it files under, e.g. "Data display". */
  readonly group: string;
  /** The element tag it is placed as — its identity, and what a page names when it places one. */
  readonly tag: string;
  /** Whether it may be placed on a page at all. `false` ⇒ an internal piece other pieces use. */
  readonly exposed: boolean;
  /**
   * Where it may be placed (`recordPage` | `homePage` | `appPage` | `seam`).
   * Placement is OPT-IN: a piece is offered where it says it may go and nowhere else, so ABSENT
   * and an EMPTY list both mean nowhere. They are kept apart only so the library can explain
   * which one it is — "has not said yet" and "says nowhere" want different follow-ups.
   */
  readonly placements?: readonly string[];
  /**
   * The values a placement may set on it, in the order the component declares them.
   * ABSENT means the manifest does not say; an EMPTY list means it says there are none.
   */
  readonly inputs?: readonly ComponentInputMeta[];
  /**
   * CAOS-387 — what is WRONG with this piece's manifest, in the words a person reading Setup
   * uses. Present ⇒ the piece IS installed, and its manifest is missing something it needs, so
   * it is shown saying exactly that. It is not dropped: a piece that vanishes from the library
   * reads on a page as "not installed — install the package", which sends somebody to install
   * something they already have.
   */
  readonly problems?: readonly string[];
  /**
   * CAOS-461 — still working, still resolving, but the piece is signalling that authors should
   * stop reaching for it in new work. Deprecating a piece never removes it and never changes what
   * it does; it only changes what the library says about it. Absent ⇒ not deprecated.
   */
  readonly deprecated?: boolean;
  /**
   * CAOS-577 — where this piece's code lives, exactly as its manifest declared it. Carried through
   * so the page renderer can fetch and load it when the workspace does not already have the tag
   * registered, instead of the only available answer being "this build did not compile it in".
   * ABSENT means the manifest did not declare one (or declared it in a shape this projection could
   * not read) — never invented, because a component whose code cannot be located this way is
   * exactly the "installed, but its code is not here" case the page renderer already has a
   * sentence for.
   */
  readonly bundle?: ComponentBundleMeta;
}

/** CAOS-577 — where a component's code lives, as its manifest's `bundle` names it. */
export interface ComponentBundleMeta {
  /** The bundle entry to fetch and load, resolved against this workspace's own origin. */
  readonly module: string;
}

/** CAOS-387 — one value a placement may set on a component. */
export interface ComponentInputMeta {
  readonly name: string;
  readonly label: string;
  /**
   * The declared type. ABSENT when the manifest did not state one — the projection does not
   * substitute a default, because a type is a contract claim and only the component may make it.
   * An input arriving without one is reported in the component's `problems` instead.
   */
  readonly type?: string;
  readonly required: boolean;
  readonly help?: string;
  /**
   * CAOS-461 — this INPUT, not the whole piece, is deprecated: still working, still resolving,
   * signalling that authors should choose something else for a new placement. Absent ⇒ not
   * deprecated.
   */
  readonly deprecated?: boolean;
  /**
   * CAOS-798 — WHERE the value comes from, when the manifest says
   * (`literal` | `urlParameter` | … — `kernel/src/meta/component.ts`).
   *
   * Absent stays absent, like `type`: a source is a claim about the input's wiring and only the
   * component may make it. It is projected because a source nothing carries is a source nothing can
   * resolve — `urlParameter` was declared by the metatype and by a shipped manifest for a year
   * while every placed component got `undefined`, because this projection dropped it here.
   */
  readonly source?: string;
  /**
   * CAOS-653 — this input can only be ASSIGNED, never written as markup: the component observes no
   * attribute for it. Absent ⇒ it can be written as an attribute, which is what every manifest said
   * before the field existed.
   *
   * CAOS-159 — projected for the same reason `source` above is, and it was missing here for the
   * same reason: the declaration existed in the metatype and in 81 shipped manifests while the
   * renderer that needed it got `undefined`, because this projection dropped it. See `applyProps`
   * in `surfaces/page-surface.ts` for what reads it, and for the eight scalar inputs that were
   * losing their value because of it.
   */
  readonly propertyOnly?: boolean;
}

/** The org's installed metadata, projected to what the shell frame + surfaces consume. */
export interface OrgMetadata {
  /** The installed apps, in the order the registry returns them. */
  readonly apps: readonly AppMeta[];
  /** source key → display label for any component that can source a shell injection (packages/apps). */
  readonly labels: ReadonlyMap<string, string>;
  /**
   * CAOS-287 — the keys of the `package` components installed in this org (e.g. `pkg_sales_atlas`).
   *
   * The in-shell "Get more apps" surface reads this to show an app already in the workspace as
   * INSTALLED rather than offering to install it a second time. Keyed on PRESENCE: a key is here
   * because the org has that package, and absence is the only thing that means "available". There
   * is deliberately no per-package state beyond that — an install either happened or it did not,
   * and a richer status would be a claim this projection cannot substantiate from the components
   * it is handed.
   */
  readonly installedPackageKeys: ReadonlySet<string>;
  /** object key → its projected metadata (fields, layout sections, list columns). */
  readonly objects: ReadonlyMap<string, ObjectMeta>;
  /** value-set key → its options (value → {@link ValueOption}), for Select/status rendering. */
  readonly valueSets: ReadonlyMap<string, ReadonlyMap<string, ValueOption>>;
  /**
   * CAOS-640 — value-set key → the presentation it declares. A PARALLEL map rather than a property
   * on the options map above, because that map is keyed by value and has nowhere to carry something
   * belonging to the set as a whole. A key absent from here declared nothing and is a dropdown.
   */
  readonly valueSetPresentations: ReadonlyMap<string, ValueSetPresentation>;
  /**
   * CAOS-320 — injection source key → the engine-rendered INTAKE form the source contributes, when it
   * has one (the Report-a-Bug orb). Keyed by the SAME source key an edge-anchored injection carries
   * (the contributing package's key), so {@link enrichShellPlan} can attach the form to the orb.
   */
  readonly intakeBySource: ReadonlyMap<string, ShellIntakeDescriptor>;
  /**
   * CAOS-148 — injection source key → the guidance that source contributes: the recipes its package
   * roots and the installed anchors their steps name. Keyed like {@link intakeBySource}, by the
   * contributing package's key. Absent ⇒ the package roots no recipe and its help tab keeps the
   * generic placeholder.
   */
  readonly guidanceBySource: ReadonlyMap<string, ShellGuidanceDescriptor>;
  /**
   * CAOS-151 — injection source key → the panel kind that package's own edge-anchored shell claim
   * declares (CAOS-734's `anchor.kind`). The kernel's capability projection keeps a claim's
   * placement but not its kind, so the boot plan arrives without one and the frame would classify
   * the tab by its key and label text. Read from the same retrieved package body, this lets
   * {@link enrichShellPlan} put the declared kind back. Optional: metadata built without it reads as
   * "nothing declared", which is what it was before.
   */
  readonly anchorKindBySource?: ReadonlyMap<string, EdgeAnchorKind>;
  /**
   * CAOS-688 — injection source key → the assistant panel that source contributes: its peek read,
   * its chat write, its openers and its way through. Keyed like {@link intakeBySource}, by the
   * package key an edge-anchored injection carries.
   */
  readonly assistantBySource: ReadonlyMap<string, ShellAssistantDescriptor>;
  /**
   * CAOS-345 — object key → the SAVED LIST VIEWS authored for it, in label order. A saved view is a
   * named set of columns (and a sort) over one object; the workspace offers them beside each object
   * tab and opens the one asked for. Absent from the map ⇒ the object has no saved views and its
   * default columns stand.
   */
  readonly listViews: ReadonlyMap<string, readonly ListViewMeta[]>;
  /**
   * CAOS-387 — the COMPONENTS installed in this org, in the order the org's metadata returns them.
   * Empty ⇒ no component library is installed here, which the library surface reports as such.
   */
  readonly components: readonly ComponentMeta[];
  /**
   * CAOS-388 — the PAGES authored in this org, keyed by their component key.
   *
   * A page has always been deployable and has never been drawn: nothing read what it said it
   * contained. This is that reading. What a page holds is carried through verbatim — its sections,
   * and inside each the fields and the pieces placed on it — so a renderer can draw the page the
   * author wrote rather than a separate description of the same screen.
   */
  readonly pages: ReadonlyMap<string, PageMeta>;
  /**
   * CAOS-388 / CAOS-393 — the PAGE TEMPLATES this org holds, keyed by their component key.
   *
   * A page's sections name the template region they sit in. Without the template that name is a
   * string nothing reads, and the sections draw in authored order regardless of where the author
   * placed them. With it, the page draws in the order its blueprint declares.
   */
  readonly pageTemplates: ReadonlyMap<string, PageTemplateMeta>;
  /**
   * CAOS-582 — every package's `setup` capability INJECT claim: the group it hangs a page under,
   * and the page it names. Setup's own nav rail is built from these, alongside its own
   * platform-core sections, instead of a fixed list Setup's code carries — so a package that
   * declares one of these appears in Setup without Setup ever being told its name.
   */
  readonly setupContributions: readonly SetupContributionMeta[];
  /**
   * CAOS-1567 — roster list href (a `rosterDetailPage`'s `breadcrumbRootHref`) → the keys of the
   * record pages that name it. Normally one; more than one is an authoring collision the caller
   * refuses rather than resolving, and an address nobody claims is simply absent.
   */
  readonly rosterDetailPagesByListHref: ReadonlyMap<string, readonly string[]>;
  /**
   * CAOS-595 — every installed package's rich text toolbar button: a `rich-text` inject claim at the `toolbar`
   * seam. The editor orders and groups them; this is only what was contributed. Absent on metadata built
   * before the field existed, which reads the same as none.
   */
  readonly richTextToolbar?: readonly RichTextToolbarMeta[];
  /**
   * CAOS-736 — every seam-order override this org has pinned, keyed by seam id, exactly as
   * `CapabilityRegistry.seamOrderOverrides()` (kernel `capabilities/registry.ts`) projects it and
   * `metadata/retrieve` now carries it alongside the component bundle. Kernel-mastered seams
   * (`edge-anchored`, `avatar-menu`, `header-actions`, `navigation-entries`) already have their pin
   * applied server-side by `CapabilityRegistry.injectionsFor` before the host ever sees them — this
   * map exists for the two seams that DON'T route through that resolver: `setup-nav` (Setup's own
   * contributed rail groups, which bypass `CapabilityRegistry` entirely by design — CAOS-431) and
   * `app-switcher` (the installed-apps ordering below, which sorts `AppMeta` rows, not capability
   * injection rows). A seam id absent from this map has no pin — the deterministic default applies,
   * unchanged from before this ticket.
   */
  readonly seamOrderOverrides: ReadonlyMap<string, readonly string[]>;
}

/** CAOS-736 — the seam id `setup-nav` claims + this pin both use: Setup's contributed rail groups. */
export const SETUP_NAV_SEAM_ID = 'setup-nav';

/**
 * CAOS-736 — the synthetic seam id the app switcher's ordering is pinned under. Apps are not
 * `caos.capability_injection` rows (one app per package, not a claim on a shared seam), so this
 * name is not declared by any capability slot contract the way `setup-nav` is — it exists only as
 * the key an admin's `seam-order/set` call and this override map agree on.
 */
export const APP_SWITCHER_SEAM_ID = 'app-switcher';

/**
 * CAOS-582 — one `setup` capability inject claim, as the workspace reads it off an installed
 * package's `capabilityClaims` (the same field `validate-capabilities.ts` enforces at deploy).
 * Both `group` and `page` are always present here: a claim declaring only one is refused at
 * install (deploy-time rule 3) and never reaches an org's active metadata.
 */
export interface SetupContributionMeta {
  /** The KEY of the package contributing this injection — resolves to a display name via `labels`. */
  readonly sourceKey: string;
  /** The setup grouping this page hangs under (a rail heading; several pages may share one). */
  readonly group: string;
  /** The page this contribution names, resolved against `pages`. */
  readonly page: string;
  /**
   * CAOS-465 — the declared intent for this contribution's position among the other pages in
   * `group`, lower first; `undefined` when the claim never set one. Fed to `orderSeamContributions`
   * (`../seam-order.js`) by `setup-surface.ts` — never read positionally.
   */
  readonly order?: number;
  /** CAOS-762 — the rail route this contribution draws at, when it differs from `page` itself. */
  readonly route?: string;
  /** CAOS-762 — an explicit rail-entry label, when `page` does not resolve to a component `label`
   *  `contributedSectionGroups` can read (a section with no backing page component at all). */
  readonly label?: string;
  /** CAOS-762 — the rail entry's own glyph; `undefined` ⇒ `contributedSectionGroups` falls back to `layers`. */
  readonly icon?: string;
  /** CAOS-762 — the rail entry's own description line; `undefined` ⇒ the generic "Added by …" fallback. */
  readonly blurb?: string;
}

/**
 * CAOS-595 — one button a package adds to the rich text editor's toolbar, read off its `rich-text` capability
 * inject claim's nested `richTextToolbar`. Deploy refuses a button with no action or with both
 * (`validate-capabilities.ts` rule 7), so each carries exactly one of `insert` and `command`. The shape is
 * the component library's `RichTextToolbarContribution`, so it is handed to the renderer as it is.
 */
export interface RichTextToolbarMeta {
  /** The package the button came from. */
  readonly sourceKey: string;
  readonly key: string;
  readonly label: string;
  readonly icon?: string;
  readonly group?: string;
  /** The claim's declared position among contributed buttons, lower first. */
  readonly order?: number;
  readonly insert?: string;
  readonly command?: string;
}

/** CAOS-595 — the buttons a field's renderer draws: every contribution, for a Text field in the rich format only. */
export function richTextToolbarFor(
  field: { readonly storageType: string; readonly format?: string | null },
  meta: Pick<OrgMetadata, 'richTextToolbar'>,
): readonly RichTextToolbarMeta[] | undefined {
  return field.storageType === 'Text' && field.format === 'rich' ? (meta.richTextToolbar ?? []) : undefined;
}

/**
 * CAOS-388 — one PAGE, as the workspace reads it in order to draw it.
 *
 * ABSENT and EMPTY are kept apart throughout, deliberately. A page that never declared any sections
 * has not said what it contains; a page that declared none has said it contains nothing. They are
 * different statements, a reader would act on them differently, and collapsing the first into the
 * second would put a claim on the screen that nobody made.
 */
export interface PageMeta {
  /** The authored key — what a tab or a link asks for by name. */
  readonly key: string;
  /** Its display name, which is what the screen is titled. */
  readonly label: string;
  /** The record type it is bound to, when it is bound to one. Absent ⇒ it is about no record. */
  readonly object?: string;
  /**
   * CAOS-388 — WHAT FEEDS THIS PAGE, in the page's own words. `object` ⇒ a record; `content` ⇒
   * nothing, it draws only what it places; `intake` ⇒ it is a form that writes; `controlPlane` ⇒ its
   * rows come from a control-plane route. CAOS-438 — `appLanding` ⇒ it is the surface an application
   * opens on, and what it shows is derived from the application it is being drawn in.
   *
   * CAOS-1767 — `list` ⇒ it shows MANY records of one object, through that object's own list view.
   * It is the only kind besides `object` that is about the store, and the difference between the two
   * is one record against many: a record page resolves its placed fields against the record it was
   * opened for, and a list page is opened for none.
   *
   * Carried because a surface that does not know which kind it is holding will describe all six the
   * same way, and five of those descriptions are false. The intake page that ships was being told it
   * "contains nothing" while declaring a complete form.
   */
  readonly kind: 'object' | 'content' | 'intake' | 'controlPlane' | 'appLanding' | 'list';
  /** The skeleton it assembles into. */
  readonly template: string;
  /** What it holds, in authored order. ABSENT ⇒ the page never said; EMPTY ⇒ it said nothing. */
  readonly sections?: readonly PageSectionMeta[];
  /**
   * CAOS-744 — a `controlPlane`-kind page's READ route (a pure (noun,verb) ref; the host turns it
   * into `/api/<noun>/<verb>`). Absent for every other kind. Carries NO parameter of any kind — see
   * {@link RouteVerbRefMeta}'s doc for why that absence is the security boundary, not an omission.
   */
  readonly controlPlaneRead?: RouteVerbRefMeta;
  /** CAOS-744 — a `controlPlane`-kind page's optional JSON path into the read payload naming the
   *  row array (e.g. `"data.items"`). Absent ⇒ the payload itself is the row source. */
  readonly controlPlaneRowsAt?: string;
  /**
   * CAOS-1767 — a `list`-kind page's two bindings: the object whose records it lists, and the saved
   * list view it opens on.
   *
   * `listObject` is carried under its own name rather than folded into {@link object} on purpose.
   * `object` means "the record this page is bound to", and every reader of it — the record-page
   * binding map, the template resolution, the field instances — means that one record. A list page
   * binds none, and writing its listed object there would have made every one of those readers
   * treat it as a record page. The kernel refuses a top-level `object` on a list page for the same
   * reason, so the two cannot disagree.
   *
   * `listView` is ABSENT when the page named none, and absent is not empty: it means the object's
   * own default columns stand, which is what the list surface already does for a tab that names no
   * saved view (CAOS-345).
   */
  readonly listObject?: string;
  readonly listView?: string;
  /**
   * CAOS-744 — PAGE-LEVEL actions, collected from the manifest's own `rowActions`. Each is EITHER
   * row-scoped (attaches to a roster component named by {@link rowActionsTarget}, or the page's
   * primary roster when absent) or CALLER-scoped (see {@link PageRowActionMeta} — no `param`, drawn
   * as a standalone button in the page header instead of a roster's action column).
   */
  readonly rowActions?: readonly PageRowActionMeta[];
  /** CAOS-744 — the roster component instance {@link rowActions} attach to, when row-scoped. */
  readonly rowActionsTarget?: string;
  /**
   * CAOS-1908 — the READER'S controls for a `controlPlane` page's roster: search, ordering, narrowing
   * tabs and a board.
   *
   * ABSENT IS THE DEFAULT AND IT MEANS "draw the bare table", which is load-bearing rather than
   * cautious. The surface these configure is the host's own {@link renderCollection}, whose
   * `modesFor` always offers card and split and whose own default reading is `card` — so a setup page
   * that never asked for any of this would have stopped being a table the day the controls shipped.
   * Opt-in keeps every existing page byte-identical and makes the one page that wants a toolbar say
   * `defaultMode: 'table'` to keep the reading it already has.
   *
   * The names mirror a roster page's exactly, because they configure the same surface and an author
   * who has written one should have nothing new to learn.
   */
  readonly rosterControls?: PageRosterControlsMeta;
  // (shape: {@link PageRosterControlsMeta} below)
  /**
   * CAOS-388 — the names of the things this page declares that the page surface does NOT draw
   * (related lists, a compact layout, header actions). Projected so the screen can SAY they are not
   * drawn here rather than dropping them without a word: a record page opened through a tab losing
   * its related lists silently is the same defect class this ticket exists to close.
   */
  readonly undrawn: readonly string[];
}

/** CAOS-1908 — one segmented narrowing tab above a page's roster. Mirrors the kernel's `RosterFilterTab`. */
export interface PageRosterFilterMeta {
  readonly label: string;
  /** `null` is the roster's own "All" tab; a string narrows to rows whose tested value equals it. */
  readonly equals?: string | null;
  /** The same question asked of a field holding a list. */
  readonly contains?: string;
  /** Ask this tab's question of a named COLUMN's value rather than of the roster's `filterField`. */
  readonly column?: string;
}

/**
 * CAOS-1908 — the reader's controls for a `controlPlane` page's roster, as the host reads them.
 *
 * Every member is optional, and the combinations that would draw a control which narrows, orders or
 * searches NOTHING are refused by the kernel on both validators before anything deploys — so this
 * type does not restate those rules and the renderer does not re-check them. What it does do is
 * carry only what the host can act on, which is why there is no `groupBy` here: a page roster
 * declares no grouping, so `defaultMode: 'group'` is refused at author time rather than modelled.
 */
export interface PageRosterControlsMeta {
  readonly searchPlaceholder?: string;
  /** The row keys an in-memory search matches. Without these the search box would match the title alone. */
  readonly searchFields?: readonly string[];
  readonly searchEmptyLabel?: string;
  readonly emptyLabel?: string;
  readonly sortOptions?: readonly { readonly value: string; readonly label: string }[];
  readonly stages?: readonly { readonly value: string; readonly label: string }[];
  readonly stageField?: string;
  readonly filters?: readonly PageRosterFilterMeta[];
  readonly filterField?: string;
  readonly filterDefault?: number;
  /** The row key a row's NAME is read from; absent ⇒ the first column's value. */
  readonly titleField?: string;
  readonly defaultMode?: 'card' | 'table' | 'kanban' | 'split' | 'group';
  /** CAOS-1912 — an `{id}`-templated path a row opens, e.g. `/setup/feedback/{id}`. Absent ⇒ rows do not open. */
  readonly detailRoute?: string;
}

/**
 * CAOS-247 / CAOS-744 — a pure (noun, verb) reference to a control-plane route, mirroring the
 * kernel's `RouteVerbRefSchema`. The host turns it into `/api/<noun>/<verb>` at fetch time. It is
 * DELIBERATELY only ever these two bare names — never a query string, never a body, never an id — on
 * BOTH the page's own read ({@link PageMeta.controlPlaneRead}) and a caller-scoped action's write
 * ({@link PageRowActionMeta.verb} when {@link PageRowActionMeta.param} is absent). That is what makes
 * "show me my own data" structurally incapable of becoming "show me someone else's": there is no
 * field anywhere in this projection a page could set to a foreign principal/org id, so the route it
 * names is the only thing that can decide whose data comes back — and it decides from the
 * authenticated session, server-side, never from anything this object could carry.
 */
export interface RouteVerbRefMeta {
  readonly noun: string;
  readonly verb: string;
}

/**
 * CAOS-247 / CAOS-744 — one labelled button that POSTs a control-plane write route, mirroring the
 * kernel's `RowActionSchema`. `param`/`paramFrom` are CO-OPTIONAL:
 *
 *   • both present  — ROW-scoped: `param` names the route parameter, `paramFrom` the row field its
 *     value comes from. The host POSTs `/api/<noun>/<verb>/<the row's paramFrom field>`.
 *   • both absent   — CALLER-scoped, no row to key against. The host POSTs `/api/<noun>/<verb>` with
 *     nothing appended — see {@link RouteVerbRefMeta}'s doc for why that absence is load-bearing.
 */
/**
 * CAOS-1892 — one value an action asks the person for, as the wire carries it.
 *
 * `control` is a BARE STRING rather than a union of the six words the kernel declares, and that is
 * the same decision `PageRowActionMeta.intent` already makes one type up. This projection reads what
 * an org actually deployed, which may predate a word this build knows or postdate it; narrowing here
 * would make a future control a parse failure in the host rather than a control this host does not
 * draw. The drawing side decides what it can draw and says so for anything else.
 */
export type PageCollectedFieldMeta = {
  readonly path: string;
  readonly label: string;
  readonly control: string;
  readonly required?: boolean;
  readonly placeholder?: string;
  readonly options?: readonly { readonly value: string; readonly label: string }[];
};

/** CAOS-1892 — what a person is told before a declared action runs. */
export type PageActionConfirmationMeta = {
  readonly message: string;
  readonly confirmLabel?: string;
};

export type PageRowActionMeta = {
  readonly id: string;
  readonly label: string;
  readonly intent?: string;
  readonly appliesWhen?: string;
} & (
  | {
      readonly verb: RouteVerbRefMeta;
      readonly param?: string;
      readonly paramFrom?: string;
      /** CAOS-744 — a fixed literal JSON body POSTed with the write (mirrors `RowActionSchema.body`).
       *  Never computed, never read off a row — see that schema's doc for why this is what lets a
       *  caller-scoped action express an outcome (e.g. "set my language to French") safely. */
      readonly body?: Readonly<Record<string, string | number | boolean | null>>;
      /** CAOS-1892 — the values this action asks the person for before it runs, in draw order
       *  (mirrors `RowActionSchema.collect`). Each lands in the request body at its own `path`,
       *  merged over nothing: `body` stays the fixed part, this is the supplied part. */
      readonly collect?: readonly PageCollectedFieldMeta[];
      /** CAOS-1892 — what the person is told before this runs, when it must not run unannounced. */
      readonly confirm?: PageActionConfirmationMeta;
      readonly destination?: undefined;
    }
  /**
   * CAOS-1616 — the OPENING arm: this action goes somewhere instead of writing something.
   *
   * Written as a second arm rather than as one arm with everything optional, and the reason is the
   * same one that made the kernel's `RowActionSchema` a union: the write fields are not optional
   * here, they are ABSENT. A consumer that narrows on `verb !== undefined` gets `destination` back
   * typed on the other side, so a reader handling only writes fails to compile rather than quietly
   * POSTing `/api/undefined/undefined` — which is exactly what this type's previous shape allowed,
   * because an opening action carries no `param` and so satisfied every caller-scoped test there was.
   */
  | {
      readonly verb?: undefined;
      readonly param?: undefined;
      readonly paramFrom?: undefined;
      readonly body?: undefined;
      readonly destination: ActionDestinationMeta;
    }
);

/**
 * CAOS-388 / CAOS-393 — one PAGE TEMPLATE, as the workspace reads it in order to lay a page out.
 *
 * CAOS-428 — the geometry is carried now. It used to stop here, and this doc used to say so:
 * "the grid geometry is presentation the page surface does not apply yet". That was the
 * mechanism behind a real defect rather than a deferred nicety — the renderer could not tell one
 * region from the next because it was never handed anything to tell them apart with, so every
 * region's sections went into one flat column and two regions drew as one undifferentiated area.
 */
export interface PageTemplateMeta {
  readonly key: string;
  readonly label: string;
  /** The regions it declares, in declaration order — which is the order a page's blocks draw in. */
  readonly regions: readonly { readonly key: string; readonly label: string }[];
  /**
   * The arrangement BETWEEN regions, when the template declares one. ABSENT IS NOT EMPTY: a
   * template that declares no geometry has none and its regions stack in declared order, which
   * is a different statement from one whose geometry could not be read.
   */
  readonly geometry?: {
    /** `grid-template-areas`, one string per row, region keys and `.` for an empty cell. */
    readonly areas: readonly string[];
    /** `grid-template-columns` as a RELATIONSHIP — fractions, never pixels. */
    readonly cols: string;
    /** The declared narrow-viewport order, so a page never stacks in document order by accident. */
    readonly stack: readonly string[];
  };
}

/** CAOS-388 — one titled block of a page. */
export interface PageSectionMeta {
  readonly id: string;
  /** The heading the block carries, when it carries one. Absent ⇒ the block is untitled. */
  readonly label?: string;
  /**
   * How many columns it lays its contents out in. OPTIONAL: a section that did not say has not said
   * "two" (CAOS-388). The renderer picks the display default; the projection does not invent one and
   * then present it as the author's choice.
   */
  readonly columns?: number;
  /** The template region it sits in, when it names one. */
  readonly region?: string;
  /** What is placed in it. ABSENT ⇒ the block never said; EMPTY ⇒ it said nothing is placed. */
  readonly items?: readonly PageItemMeta[];
}

/**
 * CAOS-388 / CAOS-498 — one thing placed in a block: a field of the bound record type, a component,
 * or another PAGE.
 */
export type PageItemMeta =
  | PageFieldItemMeta
  | PageComponentItemMeta
  | PageEmbedItemMeta
  | PageUnknownItemMeta;

/**
 * CAOS-498 — another page drawn inside this one: the cross-application embed.
 *
 * `pageKey` is resolved against the org's installed pages AT RENDER, for the same reason a placed
 * component's `key` is: whether the org has that page is a fact about the org, not about this page.
 * The deploy gate refuses an embed naming a page the org does not have, so a missing one here means
 * the page was installed and the other package was later removed — which is exactly the case that
 * has to be NAMED rather than drawn as a gap.
 *
 * Nothing here says which application the page belongs to, and that is the seam being general
 * rather than a pairing: the host resolves a page by key and neither page knows the other's app.
 */
export interface PageEmbedItemMeta {
  readonly kind: 'embed';
  readonly id: string;
  /**
   * The page this placement draws, when it names one.
   *
   * CAOS-492 — one of this and `rosterPageKey` is present and never both, mirroring the two authored
   * arms. Kept as two names rather than one key with a kind beside it: the two are resolved from
   * DIFFERENT places — a page from `meta.pages`, which the bulk metadata read carries, and a roster
   * through `/api/metadata/component-get`, which it does not — so the renderer has to branch anyway,
   * and a single field would make the branch a guess.
   */
  readonly pageKey?: string;
  /** CAOS-492 — the roster this placement draws instead, when it names one. */
  readonly rosterPageKey?: string;
}

/**
 * CAOS-388 — a placement whose `type` this workspace does not recognise.
 *
 * It is CARRIED rather than dropped. Dropping it is the silent-blank defect in miniature: the author
 * placed something, the screen does not show it, and nothing anywhere says why. A workspace running
 * older code than the page was authored against is the ordinary way this happens, and "this
 * workspace does not know what this is" is a sentence somebody can act on.
 */
export interface PageUnknownItemMeta {
  readonly kind: 'unknown';
  readonly id: string;
  /** Whatever the placement called itself, for the screen to name. */
  readonly type: string;
}

/** A field of the page's bound record type, placed on the page. */
export interface PageFieldItemMeta {
  readonly kind: 'field';
  readonly id: string;
  /** The authored, object-qualified key, e.g. `account.billing_city`. */
  readonly fieldKey: string;
  /** The bare field api name, resolvable against {@link ObjectMeta.fields}. */
  readonly field: string;
  /** `readonly` when the placement pins it so; absent ⇒ the field's own behaviour stands. */
  readonly uiBehavior?: string;
}

/**
 * A COMPONENT placed on the page. `key` names the component the placement wants; it is resolved
 * against the org's installed components at render, because whether the org actually has that piece
 * is a fact about the org and not about the page.
 */
export interface PageComponentItemMeta {
  readonly kind: 'component';
  readonly id: string;
  /** The component key this placement names. */
  readonly key: string;
  /**
   * The values this placement stores for the component. ABSENT ⇒ the placement set nothing at all;
   * EMPTY ⇒ it declared a set and left it empty. The renderer treats the two the same, but the
   * projection must not be the thing that decides they are the same.
   */
  readonly props?: Readonly<Record<string, unknown>>;
  /** CAOS-744 — row actions declared directly on this placement (always row-scoped: a roster
   *  placement IS a row context). Mirrors `ComponentInstanceSchema.rowActions`. */
  readonly rowActions?: readonly PageRowActionMeta[];
  /** CAOS-654 — for a piece placed inside another: which of its parent's named slots it goes into. */
  readonly slot?: string;
  /**
   * CAOS-402 — the record-scoped read this piece is fed from. ABSENT ⇒ the piece is drawn from its
   * stored `props` alone, which is every placement authored before this existed.
   * Mirrors `ComponentInstanceSchema.read`.
   */
  readonly read?: PageReadMeta;
  /**
   * CAOS-1576 — the stored report this piece shows a figure for. ABSENT ⇒ it shows no figure, which
   * is every placement authored before this existed. Mirrors `ComponentInstanceSchema.report`.
   *
   * A KEY AND NOTHING ELSE, on purpose. The question — which object, which measure, which grouping
   * field, which filter — lives in the report, and a placement that could restate any of it would
   * be a second description of what the figure counts. It is also what keeps this projection unable
   * to name a principal: a report key is resolved against metadata, so there is nowhere here to put
   * a person or an org, and the figure is measured for whoever is looking.
   */
  readonly report?: string;
  /**
   * CAOS-1575 — the write this piece performs when it raises the event the write names. ABSENT ⇒
   * the piece writes nothing, which is every placement authored before this existed.
   * Mirrors `ComponentInstanceSchema.write`.
   */
  readonly write?: PageWriteMeta;
  /**
   * CAOS-654 — what is placed inside the piece, in order. ABSENT ⇒ the placement never said what it
   * holds; EMPTY ⇒ it said nothing goes inside. Mirrors `ComponentInstanceSchema.children`.
   */
  readonly children?: readonly PagePlacedChildMeta[];
}

/**
 * CAOS-402 — where one route parameter's value comes from. Mirrors `PlacedReadParamSourceSchema`.
 *
 * CAOS-1575 added `urlParameter`: the route parameter SPELLED THE SAME as the request parameter it
 * fills. It carries no name of its own on purpose — a binding that could remap names could point a
 * request parameter at a route parameter meaning something else, and that is where a smuggled value
 * would come from. See the kernel schema's doc for what this does to the security boundary and why
 * the narrower claim is the one the adversarial test now holds.
 */
export type PageReadParamSource = 'recordId' | 'objectKey' | 'urlParameter';

/**
 * CAOS-402 — a READ a placed piece is fed from, parameterised by the record it sits on.
 *
 * This is what lets a record-scoped route reach a screen. Before it, a page could only name a read
 * that takes no arguments, so `history list` — which needs an object and a record id — was built,
 * permission-checked, and callable by nothing anyone could author.
 *
 * The values are NOT resolved in this projection. It reads the page, not the request, and the record
 * on screen belongs to the request; binding here would mean the same substitution lived in two
 * places, and the copy that drifted would be the one nobody reads. The renderer binds it.
 */
export interface PageReadMeta {
  readonly noun: string;
  readonly verb: string;
  /**
   * Route parameter name → where its value comes from. ABSENT ⇒ the read takes no parameters.
   * A parameter bound to a word outside the closed set is DROPPED here rather than passed on: the
   * deploy gate already refuses one by name, so a workspace seeing it is reading metadata that did
   * not come through that gate, and sending an uninterpreted word as a query value would be this
   * layer inventing a binding nobody declared.
   */
  readonly params?: Readonly<Record<string, PageReadParamSource>>;
  /** JSON path into the payload naming the row array; ABSENT ⇒ the payload IS the array. */
  readonly rowsAt?: string;
  /**
   * CAOS-1575 — the component input the payload is applied to; ABSENT ⇒ `rows`.
   * Mirrors `PlacedReadSchema.into`.
   */
  readonly into?: string;
  /**
   * CAOS-1575 — how the payload is converted before it is applied; ABSENT ⇒ applied as it came.
   * Mirrors `PlacedReadSchema.intoAs`.
   */
  readonly intoAs?: 'jsonText';
}

const READ_PARAM_SOURCES: readonly string[] = ['recordId', 'objectKey', 'urlParameter'];

/**
 * CAOS-402 — read a placement's `read`, or `undefined` when it does not have one this workspace can
 * act on. Defensive in the same way every other projection here is: this reads stored JSON, so a
 * read missing its noun or verb is not a read, and is dropped rather than turned into a fetch of
 * `/api/undefined/undefined`.
 */
function projectPlacedRead(raw: unknown): PageReadMeta | undefined {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const rec = raw as Record<string, unknown>;
  const noun = str(rec, 'noun');
  const verb = str(rec, 'verb');
  if (!noun || !verb) return undefined;

  const rawParams = rec['params'];
  const params: Record<string, PageReadParamSource> = {};
  if (rawParams != null && typeof rawParams === 'object' && !Array.isArray(rawParams)) {
    for (const [name, source] of Object.entries(rawParams as Record<string, unknown>)) {
      if (typeof source === 'string' && READ_PARAM_SOURCES.includes(source)) {
        params[name] = source as PageReadParamSource;
      }
    }
  }

  return {
    noun,
    verb,
    // Absent stays absent, as it does everywhere else in this projection: a read that declared no
    // parameters and one whose every parameter was unreadable are different facts about the page.
    ...(Object.keys(params).length > 0 ? { params } : {}),
    ...(str(rec, 'rowsAt') ? { rowsAt: str(rec, 'rowsAt')! } : {}),
    ...(str(rec, 'into') ? { into: str(rec, 'into')! } : {}),
    // Closed, as every source word here is: an unrecognised conversion is DROPPED rather than
    // guessed at, so the payload is applied as it came instead of being silently mangled into a
    // shape the piece cannot read.
    ...(str(rec, 'intoAs') === 'jsonText' ? { intoAs: 'jsonText' as const } : {}),
  };
}

/**
 * CAOS-1575 — where one written body field's value comes from. Mirrors
 * `PlacedWriteValueSourceSchema`; see that schema's doc for why the caller-supplied source is
 * constrained by name and the server-returned one is not.
 */
export type PageWriteValueSource = 'componentValue' | 'componentValueAsJson' | 'urlParameter' | 'payload';

/** CAOS-1575 — the component event a placed write is triggered by. Mirrors `PlacedWriteTriggerSchema`. */
export type PageWriteTrigger = 'save';

/** CAOS-1575 — one body field's binding. `name` is meaningful only for `payload`. */
export interface PageWriteBindingMeta {
  readonly from: PageWriteValueSource;
  /** The field within the source. Present only for `payload`; see `projectPlacedWrite`. */
  readonly name?: string;
}

/**
 * CAOS-1575 — a WRITE a placed piece performs when it raises the event named by `on`.
 *
 * As with {@link PageReadMeta}, nothing is RESOLVED here. This projection reads the page, not the
 * request: what the piece holds belongs to the event, the route parameters belong to the request,
 * and the payload belongs to a fetch that has not happened yet. Binding here would put the same
 * substitution in two places, and the copy that drifted would be the one nobody reads.
 */
export interface PageWriteMeta {
  readonly on: PageWriteTrigger;
  readonly noun: string;
  readonly verb: string;
  /** Destination path in the request body (dotted) → the binding that fills it. */
  readonly body: Readonly<Record<string, PageWriteBindingMeta>>;
}

const WRITE_VALUE_SOURCES: readonly string[] = ['componentValue', 'componentValueAsJson', 'urlParameter', 'payload'];

/**
 * CAOS-1575 — prototype-chain identifiers denied as a destination path SEGMENT.
 *
 * Mirrors the kernel's `RESERVED_IDENTIFIERS` (`kernel/src/meta/shared.ts`, CAOS-1354) rather than
 * importing it, because this projection deliberately depends on no kernel code. A destination path
 * is written into the request body by plain property assignment, so `__proto__` as a segment would
 * reach `Object.prototype` itself: the deploy gate refuses such a path, and this is the second line
 * for metadata that did not come through that gate.
 */
const RESERVED_PATH_SEGMENTS: ReadonlySet<string> = new Set([
  'constructor',
  'prototype',
  '__proto__',
  '__defineGetter__',
  '__defineSetter__',
  '__lookupGetter__',
  '__lookupSetter__',
]);

/** True when any dot-joined segment of a destination path is a reserved prototype-chain identifier. */
function hasReservedPathSegment(path: string): boolean {
  return path.split('.').some((seg) => RESERVED_PATH_SEGMENTS.has(seg.toLowerCase()));
}
const WRITE_TRIGGERS: readonly string[] = ['save'];

/**
 * CAOS-1575 — read a placement's `write`, or `undefined` when it does not have one this workspace
 * can act on. Defensive on the same rule as `projectPlacedRead`, and it matters more here because
 * what is being projected performs a WRITE: every drop below fails CLOSED, leaving the piece unable
 * to save rather than able to save something nobody declared.
 *
 *   - no `noun`/`verb`, or an `on` outside the closed set ⇒ not a write. An unrecognised trigger is
 *     dropped rather than defaulted to `save`: a workspace guessing which event fires a write is a
 *     workspace inventing one.
 *   - a binding whose `from` is outside the closed set drops the WHOLE write. The deploy gate
 *     refuses one by name, so a workspace seeing it is reading metadata that did not come through
 *     that gate. Dropping only the binding would leave a write that still fires with a field
 *     missing — and a `component-upsert` missing `expectedVersion` is a save with no precondition,
 *     the unconditional overwrite that route refuses to let anyone perform.
 *   - `payload` with no field name drops the whole write, for the same reason: deploy refuses it,
 *     and a payload binding that names nothing has not said what it takes.
 *   - a name on any OTHER source is IGNORED, and the binding kept. Deploy refuses that too, but if
 *     one arrives, the name was never meaningful — `urlParameter` means the route parameter spelled
 *     like the destination's last segment, and honouring a different name here would be this layer
 *     inventing exactly the remapping the schema exists to forbid.
 *   - nothing left to bind ⇒ not a write, rather than a write that POSTs an empty body.
 */
function projectPlacedWrite(raw: unknown): PageWriteMeta | undefined {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const rec = raw as Record<string, unknown>;
  const noun = str(rec, 'noun');
  const verb = str(rec, 'verb');
  const on = str(rec, 'on');
  if (!noun || !verb || !on || !WRITE_TRIGGERS.includes(on)) return undefined;

  const rawBody = rec['body'];
  const body: Record<string, PageWriteBindingMeta> = {};
  if (rawBody != null && typeof rawBody === 'object' && !Array.isArray(rawBody)) {
    for (const [path, rawBinding] of Object.entries(rawBody as Record<string, unknown>)) {
      if (rawBinding == null || typeof rawBinding !== 'object' || Array.isArray(rawBinding)) continue;
      // Drops the WHOLE write, on the same rule as an unreadable binding below: a path that would
      // assign through the prototype chain is not a field this piece may write, and a workspace
      // seeing one is reading metadata that did not come through the deploy gate.
      if (hasReservedPathSegment(path)) return undefined;
      const b = rawBinding as Record<string, unknown>;
      const from = str(b, 'from');
      const name = str(b, 'name');
      // An unreadable binding drops the WHOLE write, not just itself. Dropping one field would
      // leave a write that still fires and POSTs a body missing a field the author declared — and
      // for `expectedVersion` that is a save sent with no precondition, which is precisely the
      // unconditional overwrite `component-upsert` refuses to allow anybody to perform. Fail closed:
      // the piece cannot save, rather than saving something nobody declared.
      if (!from || !WRITE_VALUE_SOURCES.includes(from)) return undefined;
      if (from === 'payload' && !name) return undefined;
      body[path] = from === 'payload'
        ? { from: from as PageWriteValueSource, name: name! }
        : { from: from as PageWriteValueSource };
    }
  }
  if (Object.keys(body).length === 0) return undefined;

  return { on: on as PageWriteTrigger, noun, verb, body };
}

/**
 * CAOS-654 — one thing placed inside a placed piece: text, a further placed piece, or a placement this
 * workspace cannot read, carried so the screen can name it rather than draw the parent without it.
 */
export type PagePlacedChildMeta = string | PageComponentItemMeta | PageUnknownItemMeta;

/** CAOS-345 — one saved list view: a named set of columns (and a sort) over a single object. */
/**
 * CAOS-349 — one condition a saved list view declares. `eq` matches exactly, `contains` is a
 * case-insensitive substring; the value `$currentUser` means "the person reading this" and is
 * resolved server-side, never here.
 */
export interface ListViewFilter {
  readonly field: string;
  /**
   * CAOS-1492 K6/CAOS-1624 — apply this condition ONLY to a viewer who does NOT hold the named
   * system permission. Absent means it applies to everyone, which is what every filter did before.
   *
   * Deliberately negative, and the schema offers no positive form: a condition can only ever be
   * ADDED, so this can narrow what somebody sees and can never widen it. A filter that applied
   * only to someone who HOLDS a permission would be a grant wearing a filter's clothes.
   */
  readonly appliesWhen?: { readonly lacksPermission: string };
  /**
   * CAOS-1601 — `outcome` names a CLASS of values rather than a value: `open`, `won` or `lost`, as
   * declared on the field's value set options. The Engine resolves it server-side against that set,
   * so a surface never has to enumerate which statuses currently mean live work — the enumeration
   * was a second copy of the classification, and it went stale the first time a status was added.
   */
  readonly op: 'eq' | 'contains' | 'outcome';
  readonly value: unknown;
}

/**
 * CAOS-467 — the five ways a list view may ask to be shown. Mirrors
 * `ListViewPresentationSchema` (kernel `src/meta/metatypes.ts`); kept as a bare string union here
 * rather than importing the kernel package so the host's parsing stays a plain string read like
 * every other body field on this interface.
 */
export type ListViewPresentation = 'table' | 'card' | 'kanban' | 'group' | 'split';

export interface ListViewMeta {
  /** The authored component key, e.g. `account_all` — what a link asks for by name. */
  readonly key: string;
  /** The object the view is over. */
  readonly object: string;
  /** The view's display label, e.g. "All Accounts". */
  readonly label: string;
  /** The columns it shows, as bare field api names, in order. Empty ⇒ the object's defaults. */
  readonly columns: readonly string[];
  /**
   * CAOS-1646 L1 — the heading a column named for ITSELF, keyed by field api name. A field absent
   * from this map defers to its own label, which is what every column did before this.
   *
   * `columns` stays a list of bare api names because that is what this projection has always
   * promised and what three readers downstream consume. A MAP beside it would be two things to
   * keep in step if it were AUTHORED — which is exactly why the metatype refused that shape and
   * takes `{field, label}` in one array instead. Here it is DERIVED, in one place, from that one
   * array, so it cannot disagree with the metadata; it is a projection of the authoring, not a
   * second surface for it.
   */
  readonly columnLabels?: Readonly<Record<string, string>>;
  /**
   * CAOS-1677 — the columns that asked to be EDITED here, by field api name.
   *
   * The same shape and the same reasoning as `columnLabels` above, derived in the same loop over
   * the same authored array: `columns` stays bare api names, and this is a projection of the
   * authoring rather than a second surface for it. A field absent from this set asked for nothing,
   * which is every column authored before CAOS-1672 made the declaration expressible.
   *
   * A SET rather than a map of the enum, because `readonly` is the absence of a request and has
   * nothing to carry: the kernel's `UiBehaviorSchema` resolves an absent declaration to `readonly`
   * for a list column already (`assemble-list.ts`), so a host that stored both words would be
   * holding a distinction it can never act on differently.
   *
   * MEMBERSHIP IS NOT PERMISSION, and the gap matters here more than on a record page. This says
   * the view asked; whether an editor is drawn is decided later and separately against the
   * person's field permissions, the field's storage kind and the column's job in the table.
   */
  readonly columnEditable?: ReadonlySet<string>;
  /**
   * CAOS-1646 L4 — the search box's own wording. Absent ⇒ the host words it from the fields it
   * matches (`searchPromptFor`).
   */
  readonly searchPlaceholder?: string;
  /** CAOS-1646 L4 — the empty state for an unsearched, unfiltered list. Absent ⇒ generated. */
  readonly emptyLabel?: string;
  /**
   * CAOS-1646 L4 — the empty state for "the search matched nothing"; `{search}` is replaced with
   * what was typed. Kept APART from `emptyLabel` because `describeEmpty` already refuses to
   * conflate them: "No accounts to display yet" is true of a new workspace and false of a search
   * that found nothing, and a reader of the second concludes their records are gone.
   */
  readonly searchEmptyLabel?: string;
  /**
   * CAOS-1646 L5 — the orderings this list OFFERS, in order. Absent ⇒ the generated pair per
   * sortable field, unchanged. Distinct from `sort`, which is the one it OPENS on.
   */
  readonly sortOptions?: readonly { readonly value: string; readonly label: string }[];
  /**
   * CAOS-1860 — the buttons this list draws on every row, in the same vocabulary a record's own
   * buttons and a related list's row buttons use. Absent ⇒ no actions column at all, which is every
   * list authored before this: a table that grows an empty trailing column because a view declared
   * no buttons has lost a column of room for no reason (the rule `buildRecordTable` already states
   * on its own `rowActions`).
   *
   * `RecordActionMeta[]`, the SAME projection `RelatedList.rowActions` carries, so the host has one
   * row-action shape and not two. MEMBERSHIP IS NOT PERMISSION, on the rule `columnEditable` states
   * above: this says the view declared them. Whether any given button is drawn for any given row is
   * decided later and separately by `recordActionBar`, against the person's permissions, the
   * action's `appliesWhen` and its `requires`.
   */
  readonly rowActions?: readonly RecordActionMeta[];
  /** The sort it opens on, when the view declares one. */
  readonly sort?: { readonly field: string; readonly dir: 'asc' | 'desc' };
  /** The conditions it narrows by (CAOS-349). Empty/absent ⇒ every record the reader may see. */
  readonly filters?: readonly ListViewFilter[];
  /**
   * CAOS-360 — the CREATE control this list offers, when it authored one. ABSENT ⇒ the list does
   * not offer creating a record, which is what every list view authored before this one says.
   *
   * `label` is the control's wording; absent ⇒ the host's own word, so declaring the intent does
   * not force a package to re-state "New". Never a grant: someone who may not create the object is
   * not offered it whatever this says.
   */
  readonly create?: { readonly label?: string };
  /**
   * CAOS-467 — which presentation this view asks to open on. Absent ⇒ the surface keeps
   * inferring the default the way it always has (CAOS-379's `card`). A declared value is a
   * REQUEST: the surface still refuses to open on `kanban`/`group` when the object has nothing
   * to stand them up from, falling back to its own inference in that case.
   */
  readonly presentation?: ListViewPresentation;
  /**
   * CAOS-467 — the field a `kanban`/`group` presentation stands its columns/sections up from, as
   * a bare field api name. The kernel schema requires this whenever `presentation` is `kanban`
   * or `group`; a `table`/`card`/`split` view may still declare it to seed the grouped view's
   * default without switching to it.
   */
  readonly groupBy?: string;
  /**
   * CAOS-467 — which fields a search box on this list matches over, as bare field api names.
   * Empty/absent ⇒ no search-field restriction is declared (unchanged from today).
   */
  readonly search?: readonly string[];
  /**
   * CAOS-467 — what belongs on a card for this view, as bare field api names, separate from
   * `columns` (which stays the table's concern). Empty/absent ⇒ the surface's existing
   * card-field inference is unchanged.
   */
  readonly cardFields?: readonly string[];
  /**
   * CAOS-1492 K5 / CAOS-1623 — columns whose value is PROJECTED FROM THE ROW'S IDENTITY rather
   * than read from a stored field. Empty/absent ⇒ the list shows only what the records store,
   * which is every list view authored before this was read.
   *
   * It exists because the reference code a reporter is handed on submit is a projection of the id
   * (`FB-` plus six hex digits), so the object has no field holding it: the host's own My Feedback
   * list could show that code and a list view authored as metadata could not.
   *
   * ONLY THE PRESENTATIONS THAT DRAW COLUMNS DRAW THESE, which is `table` and `group` — the
   * grouped view is a table per group and goes through the same builder. `card`, `split` and
   * `kanban` draw `cardFields` and lanes, not columns, so a derived column is not a thing they
   * have anywhere to put. See the renderer for why that is stated rather than silently true.
   */
  readonly derivedColumns?: readonly ListViewDerivedColumn[];
}

/**
 * CAOS-1623 — one authored derived column: what to call it, and how to project it from the id.
 *
 * `from` is `'id'` and only `'id'`, which is the kernel schema's own constraint rather than this
 * projection's: there is exactly one thing a row has that is not a field. The projection itself
 * lives in `surfaces/submission-ref.ts` beside the submission code it generalises, so a derived
 * column and a confirmation reference cannot drift into two answers for the same id.
 */
export interface ListViewDerivedColumn {
  /** The column's own key. NOT a field api name — no field is being read. */
  readonly key: string;
  /** The heading. Required by the schema: a column nobody can name is not worth drawing. */
  readonly label: string;
  /**
   * The source. `'id'` is the row's identity; absent ⇒ this column folds `cases` instead. The
   * metatype refuses a column declaring neither and one declaring both.
   */
  readonly from?: 'id';
  readonly prefix?: string;
  readonly length?: number;
  readonly transform?: 'upper' | 'lower';
  /**
   * CAOS-1646 R2 — fold this column's value out of several of the record's FIELDS, first match
   * winning. `at` is a field api name here, because the row is a record.
   */
  readonly cases?: readonly {
    readonly at: string;
    readonly equals?: string;
    readonly contains?: string;
    readonly value: string;
  }[];
  /** The word when no case matched. Required with `cases`. */
  readonly otherwise?: string;
  /**
   * CAOS-1646 L2 — where this column sits, as a 0-based index into the column list as built so
   * far. Absent ⇒ appended after the stored columns, which is where every one went before this.
   */
  readonly at?: number;
}

/** The retrieve envelope, as the host reads it off the wire (substrate passthrough). */
interface RetrieveEnvelope {
  ok?: boolean;
  data?: { version?: number; components?: RawComponent[]; seamOrderOverrides?: Record<string, unknown> };
}

interface RawComponent {
  type?: string;
  key?: string;
  body?: Record<string, unknown>;
}

/**
 * An org with nothing in it, and the ONE place that shape is written down.
 *
 * CAOS-623 — the return type is `OrgMetadata`, so the compiler keeps it complete: a property added
 * to the interface fails HERE, at the one declaration, rather than everywhere somebody wrote the
 * shape out by hand. Six test files each kept their own copy, each an `OrgMetadata` literal that
 * stopped being one the moment the interface grew — six type errors with nothing wrong with the
 * code, in the one type program nothing built, which is how they sat there.
 *
 * A FUNCTION, not a shared constant, because the empty maps inside one are the part a caller is
 * most likely to reach into. Handed out as a single object, every holder of it would share the same
 * `objects`, `pages` and `labels`, and one test setting a key on a fixture derived from it would
 * change what an unrelated test in another file sees — a corruption that surfaces as an unrelated
 * flake much later. Each caller gets its own.
 */
export function emptyMetadata(): OrgMetadata {
  return {
    apps: [],
    labels: new Map(),
    objects: new Map(),
    valueSets: new Map(),
    valueSetPresentations: new Map(),
    listViews: new Map(),
    intakeBySource: new Map(),
    guidanceBySource: new Map(),
    anchorKindBySource: new Map(),
    assistantBySource: new Map(),
    components: [],
    pages: new Map(),
    setupContributions: [],
    rosterDetailPagesByListHref: new Map(),
    richTextToolbar: [],
    pageTemplates: new Map(),
    seamOrderOverrides: new Map(),
    installedPackageKeys: new Set(),
  };
}

/** The one handed back when metadata cannot be read at all. Its own copy, like every other. */
const EMPTY_METADATA: OrgMetadata = emptyMetadata();

/**
 * CAOS-736 — parse the retrieve envelope's `seamOrderOverrides` (seam id → pinned source keys) into
 * the map {@link OrgMetadata.seamOrderOverrides} carries. Fail-soft, matching every other read in
 * this projection: a malformed entry (not an array, or an array of non-strings) is dropped rather
 * than thrown on — the seam simply resolves with no pin, the same as an org that never set one.
 */
function projectSeamOrderOverrides(raw: Record<string, unknown> | undefined): Map<string, readonly string[]> {
  const out = new Map<string, readonly string[]>();
  if (!raw) return out;
  for (const [seamId, value] of Object.entries(raw)) {
    if (!Array.isArray(value)) continue;
    const keys = value.filter((k): k is string => typeof k === 'string' && k.length > 0);
    if (keys.length > 0) out.set(seamId, keys);
  }
  return out;
}

/**
 * CAOS-320 — project a page component whose data source is `intake` into a {@link ShellIntakeDescriptor},
 * or `null` when the page is not an intake form. Fail-soft: a malformed field is dropped, never thrown.
 */
function intakeOfPage(body: Record<string, unknown> | undefined): ShellIntakeDescriptor | null {
  const ds = body?.['dataSource'] as Record<string, unknown> | undefined;
  if (!ds || ds['kind'] !== 'intake') return null;
  const write = ds['write'] as { noun?: unknown; verb?: unknown } | undefined;
  const object = ds['object'];
  if (typeof write?.noun !== 'string' || typeof write?.verb !== 'string' || typeof object !== 'string') return null;
  const rawFields = Array.isArray(ds['fields']) ? (ds['fields'] as unknown[]) : [];
  const fields: ShellIntakeField[] = [];
  for (const rf of rawFields) {
    const f = rf as Record<string, unknown>;
    const field = f['field'];
    const label = f['label'];
    const control = f['control'];
    if (typeof field !== 'string' || typeof label !== 'string' || typeof control !== 'string') continue;
    // CAOS-1625 — `boolean` joins the four. This list is a WHITELIST, so a control the kernel
    // grammar gained and this line did not know about is dropped silently: the field vanishes from
    // the form rather than rendering wrong, which is the safe direction but also why adding a
    // control means editing here as well as the renderer.
    if (
      control !== 'text' &&
      control !== 'textarea' &&
      control !== 'picklist' &&
      control !== 'attachment' &&
      control !== 'boolean'
    )
      continue;
    const options = Array.isArray(f['options'])
      ? (f['options'] as unknown[])
          .map((o) => o as Record<string, unknown>)
          .filter((o) => typeof o['value'] === 'string' && typeof o['label'] === 'string')
          .map((o) => ({ value: o['value'] as string, label: o['label'] as string }))
      : undefined;
    fields.push({
      field,
      label,
      control,
      ...(typeof f['required'] === 'boolean' ? { required: f['required'] as boolean } : {}),
      // CAOS-1697 — only a real boolean is carried, and the fail-soft direction is DELIBERATELY
      // towards drawing the caption: a malformed `hideLabel` (the string "true", a 1) leaves the
      // label visible, which is the state every page is in today. The opposite default would let a
      // typo quietly strip a caption off a form that still needs one, and a missing caption is
      // much harder to notice than a redundant one.
      ...(typeof f['hideLabel'] === 'boolean' ? { hideLabel: f['hideLabel'] as boolean } : {}),
      ...(typeof f['placeholder'] === 'string' ? { placeholder: f['placeholder'] as string } : {}),
      ...(options && options.length > 0 ? { options } : {}),
      // CAOS-1625 — only a real boolean is carried. Anything else (a string "true", a number) is
      // dropped and the box draws unticked, which is the fail-soft direction: a malformed default
      // must never be the reason somebody is opted IN to something they did not ask for.
      ...(typeof f['default'] === 'boolean' ? { default: f['default'] as boolean } : {}),
      // CAOS-1663 — an attachment's constraints, read on the same fail-soft rule as everything
      // above. A malformed `accept` falls back to the host's own allowlist rather than to
      // "accept anything", and a `maxBytes` that is not a positive, finite integer is dropped
      // rather than coerced: `Number('10mb')` is NaN and NaN fails every comparison it is put in,
      // so a coerced cap would read as a cap and enforce nothing at all.
      ...(typeof f['accept'] === 'string' && f['accept'] !== '' ? { accept: f['accept'] as string } : {}),
      // `isSafeInteger`, not `isInteger`, so this layer agrees exactly with the kernel's upper
      // bound (`z.number().int()` emits `maximum: Number.MAX_SAFE_INTEGER`). `Number.isInteger(1e300)`
      // is true, so the looser test let a value the deploy gate refuses through the projection —
      // unreachable via a validated deploy, but the two layers disagreeing about what is acceptable
      // is the kind of gap that only stays harmless until something else writes this field.
      ...(typeof f['maxBytes'] === 'number' && Number.isSafeInteger(f['maxBytes']) && (f['maxBytes'] as number) > 0
        ? { maxBytes: f['maxBytes'] as number }
        : {}),
    });
  }
  if (fields.length === 0) return null;
  // CAOS-326 — the CAOS-1492 vocabulary, read on the same fail-soft rule as the fields above: a
  // malformed member is dropped rather than taking the descriptor down with it, because a panel that
  // will not open at all is a worse outcome than one missing its lead copy.
  const lead = str(ds, 'lead');
  const confirmation = intakeConfirmationOf(ds['confirmation']);
  const actions = intakeActionsOf(ds['actions']);
  return {
    title: str(body, 'label') ?? 'Submit',
    write: { noun: write.noun, verb: write.verb },
    object,
    ...(typeof ds['submitLabel'] === 'string' ? { submitLabel: ds['submitLabel'] as string } : {}),
    fields,
    ...(lead !== undefined ? { lead } : {}),
    ...(confirmation !== undefined ? { confirmation } : {}),
    ...(actions !== undefined ? { actions } : {}),
    // Only a real boolean travels. Absent MEANS "retained" downstream, so coercing anything else to
    // `false` here would turn a typo into a silent loss of the reporter's draft.
    ...(typeof ds['retainDraft'] === 'boolean' ? { retainDraft: ds['retainDraft'] as boolean } : {}),
  };
}

/**
 * CAOS-1492 K2 — an intake confirmation, or `undefined` when absent or unusable.
 *
 * `message` is required by the schema and carries the whole value of this field, so a confirmation
 * without one is dropped entirely rather than passed on as an empty shape: the form then falls back
 * to the host's own sentence, which is a working confirmation, instead of showing a blank one.
 */
function intakeConfirmationOf(raw: unknown): ShellIntakeDescriptor['confirmation'] | undefined {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const c = raw as Record<string, unknown>;
  const message = str(c, 'message');
  if (message === undefined) return undefined;
  const rawRef = c['reference'];
  if (rawRef == null || typeof rawRef !== 'object' || Array.isArray(rawRef)) return { message };
  const r = rawRef as Record<string, unknown>;
  const field = str(r, 'field');
  const idPrefix = str(r, 'idPrefix');
  const rawLength = r['idLength'];
  const idLength =
    typeof rawLength === 'number' && Number.isInteger(rawLength) && rawLength > 0 ? rawLength : undefined;
  // An empty `reference` is not a reference. Dropping it keeps "authored a reference" and "authored
  // nothing" distinguishable downstream, which is what decides whether the host's own code is shown.
  if (field === undefined && idPrefix === undefined && idLength === undefined) return { message };
  return {
    message,
    reference: {
      ...(field !== undefined ? { field } : {}),
      ...(idPrefix !== undefined ? { idPrefix } : {}),
      ...(idLength !== undefined ? { idLength } : {}),
    },
  };
}

/**
 * The kernel's own authoring-time rule for an intake action's `to`, restated verbatim.
 *
 * Copied rather than approximated, and that distinction is the whole finding it replaces. A first
 * draft of this checked `to.startsWith('/') && !to.startsWith('//')`, which reads like the same rule
 * and is strictly weaker than it. Four inputs pass that test and still navigate OFF-ORIGIN, because
 * WHATWG URL resolution against an `https:` base normalises a backslash to a slash and strips ASCII
 * tab, LF and CR from anywhere in the input:
 *
 *   `/\evil.example`     `/<LF>/evil.example`     `/<TAB>/evil.example`     `/<CR>/evil.example`
 *
 * All four collapse to `//evil.example` and resolve to `https://evil.example/`. Measured, not
 * reasoned about. The character class below admits no backslash and no whitespace, so it refuses
 * every one of them; `(?!\/)` is what refuses the plain protocol-relative form.
 *
 * Kept in sync by `caos-326-intake-vocabulary.test.ts`, which reads the pattern out of
 * `kernel/src/meta/page.ts` and fails if the two texts diverge — the same way the host's other
 * kernel mirrors are pinned. A hand-rolled subset of somebody else's rule is exactly the thing that
 * drifts silently, and a comment claiming it duplicates that rule is worse than no comment.
 */
const INTAKE_ACTION_PATH = /^\/(?!\/)[A-Za-z0-9\-._~!$&'()*+,;=:@%/?#]*$/;

/**
 * CAOS-1492 K3 — the navigate actions an intake form offers, or `undefined` when it authored none.
 *
 * The `to` check is deliberately repeated here rather than left to the kernel schema that already
 * carries it. The schema's regex holds at authoring time; this runs on a body that was STORED, and a
 * body stored before that regex existed can carry anything a string can. The host is what eventually
 * hands this value to `location.assign`, so it is the right place for the second half of the
 * guarantee — provided it is the SAME rule, which is why it is the same regex and not a paraphrase.
 */
function intakeActionsOf(raw: unknown): ShellIntakeDescriptor['actions'] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: { id: string; label: string; to: string; intent?: string; helpText?: string }[] = [];
  for (const entry of raw as unknown[]) {
    if (entry == null || typeof entry !== 'object' || Array.isArray(entry)) continue;
    const a = entry as Record<string, unknown>;
    const id = str(a, 'id');
    const label = str(a, 'label');
    const to = str(a, 'to');
    if (id === undefined || label === undefined || to === undefined) continue;
    if (!INTAKE_ACTION_PATH.test(to)) continue;
    const intent = str(a, 'intent');
    // CAOS-1626 — forwarded like `intent`, and unlike `description` on a field: this one is written
    // FOR the person filling the form in, so the reader of the form is exactly who it is for. It is
    // read through `str()`, so a body storing a non-string or an empty string yields `undefined` and
    // the button simply carries no help rather than an empty tooltip.
    //
    // The whitespace check is the SECOND HALF of the schema's `\S`, and it is here for the same
    // reason the `to` path check is: the schema holds at authoring time, this runs on a body that
    // was STORED, and a body stored before that rule existed can carry anything a string can. A
    // lone space would otherwise reach the renderer and set a `title` that hovers as an empty box —
    // a broken tooltip rather than no tooltip. Same rule as the kernel's, not a paraphrase of it.
    const rawHelp = str(a, 'helpText');
    const helpText = rawHelp !== undefined && /\S/.test(rawHelp) ? rawHelp : undefined;
    out.push({
      id,
      label,
      to,
      ...(intent !== undefined ? { intent } : {}),
      ...(helpText !== undefined ? { helpText } : {}),
    });
  }
  return out.length > 0 ? out : undefined;
}

/** Read a string field off a component body, or `undefined`. */
function str(body: Record<string, unknown> | undefined, field: string): string | undefined {
  const v = body?.[field];
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

/**
 * CAOS-283 — read a HUMAN-FACING string that may have been authored with its translations.
 *
 * A label is now either a bare string or `{ default, translations }` (kernel `meta/shared.ts`
 * LocalizableText). The host reads component BODIES verbatim — `metadata/retrieve` returns each
 * stored body untouched, by design — so it sees whichever shape the author wrote, and `str()`
 * above returns `undefined` for the map. Every caller of `str()` for a label then falls back to
 * the component KEY, and a screen that said "Spare Parts" starts saying "spare_parts". Nothing
 * would catch it: the value is `unknown` here, so the compiler sees no change, and the fallback
 * has no test asserting it is not taken.
 *
 * This resolves the DEFAULT only. Picking the viewer's language needs the viewer's language
 * plumbed into this projection, which is a separate change — `projectMetadata` is a pure function
 * of the component list today and has no principal in scope. Reading the default first means
 * authoring a translation never makes a surface worse, which is the part that has to be true
 * before anybody authors one.
 */
function authoredStr(body: Record<string, unknown> | undefined, field: string): string | undefined {
  const direct = str(body, field);
  if (direct !== undefined) return direct;
  const v = body?.[field];
  if (v === null || typeof v !== 'object' || Array.isArray(v)) return undefined;
  const def = (v as { default?: unknown }).default;
  return typeof def === 'string' && def.length > 0 ? def : undefined;
}

/** Read a boolean body field (e.g. an app's `showInAppSwitcher`); absent/non-boolean ⇒ undefined. */
function bool(body: Record<string, unknown> | undefined, field: string): boolean | undefined {
  const v = body?.[field];
  return typeof v === 'boolean' ? v : undefined;
}

/** Read a positive-integer body field (e.g. an app's `overflowAfter`); absent/invalid ⇒ undefined. */
function posInt(body: Record<string, unknown> | undefined, field: string): number | undefined {
  const v = body?.[field];
  return typeof v === 'number' && Number.isInteger(v) && v >= 1 ? v : undefined;
}

/** Read an INTEGER body field (e.g. an app's `sortOrder`, which may be 0 or negative). */
function int(body: Record<string, unknown> | undefined, field: string): number | undefined {
  const v = body?.[field];
  return typeof v === 'number' && Number.isInteger(v) ? v : undefined;
}

/** The bare field api name from an object-qualified field key (`account.billing_city` → `billing_city`). */
export function bareFieldName(fieldKey: string): string {
  const dot = fieldKey.indexOf('.');
  return dot === -1 ? fieldKey : fieldKey.slice(dot + 1);
}

/**
 * CAOS-1616 — read an action's `destination`, or `undefined` when it does not have a whole one.
 *
 * ONE reader for both action grammars, deliberately. A record action and a roster row action carry
 * the same `{ object, field }` and must agree about what counts as a usable one; two copies of that
 * rule is two places for it to drift, and the drift would be invisible — each grammar would keep
 * working on its own fixtures while disagreeing about an edge nobody wrote a test for.
 *
 * BOTH HALVES OR NOTHING. An object with no field to read the id from, and a field with no object to
 * resolve it against, are each half an address; taking half and guessing the rest is how a link goes
 * somewhere nobody authored. That is the same rule `to` follows.
 *
 * The field is normalised through {@link bareFieldName} for the reason every other authored field
 * reference here is: an author may write it namespace-qualified, and the row this is read against is
 * keyed by the bare name.
 */
function projectActionDestination(raw: unknown): ActionDestinationMeta | undefined {
  if (raw === null || typeof raw !== 'object') return undefined;
  const rec = raw as Record<string, unknown>;
  const object = str(rec, 'object');
  const field = str(rec, 'field');
  if (!object || !field) return undefined;
  return { object, field: bareFieldName(field) };
}

/** A string list off a body field, filtered to strings; `[]` when absent. */
/**
 * CAOS-362 — read a list of authored actions into the host's shape. An action missing the piece
 * that says WHAT IT DOES (a navigate with no destination, an update with no field) is DROPPED
 * rather than drawn: a button that cannot say what pressing it means is worse than one that is not
 * there.
 *
 * CAOS-559 moved this out of the layout loop unchanged, because it now reads the same authored
 * shape from two places — an OBJECT defining its actions, and a layout still defining them inline.
 * One reader, so a definition and a placement of the same idea cannot drift into two dialects.
 */
function recordActionsFrom(raw: unknown, origin: 'object' | 'surface'): RecordActionMeta[] {
  const actions: RecordActionMeta[] = [];
  for (const a of Array.isArray(raw) ? (raw as unknown[]) : []) {
    if (a == null || typeof a !== 'object') continue;
    const rec = a as Record<string, unknown>;
    const id = str(rec, 'id');
    const label = str(rec, 'label');
    const kind = str(rec, 'kind');
    if (!id || !label) continue;
    // CAOS-561 — `replaces` is read from an OBJECT definition and from nowhere else. A surface
    // places actions; it does not get to say what a standard verb means, because two surfaces for
    // one object would then be free to disagree about what Edit does.
    const replaces = origin === 'object' ? str(rec, 'replaces') : undefined;
    const shared = {
      id,
      label,
      intent: str(rec, 'intent'),
      appliesWhen: str(rec, 'appliesWhen'),
      ...(replaces !== undefined && (STANDARD_VERBS as readonly string[]).includes(replaces)
        ? { replaces: replaces as StandardVerb }
        : {}),
    };
    if (kind === 'navigate') {
      const to = str(rec, 'to');
      // CAOS-1616 — a destination is the other way to say where this goes, and it has to be read
      // here or the action is dropped for having no `to`: silently, and looking exactly like an
      // action nobody authored. Both halves must be present for it to mean anything — an object
      // with no field to read the id from, or a field with no object to resolve it against, names
      // half an address — so it is taken only when it is whole, on the same rule `to` follows.
      const destination = projectActionDestination(rec['destination']);
      if (to) actions.push({ ...shared, kind: 'navigate', to });
      else if (destination) actions.push({ ...shared, kind: 'navigate', destination });
    } else if (kind === 'update') {
      // CAOS-406 — both authored spellings collapse to one list. A single assignment is a list of
      // one, so nothing downstream has to know which spelling was written.
      //
      // An ASSIGNMENT that says nothing usable is dropped individually, on the same rule a create's
      // prefill entry is: the rest of the button still works. But an action left with NO usable
      // assignment is dropped entirely, on the rule a navigate with no destination is — there is
      // nothing left for it to do, and a button that writes nothing is worse than an absent one
      // because it looks like it worked.
      const set: UpdateAssignmentMeta[] = [];
      const field = str(rec, 'field');
      if (field && 'value' in rec) set.push({ field: bareFieldName(field), value: rec['value'] });
      for (const entry of Array.isArray(rec['set']) ? (rec['set'] as unknown[]) : []) {
        if (entry == null || typeof entry !== 'object') continue;
        const assignment = entry as Record<string, unknown>;
        const name = str(assignment, 'field');
        if (!name || !('value' in assignment)) continue;
        set.push({ field: bareFieldName(name), value: assignment['value'] });
      }
      if (set.length > 0) {
        actions.push({ ...shared, kind: 'update', set, confirm: str(rec, 'confirm') });
      }
    } else if (kind === 'clone') {
      actions.push({ ...shared, kind: 'clone' });
    } else if (kind === 'create') {
      // CAOS-560 — the target object is the whole action; without it there is nothing to create, so
      // it is DROPPED on the same rule a navigate with no destination is. A prefill ENTRY that says
      // nothing usable is dropped individually instead: the button still makes a record, it just
      // starts one field emptier, and losing the whole action over one bad prefill would be a
      // larger failure than the one being avoided.
      const target = str(rec, 'object');
      if (target) {
        const prefill: CreatePrefillMeta[] = [];
        for (const pre of Array.isArray(rec['prefill']) ? (rec['prefill'] as unknown[]) : []) {
          if (pre == null || typeof pre !== 'object') continue;
          const entry = pre as Record<string, unknown>;
          const field = str(entry, 'field');
          if (!field) continue;
          const from = str(entry, 'from');
          if (from) prefill.push({ field: bareFieldName(field), from: bareFieldName(from) });
          else if ('value' in entry) prefill.push({ field: bareFieldName(field), value: entry['value'] });
        }
        // CAOS-1706 — read as STRICTLY true rather than as truthy. The kernel's schema already
        // refuses a non-boolean, so anything else arriving here means the projection is reading
        // metadata this host did not validate, and the safe reading of an unrecognised value is
        // the default: do not attach. Attaching on a truthy accident would put a record on a
        // timeline nobody asked to put it on, and `parent_id` is create-only, so it would not be
        // undone by the next save.
        const attach = rec['attachToRecord'] === true;
        actions.push({
          ...shared,
          kind: 'create',
          object: target,
          prefill,
          ...(attach ? { attachToRecord: true } : {}),
        });
      }
    } else if (kind === 'component') {
      // The component is likewise the whole action. `requires` is normalised to an array here so
      // every reader can ask what this action needs without first asking whether it said.
      const component = str(rec, 'component');
      if (component) {
        const requires: ActionRequirementMeta[] = [];
        for (const req of Array.isArray(rec['requires']) ? (rec['requires'] as unknown[]) : []) {
          if (req == null || typeof req !== 'object') continue;
          const entry = req as Record<string, unknown>;
          const can = str(entry, 'can');
          const object = str(entry, 'object');
          if ((can !== 'create' && can !== 'update') || !object) continue;
          const field = can === 'update' ? str(entry, 'field') : undefined;
          requires.push(field ? { can, object, field: bareFieldName(field) } : { can, object });
        }
        const inputs = rec['inputs'];
        actions.push({
          ...shared,
          kind: 'component',
          component,
          ...(inputs != null && typeof inputs === 'object' && !Array.isArray(inputs)
            ? { inputs: inputs as Record<string, unknown> }
            : {}),
          requires,
        });
      }
    }
  }
  return actions;
}

/**
 * CAOS-559 — resolve a surface's placement list against the actions its object defines, IN THE
 * ORDER THE SURFACE NAMES THEM. The list is an arrangement, not a set, so this walks the placements
 * and not the definitions.
 *
 * A key naming no defined action is dropped, on the same rule the reader above drops a button that
 * cannot say what it does — there is nothing to draw. It is dropped rather than refused because the
 * write boundary is where a dangling reference belongs, not the render path, which has no way to
 * report anything to the person who authored it.
 */
function placeActions(defined: readonly RecordActionMeta[], keys: readonly string[]): RecordActionMeta[] {
  const byId = new Map(defined.map((a) => [a.id, a]));
  const placed: RecordActionMeta[] = [];
  for (const key of keys) {
    const action = byId.get(key);
    if (action) placed.push(action);
  }
  return placed;
}

function strList(body: Record<string, unknown> | undefined, field: string): string[] {
  const v = body?.[field];
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}

/**
 * CAOS-360 — project a layout section's `fields` from either authored shape.
 *
 * Reading only the bare shape (which is what this did, through `strList`) would DROP a field
 * authored as `{ field, uiBehavior }` off the record page entirely — a worse outcome than showing
 * it un-editable, and a silent one. So both shapes are read, and only the second can ask for an
 * editor.
 */
function layoutSectionFields(raw: unknown): LayoutSectionField[] {
  if (!Array.isArray(raw)) return [];
  const out: LayoutSectionField[] = [];
  for (const entry of raw) {
    if (typeof entry === 'string') {
      out.push({ name: bareFieldName(entry), offeredForEdit: false, fullWidth: false });
    } else if (entry && typeof entry === 'object') {
      const field = (entry as { field?: unknown }).field;
      if (typeof field === 'string') {
        out.push({
          name: bareFieldName(field),
          offeredForEdit: (entry as { uiBehavior?: unknown }).uiBehavior === 'editable',
          // CAOS-1881 — `=== true` rather than a truthiness read. The kernel's schema admits only
          // the literal `true`, so anything else reaching here came from a manifest that did not
          // go through it, and the safe reading of an unrecognised value is the default layout.
          fullWidth: (entry as { fullWidth?: unknown }).fullWidth === true,
        });
      }
    }
  }
  return out;
}

/** CAOS-467 — the presentation values the kernel's `ListViewPresentationSchema` allows. */
const PRESENTATION_VALUES: ReadonlySet<string> = new Set<ListViewPresentation>([
  'table',
  'card',
  'kanban',
  'group',
  'split',
]);

/** CAOS-342 — who the signed-in person is, as the account block needs to show them. */
export interface SessionIdentity {
  /** What they signed in with — the only name the platform holds for a person today. */
  readonly identifier?: string;
  /** The workspace they are in, by its label. */
  readonly orgLabel?: string;
  /**
   * CAOS-507/CAOS-1008 — the server-resolved registration kind. `paid` is production;
   * `sandbox` and `trial` are explicitly non-production. Absent means the server supplied no
   * authoritative classification and must never be guessed from the hostname or label.
   */
  readonly orgType?: 'paid' | 'sandbox' | 'trial';
  /** The roles they hold; the first names the account block's second line. */
  readonly roles: readonly { readonly key: string; readonly label: string }[];
  /**
   * CAOS-694/CAOS-342 — the app keys this person ADMINISTERS (resolved `caos.effective_app_perm`,
   * a `bool_or` union of every permission set they hold, direct or via group). Distinct from
   * `setup.access` (the global Setup front door): this is per-app, e.g. Sales Atlas without also
   * being an org-wide administrator. Drives the account menu's "Settings" entry — it appears only
   * when the ACTIVE app's key is a member of this list AND the app declares a `settingsPage`.
   * Empty ⇒ administers nothing (the entry never appears for this person, on any app).
   */
  readonly administers: readonly string[];
  /**
   * CAOS-1624 — the system permissions this person holds, as the KERNEL resolved them for this
   * session: the `platform.root` capstone already expanded, and an impersonated session already
   * intersected with the human behind it.
   *
   * ABSENT MEANS NOT KNOWN, and is not the same as holding nothing — the identity read is
   * fail-soft, so a whoami that could not be made answers `undefined` here while a viewer who
   * genuinely holds no permissions answers `[]`. Both lead a `lacksPermission` filter to APPLY,
   * which is the point: the only thing that ever lifts such a filter is a viewer POSITIVELY known
   * to hold the permission, so failing closed is a property of the shape rather than a case
   * somebody has to remember to write.
   *
   * It is not a permission check and never substitutes for one. The Engine still decides what this
   * caller may read; this decides only what a surface SHOWS.
   */
  readonly heldPermissions?: readonly string[];
  /**
   * CAOS-606 — the "Viewing as" role in force on this session, as the KERNEL resolved it, or
   * undefined when it said none (or could not be asked).
   *
   * A second witness to the same fact `preview state` reports, deliberately. The kernel narrows
   * data from the session row regardless of whether the host's preview read succeeded, so a
   * workspace whose banner depends on that one read can end up narrowed and silent about it. This
   * one rides on a call the boot already makes and the handler already knows the answer to.
   */
  readonly preview?: { readonly key: string; readonly label: string };
}

/** The control-plane route that answers who the caller is. */
export const WHOAMI_PATH = '/api/auth/whoami';

/**
 * CAOS-342 — read the signed-in identity for the account block. Never throws: the workspace must
 * still render for someone whose identity read fails, it just shows less about them.
 */
export async function fetchSessionIdentity(
  fetchImpl: typeof fetch = fetch,
  path: string = WHOAMI_PATH,
): Promise<SessionIdentity> {
  try {
    const res = await fetchImpl(path, {
      method: 'GET',
      headers: { accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!res.ok) return { roles: [], administers: [] };
    const payload = (await res.json()) as { data?: Record<string, unknown> };
    const data = payload?.data ?? {};
    const rawRoles = Array.isArray(data['roles']) ? (data['roles'] as unknown[]) : [];
    const roles = rawRoles
      .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object')
      .map((r) => ({ key: String(r['key'] ?? ''), label: String(r['label'] ?? r['key'] ?? '') }))
      .filter((r) => r.key.length > 0);
    // CAOS-694/CAOS-342 — the app keys this person administers (`caos.effective_app_perm`).
    const administers = (Array.isArray(data['administers']) ? (data['administers'] as unknown[]) : [])
      .filter((a): a is string => typeof a === 'string' && a.length > 0);
    // CAOS-606 — the preview in force. A role with no key cannot be named on a banner, so a
    // malformed answer is dropped rather than rendered as "Previewing as undefined".
    const rawPreview = data['preview'];
    let preview: { key: string; label: string } | undefined;
    if (rawPreview != null && typeof rawPreview === 'object') {
      const pv = rawPreview as Record<string, unknown>;
      if (typeof pv['key'] === 'string' && pv['key']) {
        preview = { key: pv['key'], label: typeof pv['label'] === 'string' && pv['label'] ? pv['label'] : pv['key'] };
      }
    }
    return {
      ...(typeof data['identifier'] === 'string' ? { identifier: data['identifier'] } : {}),
      ...(typeof data['orgLabel'] === 'string' ? { orgLabel: data['orgLabel'] } : {}),
      ...(data['orgType'] === 'paid' || data['orgType'] === 'sandbox' || data['orgType'] === 'trial'
        ? { orgType: data['orgType'] }
        : {}),
      roles,
      administers,
      // CAOS-1624 — carried only when the answer is actually an array. A kernel that does not serve
      // it leaves this absent, which reads as "not known" and keeps every scoped filter applied.
      ...(Array.isArray(data['heldPermissions'])
        ? {
            heldPermissions: (data['heldPermissions'] as unknown[]).filter(
              (x): x is string => typeof x === 'string' && x.length > 0,
            ),
          }
        : {}),
      ...(preview ? { preview } : {}),
    };
  } catch {
    return { roles: [], administers: [] };
  }
}

/**
 * Fetch and project the org's installed metadata. Fail-soft: any transport/parse failure, or a
 * non-success envelope, yields {@link EMPTY_METADATA} (the frame then renders "no apps" honestly
 * rather than crashing). The optional `fetchImpl` seam lets tests drive it without a network.
 */
export async function fetchOrgMetadata(
  fetchImpl: typeof fetch = fetch,
  path: string = METADATA_RETRIEVE_PATH,
): Promise<OrgMetadata> {
  let envelope: RetrieveEnvelope;
  try {
    const res = await fetchImpl(path, {
      method: 'GET',
      headers: { accept: 'application/json' },
      credentials: 'same-origin',
      // Org metadata is per-session and must never be served from a stale HTTP cache.
      cache: 'no-store',
    });
    envelope = (await res.json()) as RetrieveEnvelope;
  } catch {
    return EMPTY_METADATA;
  }

  const components = envelope?.ok === true ? envelope.data?.components : undefined;
  if (!Array.isArray(components)) return EMPTY_METADATA;

  const seamOrderOverrides = projectSeamOrderOverrides(
    envelope?.ok === true ? envelope.data?.seamOrderOverrides : undefined,
  );
  return projectMetadata(components, seamOrderOverrides);
}

/** Parse a value-set component body into value → { label, intent }. */
function projectValueSet(body: Record<string, unknown> | undefined): Map<string, ValueOption> {
  const out = new Map<string, ValueOption>();
  const values = body?.['values'];
  if (!Array.isArray(values)) return out;
  for (const raw of values) {
    if (raw == null || typeof raw !== 'object') continue;
    const v = raw as Record<string, unknown>;
    const value = typeof v['value'] === 'string' ? v['value'] : undefined;
    if (value === undefined) continue;
    // CAOS-356 — only the three authored outcomes are carried through. Anything else is treated as
    // unsaid rather than passed along, so a surface can trust that a present outcome is a real one.
    const rawOutcome = v['outcome'];
    const outcome =
      rawOutcome === 'open' || rawOutcome === 'won' || rawOutcome === 'lost' ? rawOutcome : undefined;
    // CAOS-640 — the richer half of a value, read with the same suspicion as the rest of this
    // function: a non-string (or empty) image/icon/description is treated as unsaid rather than
    // passed on, so a surface can trust that a present one is real.
    const image = typeof v['image'] === 'string' && v['image'] !== '' ? v['image'] : undefined;
    const icon = typeof v['icon'] === 'string' && v['icon'] !== '' ? v['icon'] : undefined;
    const description =
      typeof v['description'] === 'string' && v['description'] !== '' ? v['description'] : undefined;
    const rawAmount = v['amount'];
    // A NUMBER, so `0` is a real qualifier ("adds nothing to the price") and must survive. That is
    // why this one tests `!== undefined` below rather than truthiness like its neighbours.
    const amount = typeof rawAmount === 'number' && Number.isFinite(rawAmount) ? rawAmount : undefined;
    out.set(value, {
      value,
      // CAOS-283 — a value-set option's label may carry its translations. The stored `value` is
      // never translated and stays the fallback: it is an identifier, not words.
      label: authoredStr(v, 'label') ?? value,
      intent: typeof v['intent'] === 'string' ? v['intent'] : undefined,
      ...(outcome ? { outcome } : {}),
      ...(image ? { image } : {}),
      ...(icon ? { icon } : {}),
      ...(description ? { description } : {}),
      ...(amount !== undefined ? { amount } : {}),
    });
  }
  return out;
}

/**
 * Project a raw component list to {@link OrgMetadata}. Pure (no I/O), so the deterministic tests
 * feed it a fixture component set directly. Apps resolve their tab keys against the `tab` catalog to
 * carry real tab labels + target objects; every app/package contributes its label to the lookup so
 * an injection source key resolves to a real name; and objects assemble their field dictionary +
 * layout sections + list columns (CAOS-306) for the list/record surfaces.
 */
/** The panels an edge tab can declare it opens (CAOS-734), as the kernel schema closes them. */
export type EdgeAnchorKind = 'assistant' | 'report' | 'help';

const EDGE_ANCHOR_KINDS: ReadonlySet<string> = new Set<EdgeAnchorKind>(['assistant', 'report', 'help']);

/**
 * CAOS-151 — the panel kind a package body's edge-anchored shell claim declares, when it declares one
 * the frame knows how to open. Anything else reads as undeclared, leaving the frame's own fallback
 * to decide exactly as it did before.
 */
function declaredEdgeKind(body: Record<string, unknown> | undefined): EdgeAnchorKind | undefined {
  for (const c of edgeAnchoredClaims(body)) {
    const anchor = c['anchor'];
    const kind = anchor && typeof anchor === 'object' ? (anchor as Record<string, unknown>)['kind'] : undefined;
    if (typeof kind === 'string' && EDGE_ANCHOR_KINDS.has(kind)) return kind as EdgeAnchorKind;
  }
  return undefined;
}

/** Every `shell`/`inject`/`edge-anchored` claim in a package body, in the order the package lists them. */
function edgeAnchoredClaims(body: Record<string, unknown> | undefined): Record<string, unknown>[] {
  const claims = Array.isArray(body?.['capabilityClaims']) ? (body!['capabilityClaims'] as unknown[]) : [];
  const out: Record<string, unknown>[] = [];
  for (const claim of claims) {
    if (!claim || typeof claim !== 'object') continue;
    const c = claim as Record<string, unknown>;
    if (c['capability'] !== 'shell' || c['claim'] !== 'inject' || c['injectionPoint'] !== 'edge-anchored') continue;
    out.push(c);
  }
  return out;
}

/**
 * CAOS-688 — the assistant panel a package body's edge-anchored claim declares.
 *
 * `chat.route` is the one required part, because a panel that cannot take a message is not an
 * assistant. Every other part is dropped INDIVIDUALLY when it is malformed rather than voiding the
 * whole descriptor: a package that mistypes its jump target should lose the jump, not the panel. A
 * declaration with no usable `chat.route` reads as no declaration at all, which leaves the frame's
 * own chat-only panel exactly as it was.
 */
function declaredAssistantPanel(body: Record<string, unknown> | undefined): ShellAssistantDescriptor | undefined {
  for (const c of edgeAnchoredClaims(body)) {
    const raw = c['assistant'];
    if (!raw || typeof raw !== 'object') continue;
    const a = raw as Record<string, unknown>;
    const chat = a['chat'];
    const route = chat && typeof chat === 'object' ? (chat as Record<string, unknown>)['route'] : undefined;
    if (typeof route !== 'string' || route.trim() === '') continue;

    const prompts = Array.isArray(a['prompts'])
      ? (a['prompts'] as unknown[]).filter((p): p is string => typeof p === 'string' && p.trim() !== '')
      : [];

    const rawRecent = a['recent'];
    const recent =
      rawRecent && typeof rawRecent === 'object' &&
      typeof (rawRecent as Record<string, unknown>)['route'] === 'string' &&
      typeof (rawRecent as Record<string, unknown>)['label'] === 'string'
        ? {
            route: (rawRecent as Record<string, unknown>)['route'] as string,
            label: (rawRecent as Record<string, unknown>)['label'] as string,
          }
        : undefined;

    const rawJump = a['jump'];
    const jumpApp = rawJump && typeof rawJump === 'object' ? (rawJump as Record<string, unknown>)['app'] : undefined;
    const jumpLabel = rawJump && typeof rawJump === 'object' ? (rawJump as Record<string, unknown>)['label'] : undefined;
    const jumpTab = rawJump && typeof rawJump === 'object' ? (rawJump as Record<string, unknown>)['tab'] : undefined;
    const jump =
      typeof jumpApp === 'string' && jumpApp !== '' && typeof jumpLabel === 'string' && jumpLabel !== ''
        ? { app: jumpApp, label: jumpLabel, ...(typeof jumpTab === 'string' && jumpTab !== '' ? { tab: jumpTab } : {}) }
        : undefined;

    const subtitle = typeof a['subtitle'] === 'string' && a['subtitle'] !== '' ? (a['subtitle'] as string) : undefined;

    return {
      chat: { route },
      ...(subtitle ? { subtitle } : {}),
      ...(prompts.length > 0 ? { prompts } : {}),
      ...(recent ? { recent } : {}),
      ...(jump ? { jump } : {}),
    };
  }
  return undefined;
}

/**
 * CAOS-1861 — ONE list view's body, projected exactly as an installed one is.
 *
 * A person's own view arrives from `listViews/list` as a loose body rather than as part of the
 * installed component set, and it has to read the same way: the same two column forms, the same
 * `columnLabels`/`columnEditable` derivation, the same fail-soft dropping of a half-formed filter.
 *
 * SO IT GOES THROUGH {@link projectMetadata} ITSELF, on a synthetic one-component set, rather than
 * through a second projector written for this case. The whole-set function is ~200 lines of reading
 * decisions, several of them with a paragraph explaining which earlier narrowing they exist to
 * undo; a parallel copy would be a second place for all of that to be true, and the first time one
 * of them moved the two would disagree about what a person's own view shows. The cost is an
 * otherwise-empty `OrgMetadata` shell per call, which for one component is nothing — and the call
 * is made only when the address names a view the installed set does not hold.
 *
 * Returns `undefined` for a body the projection refuses (no `object`), which is a real answer: the
 * caller falls back to the object's own columns, exactly as it does for a view key naming nothing.
 */
export function projectOneListView(
  objectKey: string,
  key: string,
  body: Record<string, unknown>,
): ListViewMeta | undefined {
  // `object` is SET from the caller's own identity rather than read from the body, for the reason
  // the Engine gives for doing the same thing on the way in: "so a caller cannot store a view whose
  // body disagrees with the row that holds it — a disagreement that would surface much later as a
  // view that lists the wrong object". `saveUserListView` therefore stores it, and this does not
  // depend on that having always been true.
  const projected = projectMetadata([{ type: 'listView', key, body: { ...body, object: objectKey } }]);
  return (projected.listViews.get(objectKey) ?? []).find((v) => v.key === key);
}

export function projectMetadata(
  components: readonly RawComponent[],
  seamOrderOverrides: ReadonlyMap<string, readonly string[]> = new Map(),
): OrgMetadata {
  // Index tabs by key so an app's tab-key list resolves to labels + target objects.
  // CAOS-345 — saved list views, gathered per object so a tab can offer the ones for the object it
  // holds. Sorted by label at the end, so the menu reads the same way every time.
  const listViews = new Map<string, ListViewMeta[]>();
  for (const c of components) {
    if (c.type !== 'listView' || !c.key) continue;
    const object = str(c.body, 'object');
    if (!object) continue;
    // CAOS-349 — the conditions the view narrows by. An entry missing a field is dropped rather
    // than passed on: a half-formed condition would silently widen the view to every record.
    const rawFilters = Array.isArray(c.body?.['filters']) ? (c.body!['filters'] as unknown[]) : [];
    const filters: ListViewFilter[] = [];
    for (const raw of rawFilters) {
      const f = raw as { field?: unknown; op?: unknown; value?: unknown };
      if (typeof f?.field !== 'string' || f.field.length === 0) continue;
      // CAOS-1627 — `outcome` is CARRIED, not collapsed. It names a class of statuses the Engine
      // resolves against the field's value set, so rewriting it to `eq` does not narrow the view,
      // it asks a different question: an exact match on the literal string "open", which on a
      // normal value set matches nothing. That is the worst shape a defect here can take, because
      // the list still draws and an empty result reads as "no open work" rather than as a filter
      // the workspace declined to send. `eq` REMAINS the fallback for anything unrecognised: an op
      // this build does not know is a narrowing it cannot honour, and eq is the reading that keeps
      // the view narrow rather than widening it to every record. The VALUE is deliberately not
      // re-checked here — deploy already refuses an outcome that is not one of the three, and a
      // second copy of that list is exactly the duplicated classification CAOS-1601 removed.
      const op: ListViewFilter['op'] =
        f.op === 'contains' ? 'contains' : f.op === 'outcome' ? 'outcome' : 'eq';
      // CAOS-1624 — `appliesWhen` is carried only when it names a permission. A malformed one is
      // dropped rather than passed on, which leaves the filter UNCONDITIONAL: a half-read scoping
      // rule must fall back to the narrower reading, never to "applies to nobody".
      const when = (raw as { appliesWhen?: unknown }).appliesWhen as { lacksPermission?: unknown } | undefined;
      const lacks = when && typeof when === 'object' ? when.lacksPermission : undefined;
      filters.push({
        field: f.field,
        op,
        value: f.value,
        ...(typeof lacks === 'string' && lacks.length > 0 ? { appliesWhen: { lacksPermission: lacks } } : {}),
      });
    }
    const rawSort = c.body?.['sort'] as { field?: unknown; dir?: unknown } | undefined;
    const sortField = typeof rawSort?.field === 'string' ? bareFieldName(rawSort.field) : undefined;
    const sortDir = rawSort?.dir === 'desc' ? 'desc' : 'asc';
    // CAOS-467 — presentation/groupBy/search/cardFields. `presentation` is read only when it is
    // one of the five the kernel schema allows; anything else is treated as absent rather than
    // passed on, the same fail-closed reading `sort.dir` gets above.
    const rawPresentation = str(c.body, 'presentation');
    const presentation =
      rawPresentation != null && PRESENTATION_VALUES.has(rawPresentation)
        ? (rawPresentation as ListViewPresentation)
        : undefined;
    const rawGroupBy = str(c.body, 'groupBy');
    const groupBy = rawGroupBy != null ? bareFieldName(rawGroupBy) : undefined;
    const search = strList(c.body, 'search').map(bareFieldName);
    const cardFields = strList(c.body, 'cardFields').map(bareFieldName);
    // CAOS-1623 — derived columns. Projected fail-soft, the same way `filters` above is: an entry
    // without a key or a label, or naming a source this cannot project, is DROPPED rather than
    // passed on. A half-formed derived column would otherwise reach the table as a heading over a
    // column of nothing, which reads as data the records are missing rather than as metadata the
    // author got wrong. Note these keys are NOT run through `bareFieldName`: a derived column's key
    // names no field, so stripping a namespace off it would be corrupting an identifier rather than
    // normalising one.
    const rawDerived = Array.isArray(c.body?.['derivedColumns']) ? (c.body!['derivedColumns'] as unknown[]) : [];
    const derivedColumns: ListViewDerivedColumn[] = [];
    for (const raw of rawDerived) {
      const d = raw as {
        key?: unknown;
        label?: unknown;
        from?: unknown;
        prefix?: unknown;
        length?: unknown;
        transform?: unknown;
      };
      if (typeof d?.key !== 'string' || d.key.length === 0) continue;
      if (typeof d.label !== 'string' || d.label.length === 0) continue;
      // CAOS-1646 R2 — a column folds `cases` OR projects `from: 'id'`. Projected fail-soft like
      // everything else here: a cascade with no usable case, or one with no `otherwise`, is DROPPED
      // rather than passed on as a heading over a column of nothing.
      //
      // `at` is a FIELD api name and goes through `bareFieldName` for the reason the stored columns
      // do — a package's fields arrive namespaced and the records the host reads are keyed bare, so
      // a case testing `ns__status` would compare against a value that is never there and every row
      // would fall through to `otherwise`. That is the quiet failure this whole ticket is about,
      // and it is the one line of this projection where it could reappear.
      const rawCases = Array.isArray((d as { cases?: unknown }).cases)
        ? ((d as { cases: unknown[] }).cases)
        : undefined;
      if (rawCases !== undefined) {
        const otherwise = (d as { otherwise?: unknown }).otherwise;
        if (typeof otherwise !== 'string' || otherwise.length === 0) continue;
        const cases: { at: string; equals?: string; contains?: string; value: string }[] = [];
        for (const rc of rawCases) {
          const k = rc as { at?: unknown; equals?: unknown; contains?: unknown; value?: unknown };
          if (typeof k?.at !== 'string' || k.at.length === 0) continue;
          if (typeof k.value !== 'string' || k.value.length === 0) continue;
          const hasEquals = typeof k.equals === 'string';
          const hasContains = typeof k.contains === 'string' && k.contains.length > 0;
          // Exactly one question, matching the metatype. Neither ⇒ the case never matches and its
          // value is unreachable; both ⇒ two rules with no stated precedence.
          if (hasEquals === hasContains) continue;
          cases.push({
            at: bareFieldName(k.at),
            ...(hasEquals ? { equals: k.equals as string } : {}),
            ...(hasContains ? { contains: k.contains as string } : {}),
            value: k.value,
          });
        }
        if (cases.length === 0) continue;
        derivedColumns.push({
          key: d.key,
          label: d.label,
          cases,
          otherwise,
          ...(typeof (d as { at?: unknown }).at === 'number' &&
          Number.isInteger((d as { at: number }).at) &&
          (d as { at: number }).at >= 0
            ? { at: (d as { at: number }).at }
            : {}),
        });
        continue;
      }
      if (d.from !== 'id') continue;
      derivedColumns.push({
        key: d.key,
        label: d.label,
        from: 'id',
        ...(typeof d.prefix === 'string' && d.prefix.length > 0 ? { prefix: d.prefix } : {}),
        ...(typeof d.length === 'number' && Number.isInteger(d.length) && d.length > 0
          ? { length: d.length }
          : {}),
        ...(d.transform === 'upper' || d.transform === 'lower' ? { transform: d.transform } : {}),
        ...(typeof (d as { at?: unknown }).at === 'number' &&
        Number.isInteger((d as { at: number }).at) &&
        (d as { at: number }).at >= 0
          ? { at: (d as { at: number }).at }
          : {}),
      });
    }
    /*
     * CAOS-1646 L1 — the columns, in either shape, plus the headings any of them named.
     *
     * `strList` stood here and keeps only the STRING elements, so a `{field, label}` column would
     * have been dropped from the list entirely — the column would vanish from the table rather
     * than merely lose its heading. That is the sixth reader of this array to narrow in a way that
     * silently discards the new form, and the most damaging of them.
     */
    const columnNames: string[] = [];
    const columnLabels: Record<string, string> = {};
    /*
     * CAOS-1677 — the columns that asked to be edited here, read in the same pass as the headings.
     *
     * Only the object arm can carry it, so a string column can never land in this set: that is the
     * compatibility rule the metatype states for itself, and it is why an absent declaration has
     * to mean the same thing as a column authored before the word existed.
     *
     * `=== 'editable'` rather than a truth test, because `uiBehavior` is an enum of two and the
     * other member is `readonly`. Anything else — a typo, a boolean, a value from a newer schema
     * than this host — is not an instruction to offer a write path, and the fail-soft reading
     * every other field in this reader uses applies with more force to this one than to a heading.
     */
    const columnEditable = new Set<string>();
    for (const raw of Array.isArray(c.body?.['columns']) ? (c.body!['columns'] as unknown[]) : []) {
      if (typeof raw === 'string' && raw.length > 0) {
        columnNames.push(bareFieldName(raw));
        continue;
      }
      const col = raw as { field?: unknown; label?: unknown; uiBehavior?: unknown } | null;
      if (typeof col?.field !== 'string' || col.field.length === 0) continue;
      const name = bareFieldName(col.field);
      columnNames.push(name);
      if (typeof col.label === 'string' && col.label.length > 0) columnLabels[name] = col.label;
      if (col.uiBehavior === 'editable') columnEditable.add(name);
    }
    // CAOS-1646 L5 — the offered orderings, read through fail-soft like everything else here: an
    // entry missing either half is dropped, and a list left with none falls back to the generated
    // pair per field rather than offering an empty picker.
    const sortOptions: { value: string; label: string }[] = [];
    for (const raw of Array.isArray(c.body?.['sortOptions']) ? (c.body!['sortOptions'] as unknown[]) : []) {
      const o = raw as { value?: unknown; label?: unknown } | null;
      if (typeof o?.value !== 'string' || o.value.length === 0) continue;
      if (typeof o.label !== 'string' || o.label.length === 0) continue;
      sortOptions.push({ value: o.value, label: o.label });
    }
    // CAOS-1860 — the row buttons this view declares. See where it lands in the projection below
    // for why `'surface'` is the load-bearing argument.
    const rowActions = recordActionsFrom(c.body?.['rowActions'], 'surface');
    // CAOS-360 — an authored create control. Present-but-empty (`create: {}`) is a real answer:
    // "offer it, with the host's own wording". So the object's PRESENCE is what matters here, and
    // an absent `label` is not the same as an absent `create`.
    const rawCreate = c.body?.['create'];
    const create =
      rawCreate && typeof rawCreate === 'object' && !Array.isArray(rawCreate)
        ? (() => {
            const label = str(rawCreate as Record<string, unknown>, 'label');
            return label != null ? { label } : {};
          })()
        : undefined;
    const view: ListViewMeta = {
      key: c.key,
      object,
      label: str(c.body, 'label') ?? c.key,
      columns: columnNames,
      ...(Object.keys(columnLabels).length > 0 ? { columnLabels } : {}),
      ...(columnEditable.size > 0 ? { columnEditable: columnEditable as ReadonlySet<string> } : {}),
      ...(sortField ? { sort: { field: sortField, dir: sortDir as 'asc' | 'desc' } } : {}),
      ...(filters.length > 0 ? { filters } : {}),
      ...(presentation ? { presentation } : {}),
      ...(groupBy ? { groupBy } : {}),
      ...(search.length > 0 ? { search } : {}),
      ...(cardFields.length > 0 ? { cardFields } : {}),
      ...(derivedColumns.length > 0 ? { derivedColumns } : {}),
      ...(create ? { create } : {}),
      // CAOS-1646 L4/L5 — the authored wording and orderings. Each absent ⇒ the host keeps
      // generating its own, so nothing authored before this changes.
      ...(str(c.body, 'searchPlaceholder') != null ? { searchPlaceholder: str(c.body, 'searchPlaceholder')! } : {}),
      ...(str(c.body, 'emptyLabel') != null ? { emptyLabel: str(c.body, 'emptyLabel')! } : {}),
      ...(str(c.body, 'searchEmptyLabel') != null ? { searchEmptyLabel: str(c.body, 'searchEmptyLabel')! } : {}),
      ...(sortOptions.length > 0 ? { sortOptions } : {}),
      // CAOS-1860 — the row buttons, read through the SAME `recordActionsFrom` a related list's are
      // (`origin: 'surface'`), so the two cannot disagree about what a row action is.
      //
      // `'surface'` and not `'object'`, which is the load-bearing half of this line: that argument
      // is what withholds `replaces`, and the rule it enforces (CAOS-561) is that a surface places
      // actions but does not get to say what a standard verb MEANS. A list view is a surface. Read
      // as `'object'` it would let one of an object's several list views redefine Edit for the rows
      // it happens to draw, and the next list view over would be free to disagree.
      //
      // Empty is dropped rather than carried: an authored `rowActions` whose every entry failed to
      // read (no `id`, no `label`) must leave the list exactly as a list that declared none, or the
      // host draws an empty actions column over a declaration that said nothing readable.
      ...(rowActions.length > 0 ? { rowActions } : {}),
    };
    const bucket = listViews.get(object);
    if (bucket) bucket.push(view);
    else listViews.set(object, [view]);
  }
  for (const bucket of listViews.values()) bucket.sort((a, b) => a.label.localeCompare(b.label));

  // CAOS-388/CAOS-503 — a tab points at ONE of three things: a record type's list, a PAGE, or (as
  // of CAOS-503) an external address. All three halves of that choice are carried through now. The
  // page half was being dropped here, which is why a tab could be authored pointing at a page and
  // then arrive at the screen pointing at nothing — the same bug the url half would reproduce if it
  // were left out here too.
  const tabByKey = new Map<
    string,
    {
      label: string;
      object?: string;
      page?: string;
      // CAOS-1683 — a roster destination. Carried as its own field rather than folded into `page`
      // because the two are drawn by different renderers, and because the kernel refuses a tab whose
      // arm disagrees with the target's actual kind - so the arm IS the kind by the time it is
      // installed, and collapsing them here would throw that away and make the host guess again.
      rosterPage?: string;
      rosterDetailPage?: string;
      url?: string;
      openInNewTab?: boolean;
      icon?: string;
      defaultListView?: string;
      recordHome?: boolean;
    }
  >();
  for (const c of components) {
    if (c.type !== 'tab' || !c.key) continue;
    const target = c.body?.['target'] as {
      object?: string;
      page?: string;
      rosterPage?: string;
      rosterDetailPage?: string;
      url?: string;
    } | undefined;
    tabByKey.set(c.key, {
      // CAOS-93 — may be a translated map, like an object's or a field's label. Read with `str`
      // this fell through to the raw component KEY for a translated tab, so the navigation strip
      // would have shown `sales_pipeline` where the author had written a name in two languages.
      label: authoredStr(c.body, 'label') ?? c.key,
      object: typeof target?.object === 'string' ? target.object : undefined,
      page: typeof target?.page === 'string' ? target.page : undefined,
      rosterPage: typeof target?.rosterPage === 'string' ? target.rosterPage : undefined,
      rosterDetailPage: typeof target?.rosterDetailPage === 'string' ? target.rosterDetailPage : undefined,
      url: typeof target?.url === 'string' ? target.url : undefined,
      ...(c.body?.['openInNewTab'] === true ? { openInNewTab: true } : {}),
      icon: str(c.body, 'icon'),
      defaultListView: str(c.body, 'defaultListView'),
      // CAOS-1586 — whether this tab is where its object's records live. Carried so the address
      // for a record can be resolved from what was AUTHORED rather than from which app the reader
      // happens to be standing in.
      ...(c.body?.['recordHome'] === true ? { recordHome: true } : {}),
    });
  }

  // Group fields by their owning object (bare api name → FieldMeta).
  const fieldsByObject = new Map<string, Map<string, FieldMeta>>();
  for (const c of components) {
    if (c.type !== 'field' || !c.key) continue;
    const object = str(c.body, 'object');
    if (!object) continue;
    const reference = c.body?.['reference'] as { object?: string } | undefined;
    const apiName = bareFieldName(c.key);
    const field: FieldMeta = {
      key: c.key,
      apiName,
      label: authoredStr(c.body, 'label') ?? apiName, // CAOS-283 — may be a translated map
      description: str(c.body, 'description'),
      helpText: str(c.body, 'helpText'),
      storageType: str(c.body, 'storageType') ?? 'Text',
      required: c.body?.['required'] === true,
      format: str(c.body, 'format'),
      referenceTo: typeof reference?.object === 'string' ? reference.object : undefined,
      valueSet: str(c.body, 'valueSet'),
      formula: str(c.body, 'formula'),
    };
    (fieldsByObject.get(object) ?? fieldsByObject.set(object, new Map()).get(object)!).set(apiName, field);
  }

  // Index layout sections by the object they belong to (first layout per object wins), keeping the
  // authored layout's key so Setup → Page Layouts can name it truthfully.
  //
  // CAOS-166 — "first layout per object wins" decides the object's BASE layout and nothing more. It
  // used to decide which layouts were projected AT ALL: every later layout for the same object was
  // skipped before its sections were read, so an object with three per-type layouts projected one
  // and discarded two. A record type names a layout, so those discarded ones are exactly the ones a
  // typed record needs. Each layout is therefore now projected under its OWN key as well, and the
  // by-object maps below keep their previous meaning — the base layout, first by key — so a record
  // that resolves to no type renders byte-identically to before.
  const sectionsByObject = new Map<string, LayoutSection[]>();
  const layoutKeyByObject = new Map<string, string>();
  const relatedByObject = new Map<string, RelatedListMeta[]>();
  const sectionsByLayoutKey = new Map<string, LayoutSection[]>();
  const relatedByLayoutKey = new Map<string, RelatedListMeta[]>();
  const actionsByLayoutKey = new Map<string, RecordActionMeta[]>();
  const panelsByLayoutKey = new Map<string, RecordPanelMeta[]>();
  // CAOS-1726 — where this object's layout places the platform-drawn panels. Keyed the same way
  // and filled in the same loop, so a layout's three kinds of placement cannot come from
  // different layouts.
  const panelsByObject = new Map<string, RecordPanelMeta[]>();
  const actionsByObject = new Map<string, RecordActionMeta[]>();
  // CAOS-559 — the actions each OBJECT defines, indexed before the layouts that place them are read.
  // This is the definition side of the split fields already have: authored once on the object, named
  // by key from every surface that draws it, so a label or a condition is changed in one place.
  const definedActionsByObject = new Map<string, RecordActionMeta[]>();
  for (const c of components) {
    if (c.type !== 'object' || !c.key) continue;
    const defined = recordActionsFrom(c.body?.['actions'], 'object');
    if (defined.length > 0) definedActionsByObject.set(c.key, defined);
  }
  for (const c of components) {
    if (c.type !== 'layout') continue;
    const object = str(c.body, 'object');
    if (!object) continue;
    const rawSections = c.body?.['sections'];
    if (!Array.isArray(rawSections)) continue;
    // CAOS-166 — whether THIS layout is the object's base one. Read ONCE, here, before anything is
    // written: the by-object maps are filled at five separate points below and the first write would
    // otherwise flip the answer mid-layout, so a layout could contribute its sections as the base
    // and its panels as a non-base. Computed after the `rawSections` guard so that a layout with no
    // section array still declines to be the base, exactly as it did when it was skipped outright.
    const isBase = !sectionsByObject.has(object);
    if (isBase && c.key) layoutKeyByObject.set(object, c.key);
    // CAOS-361 — the attached sets this layout names. A set missing either half of its wiring (the
    // child object, or the child field that points back here) is DROPPED rather than half-rendered:
    // a titled table that cannot say what it is filtered by would be a table of the wrong records.
    const related: RelatedListMeta[] = [];
    for (const r of Array.isArray(c.body?.['relatedLists']) ? (c.body!['relatedLists'] as unknown[]) : []) {
      if (r == null || typeof r !== 'object') continue;
      const rec = r as Record<string, unknown>;
      const childObject = str(rec, 'object');
      const relField = str(rec, 'relationshipField');
      const id = str(rec, 'id');
      if (!childObject || !relField || !id) continue;
      const columns = strList(rec, 'columns').map(bareFieldName);
      // CAOS-407 — through `recordActionsFrom`, which is the point: a row button and a record button
      // are the same declaration, so they are read by the same function and an action that cannot say
      // what pressing it means is dropped here by the same rule, not by a second one written beside it.
      // CAOS-561 - 'surface', because a related list PLACES actions and does not get to say what a
      // standard verb means. `replaces` is read from an object definition and nowhere else, so two
      // surfaces over one object cannot disagree about what Edit does there.
      const rowActions = recordActionsFrom(rec['rowActions'], 'surface');
      const selectionActions = recordActionsFrom(rec['selectionActions'], 'surface').filter(
        (a): a is Extract<RecordActionMeta, { readonly kind: 'update' }> => a.kind === 'update',
      );
      const listRegion = str(rec, 'region');
      related.push({
        id,
        object: childObject,
        relationshipField: bareFieldName(relField),
        label: str(rec, 'label'),
        ...(listRegion ? { region: listRegion } : {}),
        ...(columns.length ? { columns } : {}),
        ...(rowActions.length ? { rowActions } : {}),
        ...(selectionActions.length ? { selectionActions } : {}),
      });
    }
    if (isBase) relatedByObject.set(object, related);
    if (c.key) relatedByLayoutKey.set(c.key, related);
    // CAOS-559 — where this surface's buttons come from. A surface that PLACES actions names the
    // keys of actions the OBJECT defines; a surface that DEFINES them inline is the pre-CAOS-559
    // shape and still works unchanged. Saying nothing is not declining: the object's own actions
    // stand, which is what "the object defines what can be done with it" has to mean for a layout
    // that was authored before actions moved.
    const placements = c.body?.['actionKeys'];
    const actions: RecordActionMeta[] =
      placements !== undefined
        ? placeActions(definedActionsByObject.get(object) ?? [], strList(c.body, 'actionKeys'))
        : c.body?.['actions'] !== undefined
          ? recordActionsFrom(c.body?.['actions'], 'surface')
          : (definedActionsByObject.get(object) ?? []);
    if (isBase) actionsByObject.set(object, actions);
    if (c.key) actionsByLayoutKey.set(c.key, actions);
    const sections: LayoutSection[] = [];
    for (const s of rawSections) {
      if (s == null || typeof s !== 'object') continue;
      const sec = s as Record<string, unknown>;
      const sectionRegion = str(sec, 'region');
      sections.push({
        label: typeof sec['label'] === 'string' ? sec['label'] : '',
        // CAOS-1884 — `'auto'` passes through as itself. Everything else keeps the behaviour it
        // had: a positive number is taken, and anything else falls to 2, which is this
        // projection's long-standing defence against a malformed section rather than a default
        // anybody authored.
        columns:
          sec['columns'] === 'auto'
            ? 'auto'
            : typeof sec['columns'] === 'number' && sec['columns'] > 0
              ? sec['columns']
              : 2,
        fields: layoutSectionFields(sec['fields']),
        // CAOS-1726 — spread so the key is ABSENT when the section named no region, never
        // present-and-undefined: a projection carrying `region: undefined` reads, to anything that
        // serializes or compares it, as a different shape from one that never had a region.
        ...(sectionRegion ? { region: sectionRegion } : {}),
      });
    }
    if (c.key) sectionsByLayoutKey.set(c.key, sections);
    // Written LAST of the by-object five, because this is the map `isBase` is read from: setting it
    // is what claims the object's base slot for this layout.
    if (isBase) sectionsByObject.set(object, sections);
    // CAOS-1726 — the panel placements this layout declares. Read only when the key is PRESENT:
    // a layout that never mentioned `panels` leaves the map untouched, so `panels` stays absent on
    // the projection and the renderer keeps today's tabbed behaviour. A layout that declares an
    // empty list has said "place none", which is a different statement and is recorded as one.
    const rawPanels = c.body?.['panels'];
    if (Array.isArray(rawPanels)) {
      const panels: RecordPanelMeta[] = [];
      for (const raw of rawPanels) {
        if (raw == null || typeof raw !== 'object') continue;
        const rec = raw as Record<string, unknown>;
        const panel = str(rec, 'panel');
        // Only the two the platform actually draws. The metatype's enum refuses anything else at
        // the write boundary; this is the read side declining to carry a panel it cannot draw,
        // rather than trusting that the boundary was the only way the body could have arrived.
        if (panel !== 'activity' && panel !== 'history') continue;
        const panelRegion = str(rec, 'region');
        panels.push({ panel, ...(panelRegion ? { region: panelRegion } : {}) });
      }
      if (isBase) panelsByObject.set(object, panels);
      if (c.key) panelsByLayoutKey.set(c.key, panels);
    }
  }

  // CAOS-166 — each object's RECORD TYPES, each joined to the layout it names.
  //
  // The reference runs type → layout, never layout → type: a layout says which OBJECT it belongs to
  // and nothing about a type, so this join is the only way the per-type layouts indexed above become
  // reachable from an object. That direction is also why the base layout cannot simply be "the one
  // no type claims" — nothing stops two types naming the same layout, or none naming the first one.
  //
  // A type whose `layout` names something that was never projected — absent, or present but
  // carrying no section array — is projected with NO layout rather than an empty one. "This type has
  // no surface of its own, draw the base" and "this type's surface is blank" are different claims
  // and only the first is true; projecting an empty section list would render a record with no
  // fields on it, which is strictly worse than the base layout the type was happy to inherit.
  const recordTypesByObject = new Map<string, RecordTypeMeta[]>();
  for (const c of components) {
    if (c.type !== 'recordType' || !c.key) continue;
    const object = str(c.body, 'object');
    if (!object) continue;
    const layoutKey = str(c.body, 'layout');
    const typeSections = layoutKey ? sectionsByLayoutKey.get(layoutKey) : undefined;
    const label = str(c.body, 'label');
    const selector = str(c.body, 'selector');
    const projected: RecordTypeMeta = {
      key: c.key,
      ...(label ? { label } : {}),
      ...(selector ? { selector } : {}),
      ...(layoutKey && typeSections
        ? {
            layout: {
              layoutKey,
              sections: typeSections,
              relatedLists: relatedByLayoutKey.get(layoutKey) ?? [],
              // The same three-way fallback the base layout uses, for the same reason (CAOS-559):
              // a layout that says nothing about actions has not declined them, so the object's own
              // actions stand.
              actions: actionsByLayoutKey.get(layoutKey) ?? definedActionsByObject.get(object) ?? [],
              ...(panelsByLayoutKey.has(layoutKey) ? { panels: panelsByLayoutKey.get(layoutKey)! } : {}),
            },
          }
        : {}),
    };
    (recordTypesByObject.get(object) ?? recordTypesByObject.set(object, []).get(object)!).push(projected);
  }

  // CAOS-405 — each object's highlights band, from the record page bound to it. Only a record page
  // counts (a content, intake or control-plane page is about no record), and the first by key wins.
  const recordPageByObject = new Map<string, { key: string; body: Record<string, unknown> | undefined }>();
  for (const c of components) {
    if (c.type !== 'page' || !c.key) continue;
    const object = str(c.body, 'object');
    if (!object || pageKindOf(c.body) !== 'object') continue;
    const held = recordPageByObject.get(object);
    if (!held || c.key < held.key) recordPageByObject.set(object, { key: c.key, body: c.body });
  }
  const compactLayoutByObject = new Map<string, { pageKey: string; fields: string[] }>();
  for (const [object, page] of recordPageByObject) {
    const fields = strList(page.body, 'compactLayout').map(bareFieldName);
    if (fields.length > 0) compactLayoutByObject.set(object, { pageKey: page.key, fields });
  }
  // CAOS-369 — the header's status badge, read off the SAME record page the band comes from, so the
  // two cannot be attributed to different pages. An empty or absent key is no badge, not a badge on
  // some field chosen for it.
  const statusFieldByObject = new Map<string, { pageKey: string; field: string }>();
  for (const [object, page] of recordPageByObject) {
    const named = str(page.body, 'statusField');
    if (named) statusFieldByObject.set(object, { pageKey: page.key, field: bareFieldName(named) });
  }
  // CAOS-1882 — the header's second line, read off the SAME record page as the band and the badge,
  // for the reason recorded on those two. An empty or absent key is no line, never a line over
  // some field chosen for it — which is the defect CAOS-369 removed from the badge, and it is not
  // worth reintroducing one surface over.
  const subtitleFieldByObject = new Map<string, { pageKey: string; field: string }>();
  for (const [object, page] of recordPageByObject) {
    const named = str(page.body, 'subtitleField');
    if (named) subtitleFieldByObject.set(object, { pageKey: page.key, field: bareFieldName(named) });
  }
  // CAOS-1726 — the blueprint a layout's regions are read against, off the SAME index the band and
  // the badge come from. Reusing `recordPageByObject` rather than re-deriving it is the whole point:
  // the deploy gate resolves the layout's template by this same first-by-key rule, so the gate and
  // the renderer cannot judge against different blueprints.
  const recordTemplateByObject = new Map<string, { pageKey: string; template: string }>();
  for (const [object, page] of recordPageByObject) {
    const named = str(page.body, 'template');
    if (named) recordTemplateByObject.set(object, { pageKey: page.key, template: named });
  }

  // CAOS-320 — index intake FORMS by their page key so a package can be joined to the form it roots.
  const intakeByPage = new Map<string, ShellIntakeDescriptor>();
  for (const c of components) {
    if (c.type !== 'page' || !c.key) continue;
    const intake = intakeOfPage(c.body);
    if (intake) intakeByPage.set(c.key, intake);
  }

  const guidanceBySource = projectGuidance(components);

  const labels = new Map<string, string>();
  const intakeBySource = new Map<string, ShellIntakeDescriptor>();
  const assistantBySource = new Map<string, ShellAssistantDescriptor>();
  const anchorKindBySource = new Map<string, EdgeAnchorKind>();
  const installedPackageKeys = new Set<string>();
  let apps: AppMeta[] = [];
  const objects = new Map<string, ObjectMeta>();
  const valueSets = new Map<string, ReadonlyMap<string, ValueOption>>();
  const valueSetPresentations = new Map<string, ValueSetPresentation>();

  for (const c of components) {
    if (!c.key) continue;

    if (c.type === 'valueSet') {
      valueSets.set(c.key, projectValueSet(c.body));
      // CAOS-640 — only a presentation the kernel closes over is recorded. Anything else is left
      // unsaid rather than passed on, so an org cannot author its way to a render nothing draws.
      if (str(c.body, 'presentation') === 'cards') valueSetPresentations.set(c.key, 'cards');
      continue;
    }

    // Any app or package can be an injection source; record its display label for the host.
    //
    // CAOS-93 — the two kinds are read DIFFERENTLY now, which is why this is no longer one call.
    // `AppSchema.label` is `LocalizableText`, so `authoredStr` is correct for it; `PackageSchema
    // .label` is still `z.string().min(1).optional()`, so reading a package's with `authoredStr`
    // would make the host honour a `{ default, … }` shape the deploy gate refuses — the same
    // mistake `nameTemplate` records below, in reverse. Splitting them keeps each read matched to
    // what its metatype actually allows, so neither can drift when the other is widened.
    if (c.type === 'app') {
      const label = authoredStr(c.body, 'label');
      if (label) labels.set(c.key, label);
    } else if (c.type === 'package') {
      const label = str(c.body, 'label');
      if (label) labels.set(c.key, label);
    }

    // CAOS-287 — every installed package, label or no label. Recorded SEPARATELY from `labels`
    // above rather than read back out of it, because that map only holds a package that declared a
    // `label`: an unlabelled package is still installed, and reading installed-ness off a display
    // concern would offer to install it again.
    if (c.type === 'package') installedPackageKeys.add(c.key);

    if (c.type === 'object') {
      const fields = fieldsByObject.get(c.key) ?? new Map<string, FieldMeta>();
      // CAOS-365 — every object carries the system fields, unless it authored something under one
      // of those names itself (in which case the authored one wins: it is the object's own).
      for (const sys of SYSTEM_FIELDS) if (!fields.has(sys.apiName)) fields.set(sys.apiName, sys);
      const label = authoredStr(c.body, 'label') ?? c.key; // CAOS-283 — may be a translated map
      const listColumns = strList(c.body, 'listColumns').map(bareFieldName);
      const authoredSections = sectionsByObject.get(c.key);
      const layoutIsDefault = authoredSections === undefined;
      // CAOS-365 — the fallback layout is the object's OWN fields under Details, then the system
      // fields under their own heading. Every record can say when it was made, and CAOS-1704 whose it
      // is, without anybody having to author a layout for it, and they never sit mixed in among the
      // fields people fill in.
      // CAOS-360 — the fallback is the ONE place a field is offered for editing without anybody
      // having authored it, and deliberately: "absent means not offered" is a rule about an
      // authored surface declining to offer a field, and here no surface was authored at all.
      // This list IS the platform's own answer to what the record looks like, and that answer has
      // always let people change what they are allowed to change. The system fields stay unoffered
      // — they are the platform's bookkeeping, not somebody's data.
      const sections: LayoutSection[] =
        authoredSections ?? [
          {
            label: 'Details',
            columns: 2,
            // CAOS-1881 — nothing here is full width. The platform's fallback has no author to
            // have asked for it, and guessing from a field's storage type would make the
            // unauthored record disagree with the authored one about the same field.
            fields: [...fields.keys()]
              .filter((k) => !SYSTEM_FIELD_NAMES.has(k))
              .map((name) => ({ name, offeredForEdit: true, fullWidth: false })),
          },
          {
            label: SYSTEM_SECTION_LABEL,
            columns: 2,
            fields: SYSTEM_FIELDS.map((f) => ({ name: f.apiName, offeredForEdit: false, fullWidth: false })),
          },
        ];
      objects.set(c.key, {
        key: c.key,
        label,
        // CAOS-283 — translated separately from the singular, because a plural does not follow
        // from a translated singular in most languages. Falling back to `label` when absent is the
        // incumbent behaviour and stays.
        pluralLabel: authoredStr(c.body, 'pluralLabel') ?? label,
        tabCode: str(c.body, 'tabCode'),
        nameField: bareFieldName(str(c.body, 'nameField') ?? 'name'),
        // CAOS-1585 — read with `str` rather than `authoredStr`: the metatype declares a plain
        // string, so the `{ default: … }` translatable shape `authoredStr` also accepts is a shape
        // the deploy gate refuses, and reading it here would make the host honour something an org
        // cannot actually author. The template's own translation is carried by `{label}`.
        nameTemplate: str(c.body, 'nameTemplate'),
        fields,
        listColumns,
        sections,
        layoutKey: layoutKeyByObject.get(c.key),
        layoutIsDefault,
        relatedLists: relatedByObject.get(c.key) ?? [],
        // CAOS-559 — the layout's answer when there is one (it may place a subset, in its own
        // order), otherwise the actions the object defines. The fallback is what lets an object
        // carry actions before anybody authors a layout for it, exactly as its fields do.
        actions: actionsByObject.get(c.key) ?? definedActionsByObject.get(c.key) ?? [],
        ...(compactLayoutByObject.has(c.key) ? { compactLayout: compactLayoutByObject.get(c.key)! } : {}),
        ...(statusFieldByObject.has(c.key) ? { statusField: statusFieldByObject.get(c.key)! } : {}),
        // CAOS-1882 — spread, like the two above it, so the key stays ABSENT on an object whose
        // record page named none rather than present-and-undefined.
        ...(subtitleFieldByObject.has(c.key) ? { subtitleField: subtitleFieldByObject.get(c.key)! } : {}),
        ...(panelsByObject.has(c.key) ? { panels: panelsByObject.get(c.key)! } : {}),
        // CAOS-166 — the discriminator and the types, spread so both stay ABSENT on an object that
        // is not record-type-driven rather than present-and-undefined. `bareFieldName` for the same
        // reason every other field reference here uses it: the metatype permits a qualified name and
        // the record's own values are keyed bare, so an unnormalised discriminator would read every
        // record's type as null and silently draw the base layout for all of them.
        ...(str(c.body, 'recordTypeField')
          ? { recordTypeField: bareFieldName(str(c.body, 'recordTypeField')!) }
          : {}),
        ...(recordTypesByObject.has(c.key) ? { recordTypes: recordTypesByObject.get(c.key)! } : {}),
        ...(recordTemplateByObject.has(c.key)
          ? { recordTemplate: recordTemplateByObject.get(c.key)! }
          : {}),
      });
      continue;
    }

    // CAOS-320 — a package that roots an intake page contributes that form; key it by the package key
    // (the source key an edge-anchored injection carries), so the orb can open the real form.
    if (c.type === 'package') {
      // CAOS-151 — and the panel its own edge tab declares, which the boot plan does not carry.
      const kind = declaredEdgeKind(c.body);
      if (kind) anchorKindBySource.set(c.key, kind);
      // CAOS-688 — and what an assistant panel it declares is made of, read from the same claim.
      const assistant = declaredAssistantPanel(c.body);
      if (assistant) assistantBySource.set(c.key, assistant);
      const roots = Array.isArray(c.body?.['roots']) ? (c.body!['roots'] as unknown[]) : [];
      for (const r of roots) {
        if (typeof r !== 'string' || !r.startsWith('page:')) continue;
        const intake = intakeByPage.get(r.slice('page:'.length));
        if (intake) {
          intakeBySource.set(c.key, intake);
          break;
        }
      }
    }
    if (c.type !== 'app') continue;

    const tabKeys = Array.isArray(c.body?.['tabs']) ? (c.body!['tabs'] as unknown[]) : [];
    const tabs: ShellNavTab[] = tabKeys
      .filter((k): k is string => typeof k === 'string')
      .map((k) => {
        const t = tabByKey.get(k);
        // CAOS-345 — an object tab carries the saved views for the object it holds, so the strip's
        // per-tab menu has something real to offer. A tab holding no object carries none.
        const views = t?.object ? listViews.get(t.object) ?? [] : [];
        return {
          key: k,
          label: t?.label ?? k,
          object: t?.object,
          // CAOS-388 — a tab that opens a page carries which page, so the route can reach it.
          ...(t?.page ? { page: t.page } : {}),
          // CAOS-1683 — the same for a ROSTER destination, and this line is the one that decides
          // whether the feature exists at all: the kernel can admit the arm and the renderer can draw
          // it, and if the key is not carried HERE the route still falls through to 'no content'.
          ...(t?.rosterPage ? { rosterPage: t.rosterPage } : {}),
          ...(t?.rosterDetailPage ? { rosterDetailPage: t.rosterDetailPage } : {}),
          // CAOS-503 — a tab that opens an external address carries it, so the frame can open it
          // in a new browser tab instead of trying to resolve it as an object or a page.
          ...(t?.url ? { url: t.url } : {}),
          ...(t?.openInNewTab ? { openInNewTab: true } : {}),
          icon: t?.icon,
          defaultListView: t?.defaultListView,
          // CAOS-1586 — only on a tab that claimed it, so a tab that did not is shaped exactly as
          // it always was.
          ...(t?.recordHome ? { recordHome: true } : {}),
          ...(views.length > 0
            ? { listViews: views.map((v) => ({ key: v.key, label: v.label })) }
            : {}),
        };
      });

    // CAOS-371 — Setup opens on its own Home. Submitted Feedback is NOT a tab: it is a section of
    // the Setup console, reached from the Platform group in the left rail like every other section.
    // It had both, which read as two different places with the same name.
    if (c.key === 'setup') tabs.push(SETUP_HOME_TAB);
    // CAOS-372 — the My Profile app's sections live in its own console rail, so the nav strip carries
    // just the one Home entry. An app with no destination at all reads in the switcher as an app with
    // nothing in it.
    if (c.key === 'my_profile') tabs.push(MY_PROFILE_HOME_TAB);

    apps.push({
      key: c.key,
      // CAOS-93 — the SECOND place an app's label is read in this file, and the one a reader
      // actually sees: `labels` above feeds injection sources, this feeds the app switcher and the
      // workspace header. Both have to use `authoredStr` or they disagree about the same property,
      // and this one degrades worse — it falls back to the raw component key, so a translated app
      // was drawn as `sales_pipeline`. A host test caught this after the `labels` read was already
      // fixed, which is why the test asserts the `apps` entry and the `labels` map separately.
      label: authoredStr(c.body, 'label') ?? c.key,
      icon: str(c.body, 'icon'),
      tabs,
      landingTab: str(c.body, 'landingTab'),
      settingsPage: str(c.body, 'settingsPage'),
      navStyle: str(c.body, 'navStyle'),
      overflowAfter: posInt(c.body, 'overflowAfter'),
      description: str(c.body, 'description'),
      showInAppSwitcher: bool(c.body, 'showInAppSwitcher'),
      sortOrder: int(c.body, 'sortOrder'),
    });
  }

  // CAOS-372, migrated under CAOS-465 — the switcher's order is the apps' own: a declared
  // `sortOrder`, lower first, defaulting to 0 for an app that declares none (the switcher's OWN
  // convention — an undeclared app sits at the neutral middle, not last; My Profile/Setup opt
  // into the bottom with a large positive number, and CAOS-372's own test proves a NEGATIVE number
  // opts into the front). This used to be a plain stable `.sort` — a stable sort means every app
  // tied at that default 0 kept the position it happened to arrive in, which is install order in
  // every case that matters, the exact bug CAOS-465 exists to close. It now calls
  // `orderSeamContributions` (`./seam-order.js`) for the comparator + tie-break + override
  // mechanism — the app switcher is a fourth seam more than one package contributes into (one app
  // per package), found during the CAOS-465 sweep alongside the edge gutter, Setup's contributed
  // nav, and header actions — while resolving `sortOrder`'s undeclared-default to 0 HERE, in the
  // `keyOf` projector, rather than inside the shared function: the default a seam falls back to
  // when nothing is declared is that seam's own calibration (edge/setup/header fall back to
  // "last"; apps fall back to "the untouched middle"), not a second competing ordering rule — the
  // sort-by-number, tie-break-by-sourceKey, override-wins mechanism underneath is identical either
  // way.
  //
  // CAOS-736 — the app switcher's own pin, `seamOrderOverrides.get(APP_SWITCHER_SEAM_ID)`. Apps
  // aren't `capability_injection` rows, so nothing upstream (`CapabilityRegistry.injectionsFor`)
  // ever applies this pin the way it does for a kernel-mastered seam — this call site is
  // the only place it takes effect. A pinned key naming an app no longer installed is silently
  // skipped by `orderSeamContributions` itself (never thrown), the same fail-soft posture every
  // other seam's stale pin already gets.
  const appSwitcherPin = seamOrderOverrides.get(APP_SWITCHER_SEAM_ID);
  const appSwitcherOverride = appSwitcherPin && appSwitcherPin.length > 0 ? { sourceKeys: appSwitcherPin } : undefined;
  apps = orderSeamContributions(apps, (a) => ({ sourceKey: a.key, order: a.sortOrder ?? 0 }), appSwitcherOverride);

  // CAOS-387 — the installed components.
  //
  // A row missing the things that make it usable — the tag it is placed as, or what it is for —
  // is KEPT and shown saying exactly what is missing. Dropping it was worse than half-showing it:
  // the piece is installed either way, so a page placing it then reported "not installed here,
  // install the package that provides it", which is a confident instruction to go and fix
  // something that is not broken. The manifest is broken; the install is fine. Say that.
  const componentList: ComponentMeta[] = [];
  for (const c of components) {
    if (c.type !== 'component' || !c.key) continue;
    const problems: string[] = [];
    const tag = str(c.body, 'tag');
    const purpose = str(c.body, 'purpose');
    if (!tag) problems.push('it does not say what element a page places it as');
    if (!purpose) problems.push('it does not say what the piece is for');
    // Absent stays absent. A manifest that has not declared its inputs is not a manifest declaring
    // none, and flattening the two would put a claim on the screen that nobody made.
    const rawInputs = Array.isArray(c.body?.['inputs']) ? (c.body!['inputs'] as unknown[]) : undefined;
    const inputs: ComponentInputMeta[] = [];
    const untypedInputs: string[] = [];
    for (const raw of rawInputs ?? []) {
      const i = raw as {
        name?: unknown;
        label?: unknown;
        type?: unknown;
        required?: unknown;
        help?: unknown;
        deprecated?: unknown;
        source?: unknown;
        propertyOnly?: unknown;
      };
      if (typeof i?.name !== 'string' || i.name.length === 0) continue;
      // A missing LABEL falls back to the name, which restates something the manifest did say.
      // A missing TYPE does not fall back to anything. "text" would be a contract claim the
      // component never made — the one an administrator would place against and be wrong — in a
      // projection that refuses to invent a purpose, refuses to invent an empty input list, and
      // reports a nameless input rather than dropping it. It keeps the silence and says so.
      const declaredType = typeof i.type === 'string' && i.type.length > 0 ? i.type : undefined;
      if (declaredType === undefined) untypedInputs.push(i.name);
      inputs.push({
        name: i.name,
        label: typeof i.label === 'string' ? i.label : i.name,
        ...(declaredType !== undefined ? { type: declaredType } : {}),
        required: i.required === true,
        ...(typeof i.help === 'string' && i.help.length > 0 ? { help: i.help } : {}),
        // CAOS-461 — an individual input can be deprecated independently of the whole piece.
        ...(i.deprecated === true ? { deprecated: true } : {}),
        // CAOS-798 — kept rather than dropped, so a renderer can resolve a declared source.
        ...(typeof i.source === 'string' && i.source.length > 0 ? { source: i.source } : {}),
        // CAOS-159 — kept for the same reason, and it was dropped here for the same reason too.
        ...(i.propertyOnly === true ? { propertyOnly: true } : {}),
      });
    }
    if (untypedInputs.length > 0) {
      problems.push(
        untypedInputs.length === 1
          ? `the setting “${untypedInputs[0]}” does not say what kind of value it takes`
          : `${untypedInputs.length} of the settings it declares do not say what kind of value they take`,
      );
    }
    // A declared input list some of whose entries are unreadable is NOT a shorter list — it is a
    // list nobody can trust. Reporting the survivors as the contract would understate it, and
    // reporting an all-unreadable list as "no settings" would be the exact claim this projection
    // goes out of its way never to make.
    const droppedInputs = (rawInputs?.length ?? 0) - inputs.length;
    if (droppedInputs > 0) {
      problems.push(
        droppedInputs === 1
          ? 'one of the settings it declares has no name, so a page could not store a value for it'
          : `${droppedInputs} of the settings it declares have no name, so a page could not store values for them`,
      );
    }
    componentList.push({
      key: c.key,
      label: str(c.body, 'label') ?? c.key,
      // A piece that never said what it is for still has to read as something on the screen, and
      // the honest something is the fault itself rather than a blank line or an invented summary.
      purpose: purpose ?? 'This piece has not said what it is for.',
      group: str(c.body, 'group') ?? 'Ungrouped',
      tag: tag ?? '',
      // `exposed` is a VETO, so only `false` withholds placement; absent means no veto. The
      // shipped library writes it on EVERY manifest (and writes `false` on every one of them
      // until CAOS-562 declares their inputs), so the absent case below is not exercised by any
      // shipped data today — it is here for a manifest from anywhere else.
      exposed: bool(c.body, 'exposed') !== false,
      ...(Array.isArray(c.body?.['placements']) ? { placements: strList(c.body, 'placements') } : {}),
      ...(rawInputs ? { inputs } : {}),
      ...(problems.length > 0 ? { problems } : {}),
      // CAOS-461 — still working, still resolving; the library marks it so authors see the notice.
      ...(bool(c.body, 'deprecated') === true ? { deprecated: true } : {}),
      // CAOS-577 — where the code lives, carried through so the page renderer can fetch it.
      ...(() => {
        const module = str(c.body?.['bundle'] as Record<string, unknown> | undefined, 'module');
        return module ? { bundle: { module } } : {};
      })(),
    });
  }

  // CAOS-388 — the pages this org holds, read so they can be drawn.
  const pages = new Map<string, PageMeta>();
  for (const c of components) {
    if (c.type !== 'page' || !c.key) continue;
    pages.set(c.key, projectPage(c.key, c.body));
  }

  // CAOS-388 / CAOS-393 — the blueprints this org holds, so a page's sections can be drawn in the
  // order its template declares rather than in whatever order they were authored.
  const pageTemplates = new Map<string, PageTemplateMeta>();
  for (const c of components) {
    if (c.type !== 'pageTemplate' || !c.key) continue;
    const rawRegions = c.body?.['regions'];
    const regions = Array.isArray(rawRegions)
      ? rawRegions
          .map((r) => (r != null && typeof r === 'object' ? (r as Record<string, unknown>) : null))
          .filter((r): r is Record<string, unknown> => r !== null)
          .map((r) => ({ key: str(r, 'key') ?? '', label: str(r, 'label') ?? str(r, 'key') ?? '' }))
          .filter((r) => r.key !== '')
      : [];
    // Read only when every part is present and the right shape. A half-read geometry would lay
    // regions out against a matrix that does not match the region set, which draws worse than not
    // laying them out at all — and unlike a missing geometry, it would look deliberate.
    const rawGeometry = c.body?.['geometry'];
    const g = rawGeometry != null && typeof rawGeometry === 'object' ? (rawGeometry as Record<string, unknown>) : null;
    // ALL OR NOTHING, per array. Filtering out a malformed entry and keeping the rest would project a
    // matrix nobody authored — `['main sidebar', null, 'footer footer']` would become a two-row grid
    // that still passed every check below because something survived. One bad row rejects the whole
    // geometry, and the template stacks in declared order instead: plainer, but recognisably so.
    const strings = (value: unknown): readonly string[] | undefined =>
      Array.isArray(value) && value.every((a) => typeof a === 'string' && a !== '')
        ? (value as string[])
        : undefined;
    const areas = strings(g?.['areas']) ?? [];
    const stack = strings(g?.['stack']) ?? [];
    const cols = g ? str(g, 'cols') : undefined;
    const geometry =
      areas.length > 0 && stack.length > 0 && cols !== undefined && cols !== ''
        ? { areas, cols, stack }
        : undefined;
    pageTemplates.set(c.key, {
      key: c.key,
      label: str(c.body, 'label') ?? c.key,
      regions,
      ...(geometry ? { geometry } : {}),
    });
  }

  // CAOS-1567 — which record page opens under a roster, keyed by the roster's own address.
  //
  // An addressed `rosterDetailPage` already says which list it belongs to: `breadcrumbRootHref` is
  // the href its breadcrumb walks back to, and the schema REQUIRES it whenever `idParam` is set
  // (kernel `rosterDetailPageConstraint`). So a page that can be opened at an address necessarily
  // declares the list it hangs under — this is a guarantee of the metatype, not a naming habit, and
  // it is what lets a contributed roster's rows open with no host code naming either component.
  //
  // Keyed to a LIST of keys rather than one, because nothing stops two pages naming the same root.
  // Two would make "the record page for this list" a question with no answer, and the caller refuses
  // rather than picking whichever was authored first.
  //
  // A SINGLETON detail page (CAOS-557 — no `idParam`, no breadcrumb) is not here, and cannot be: it
  // has no addressed record and no list to return to.
  const rosterDetailPagesByListHref = new Map<string, string[]>();
  for (const c of components) {
    if (c.type !== 'rosterDetailPage' || !c.key) continue;
    const root = str(c.body, 'breadcrumbRootHref');
    if (!root || !str(c.body, 'idParam')) continue;
    const already = rosterDetailPagesByListHref.get(root);
    if (already) already.push(c.key);
    else rosterDetailPagesByListHref.set(root, [c.key]);
  }

  // CAOS-582 — every package's `setup` capability INJECT claim (group + page), read directly off
  // `package.capabilityClaims` — the exact field `validate-capabilities.ts` reads deploy-side —
  // never a Setup-specific or app-specific shape. This is what lets Setup build its nav from what
  // was actually contributed instead of a fixed list.
  const setupContributions: SetupContributionMeta[] = [];
  for (const c of components) {
    if (c.type !== 'package' || !c.key) continue;
    const claims = Array.isArray(c.body?.['capabilityClaims']) ? (c.body!['capabilityClaims'] as unknown[]) : [];
    for (const raw of claims) {
      if (raw == null || typeof raw !== 'object') continue;
      const claim = raw as Record<string, unknown>;
      if (claim['capability'] !== 'setup' || claim['claim'] !== 'inject') continue;
      const group = str(claim, 'group');
      const page = str(claim, 'page');
      // Deploy-time validation (validate-capabilities.ts rule 3) already refuses a setup inject
      // claim missing either half; this is a defensive re-read, not the enforcement point.
      if (!group || !page) continue;
      // CAOS-465 — the claim's declared sibling-order intent, when it set one.
      const order = int(claim, 'order');
      // CAOS-762 — the rail-entry overrides a claim may carry past what `page` alone resolves to,
      // nested under `setupNav` (kernel `capabilityClaimShape`) so a bare `label` here never
      // collides with a standalone `capability_registration`'s own top-level `label`.
      const setupNav = claim['setupNav'] != null && typeof claim['setupNav'] === 'object'
        ? (claim['setupNav'] as Record<string, unknown>)
        : undefined;
      const route = str(setupNav, 'route');
      const label = str(setupNav, 'label');
      const icon = str(setupNav, 'icon');
      const blurb = str(setupNav, 'blurb');
      setupContributions.push({
        sourceKey: c.key,
        group,
        page,
        ...(order !== undefined ? { order } : {}),
        ...(route ? { route } : {}),
        ...(label ? { label } : {}),
        ...(icon ? { icon } : {}),
        ...(blurb ? { blurb } : {}),
      });
    }
  }

  // CAOS-595 — every package's rich text toolbar button: a `rich-text` inject claim at the `toolbar` seam, read
  // off the same `package.capabilityClaims` field deploy validates. Deploy refuses a button with no action or
  // both; one that reaches here anyway is skipped rather than drawn as a button that does nothing.
  const richTextToolbar: RichTextToolbarMeta[] = [];
  for (const c of components) {
    if (c.type !== 'package' || !c.key) continue;
    const claims = Array.isArray(c.body?.['capabilityClaims']) ? (c.body!['capabilityClaims'] as unknown[]) : [];
    for (const raw of claims) {
      if (raw == null || typeof raw !== 'object') continue;
      const claim = raw as Record<string, unknown>;
      if (claim['capability'] !== 'rich-text' || claim['claim'] !== 'inject' || claim['injectionPoint'] !== 'toolbar') continue;
      const button = claim['richTextToolbar'] != null && typeof claim['richTextToolbar'] === 'object'
        ? (claim['richTextToolbar'] as Record<string, unknown>)
        : undefined;
      const key = str(button, 'key');
      const label = str(button, 'label');
      const insert = str(button, 'insert');
      const command = str(button, 'command');
      if (!key || !label || (insert === undefined) === (command === undefined)) continue;
      const icon = str(button, 'icon');
      const group = str(button, 'group');
      const order = int(claim, 'order');
      richTextToolbar.push({
        sourceKey: c.key,
        key,
        label,
        ...(icon ? { icon } : {}),
        ...(group ? { group } : {}),
        ...(order !== undefined ? { order } : {}),
        ...(insert !== undefined ? { insert } : { command: command! }),
      });
    }
  }

  return {
    apps,
    labels,
    objects,
    valueSets,
    valueSetPresentations,
    intakeBySource,
    guidanceBySource,
    anchorKindBySource,
    assistantBySource,
    listViews,
    components: componentList,
    pages,
    pageTemplates,
    setupContributions,
    rosterDetailPagesByListHref,
    richTextToolbar,
    seamOrderOverrides,
    installedPackageKeys,
  };
}

/**
 * CAOS-148 — join each package to the guidance recipes it roots, with the installed anchors those
 * recipes' steps name.
 *
 * Anchors are gathered from EVERY installed `guidanceAnchor`, not only the rooting package's: an
 * anchor is registered by whichever package provides the surface it names, and a recipe resolves
 * against the installed set. When two anchors serve one role the first in retrieve order stands.
 *
 * A recipe or anchor missing a field the kernel schema requires is dropped rather than thrown on,
 * matching every other read in this projection; the kernel refuses such a body at deploy, so one
 * arriving here is a retrieve returning something no deploy could have stored. A recipe is kept
 * only while at least one of its steps survives, because a recipe of no steps guides nobody.
 */
function projectGuidance(components: readonly RawComponent[]): Map<string, ShellGuidanceDescriptor> {
  const anchorByRole = new Map<string, ShellGuidanceAnchor>();
  const recipeByKey = new Map<string, ShellGuidanceRecipe>();
  const sectionByKey = new Map<string, ShellGuidanceSection>();
  const topicByKey = new Map<string, ShellGuidanceTopic>();
  for (const c of components) {
    if (!c.key) continue;
    if (c.type === 'guidanceAnchor') {
      const role = str(c.body, 'role');
      // CAOS-93 — `authoredStr`, not `str`: this label may now be a { default, translations } map,
      // and `str` returns undefined for one. The guard below DROPS an anchor with no label, so
      // reading it with `str` would make a translated anchor disappear rather than render in the
      // wrong language — the role it serves would stop resolving for every recipe that names it.
      const label = authoredStr(c.body, 'label');
      const icon = str(c.body, 'icon');
      const region = str(c.body, 'region');
      if (!role || !label || !icon || !region || anchorByRole.has(role)) continue;
      const selector = str(c.body, 'selector');
      anchorByRole.set(role, { role, label, icon, region, ...(selector ? { selector } : {}) });
      continue;
    }
    if (c.type === 'guidanceSection') {
      // CAOS-327 — a section with no title or icon is dropped rather than drawn as a blank
      // heading, matching how an anchor missing its parts is dropped above.
      // CAOS-93 — `authoredStr` for the two human-facing ones. `icon` stays `str`: it names icon
      // metadata rather than words anybody reads, and is not translatable.
      const title = authoredStr(c.body, 'title');
      const icon = str(c.body, 'icon');
      if (!title || !icon || sectionByKey.has(c.key)) continue;
      const blurb = authoredStr(c.body, 'blurb');
      const rawOrder = c.body?.['order'];
      const order = typeof rawOrder === 'number' && Number.isInteger(rawOrder) && rawOrder >= 0
        ? rawOrder
        : undefined;
      sectionByKey.set(c.key, {
        key: c.key,
        title,
        icon,
        ...(blurb ? { blurb } : {}),
        ...(order === undefined ? {} : { order }),
      });
      continue;
    }
    if (c.type === 'guidanceTopic') {
      // CAOS-150 — an ARTICLE. Dropped on the same terms as its siblings above: a topic with no
      // heading or no prose has nothing to draw, and the kernel refuses that body at deploy, so
      // one arriving here is a retrieve returning something no deploy could have stored.
      // CAOS-93 — both `authoredStr`. The guard on the next line drops a topic missing either, so
      // with `str` a translated ARTICLE would vanish from the guide entirely. That is strictly
      // worse than the untranslated-label case CAOS-283 fixed, where a surface at least still drew.
      const title = authoredStr(c.body, 'title');
      const topicBody = authoredStr(c.body, 'body');
      if (!title || !topicBody || topicByKey.has(c.key)) continue;
      // Every axis is read as a string or left out. A scope ENTRY that survives with no axis at all
      // is kept rather than dropped, and `topicInScope` treats it as matching nothing — the schema
      // refuses it, so the only way to get one is a body no deploy could have stored, and the safe
      // reading of that is "appears nowhere" rather than "appears on every screen".
      const rawScope = c.body?.['scope'];
      const scope = Array.isArray(rawScope)
        ? rawScope.flatMap((raw) => {
            if (!raw || typeof raw !== 'object') return [];
            const e = raw as Record<string, unknown>;
            const app = str(e, 'app');
            const object = str(e, 'object');
            const page = str(e, 'page');
            const placement = str(e, 'placement');
            return [
              {
                ...(app ? { app } : {}),
                ...(object ? { object } : {}),
                ...(page ? { page } : {}),
                ...(placement ? { placement } : {}),
              },
            ];
          })
        : undefined;
      /**
       * A `visibility` THAT IS THERE AND UNUSABLE DROPS THE TOPIC, rather than projecting as no
       * `visibility` at all. Found by review, and it was a real inconsistency with this change's
       * own stated rule rather than a theoretical one.
       *
       * `str()` collapses an empty string and a non-string to `undefined`, which everywhere else in
       * this projection means "not authored" — the right reading for a label or a blurb. For a
       * PERMISSION gate it is the wrong one and the dangerous direction: `visibility: ""` would have
       * arrived as an article visible to everybody, which is the opposite of what an author who
       * typed a gate meant. The kernel refuses `""` (`z.string().min(1)`), so this can only come
       * from a body no deploy could have stored — and the rule this change applies to every such
       * body is to take the SAFE reading, exactly as a scope entry naming nothing matches nothing
       * rather than everything.
       *
       * Dropped rather than hidden-by-sentinel so there is one mechanism: the projection already
       * drops a topic with no title and one with no body, on the same reasoning.
       */
      if ('visibility' in (c.body ?? {}) && str(c.body, 'visibility') === undefined) continue;
      const visibility = str(c.body, 'visibility');
      const supersedes = str(c.body, 'supersedes');
      const topicSection = str(c.body, 'section');
      const rawTopicOrder = c.body?.['order'];
      const topicOrder =
        typeof rawTopicOrder === 'number' && Number.isInteger(rawTopicOrder) && rawTopicOrder >= 0
          ? rawTopicOrder
          : undefined;
      topicByKey.set(c.key, {
        key: c.key,
        title,
        body: topicBody,
        ...(scope && scope.length > 0 ? { scope } : {}),
        ...(visibility ? { visibility } : {}),
        ...(supersedes ? { supersedes } : {}),
        ...(topicSection ? { section: topicSection } : {}),
        ...(topicOrder === undefined ? {} : { order: topicOrder }),
        // Filled in below, once the packages have said what they root. Projected as a tenant topic
        // here so that the one case this cannot get wrong is the default: an org's own article.
        platform: false,
      });
      continue;
    }
    if (c.type !== 'guidanceRecipe') continue;
    // CAOS-93 — `authoredStr`: `shell-frame.ts` draws `recipe.label ?? recipe.intent` as the
    // walkthrough title, so this is display text for every recipe without a label. The guard below
    // drops a recipe with no intent, so `str` would delete a translated walkthrough outright.
    const intent = authoredStr(c.body, 'intent');
    const rawSteps = c.body?.['steps'];
    if (!intent || !Array.isArray(rawSteps)) continue;
    const steps: ShellGuidanceStep[] = [];
    for (const raw of rawSteps) {
      if (!raw || typeof raw !== 'object') continue;
      const step = raw as Record<string, unknown>;
      const action = str(step, 'action');
      if (!action) continue;
      const anchor = str(step, 'anchor');
      // CAOS-93 — prose the reader sees during the walkthrough, so it reads both shapes. Unlike
      // the fields above this one is optional and ungated, so `str` would have silently dropped
      // the sentence and left the step with no explanation rather than removing the step.
      const text = authoredStr(step, 'text');
      steps.push({ action, ...(anchor ? { anchor } : {}), ...(text ? { text } : {}) });
    }
    if (steps.length === 0) continue;
    // CAOS-93 — the recipe name a reader sees. `section` stays `str`: it is a component key.
    const label = authoredStr(c.body, 'label');
    const section = str(c.body, 'section');
    recipeByKey.set(c.key, {
      key: c.key,
      ...(label ? { label } : {}),
      intent,
      steps,
      ...(section ? { section } : {}),
    });
  }

  /**
   * CAOS-150 — which topics a PACKAGE roots, gathered before any package is assembled.
   *
   * Two things need this up front rather than per package. A topic's `platform` flag is "some
   * package roots this", which is not knowable while looking at one package; and a tenant topic has
   * to be matched to the guide of whichever package roots the topic it supersedes, which means
   * knowing every package's roots before deciding where the tenant's article goes.
   */
  /**
   * TOPIC KEY → the FIRST package in retrieve order that roots it.
   *
   * A set of rooted keys would have been enough for the `platform` flag, and was what this did
   * until review. It is not enough to decide WHERE the article is read, because two packages can
   * legitimately root one key: CAOS-951 — a bare, non-namespaced key is not scoped to the package
   * that authors it, and `package-ownership.ts` carries `theme` as the worked multi-owner case,
   * read through a full claimant set precisely because a winner-takes-all index is wrong for
   * questions about removal. With a bare set, a collision put the SAME article in two guides, twice
   * over, with nothing recorded about it.
   *
   * FIRST IN RETRIEVE ORDER STANDS, which is the rule this very function already applies two cases
   * up for two anchors serving one role. The alternative — drawing it in both — is not obviously
   * wrong by declaration, since both packages did claim it; it is just undecided, and an article
   * appearing twice in a reader's guide is the kind of thing that gets noticed as a bug rather than
   * as a collision. Deciding it the same way the sibling case is decided keeps one rule in this
   * file instead of two.
   */
  const firstRooterOf = new Map<string, string>();
  for (const c of components) {
    if (c.type !== 'package' || !c.key) continue;
    const roots = Array.isArray(c.body?.['roots']) ? (c.body!['roots'] as unknown[]) : [];
    for (const r of roots) {
      if (typeof r !== 'string' || !r.startsWith('guidanceTopic:')) continue;
      const key = r.slice('guidanceTopic:'.length);
      if (!firstRooterOf.has(key)) firstRooterOf.set(key, c.key);
    }
  }
  for (const key of firstRooterOf.keys()) {
    const topic = topicByKey.get(key);
    if (topic) topicByKey.set(key, { ...topic, platform: true });
  }
  // The org's own articles: every projected topic no package claims. The guide each one belongs in
  // is decided below, by the platform topic it stands in front of.
  const tenantTopics = [...topicByKey.values()].filter((t) => !t.platform);

  const out = new Map<string, ShellGuidanceDescriptor>();
  for (const c of components) {
    if (c.type !== 'package' || !c.key) continue;
    const roots = Array.isArray(c.body?.['roots']) ? (c.body!['roots'] as unknown[]) : [];
    const recipes = roots
      .filter((r): r is string => typeof r === 'string' && r.startsWith('guidanceRecipe:'))
      .map((r) => recipeByKey.get(r.slice('guidanceRecipe:'.length)))
      .filter((r): r is ShellGuidanceRecipe => r !== undefined);
    // CAOS-150 — the articles this guide carries: the ones this package roots, in its own rooted
    // order, plus the org's own articles standing in front of any of them. A tenant topic that
    // supersedes nothing reaches no guide — see `ShellGuidanceDescriptor.topics` for why that is a
    // stated boundary rather than a dropped case.
    const rootedTopics = roots
      .filter((r): r is string => typeof r === 'string' && r.startsWith('guidanceTopic:'))
      .map((r) => r.slice('guidanceTopic:'.length))
      // First rooter stands — see `firstRooterOf`. A package that roots a key another package
      // rooted first still owns it as far as install and removal are concerned; it simply is not
      // the guide the article is READ in, because an article has to be read somewhere in
      // particular.
      .filter((key) => firstRooterOf.get(key) === c.key)
      .map((key) => topicByKey.get(key))
      .filter((t): t is ShellGuidanceTopic => t !== undefined);
    const rootedTopicKeysHere = new Set(rootedTopics.map((t) => t.key));
    const topics = [
      ...rootedTopics,
      ...tenantTopics.filter((t) => t.supersedes !== undefined && rootedTopicKeysHere.has(t.supersedes)),
    ];
    /**
     * CAOS-1917 — the standing line this package's guide is read under.
     *
     * `authoredStr`, not `str`: the field is `LocalizableText` on the kernel side, so a translated
     * note arrives as a `{ default, translations }` map and `str` would return undefined for it —
     * an org running in two languages would lose the sentence entirely rather than see it in one
     * language. That is the CAOS-93 reading every human-facing field in this projection takes.
     *
     * READ ABOVE THE GUARD BELOW, which is not where it started. An independent read found that
     * computing it after the `continue` silently dropped an AUTHORED sentence for a package whose
     * roots hold a section and no recipe or topic — a shape the kernel accepts, and the exact shape
     * this change's own kernel fixture uses. That is a different thing from "absent means absent":
     * the author wrote the line, no deploy refused it, and it reached nothing, with nowhere to look
     * to find out why.
     */
    const standingNote = authoredStr(c.body, 'guideStandingNote');
    /**
     * A package contributing NOTHING to a guide gets no descriptor — but an authored standing line
     * is a contribution.
     *
     * The guard is about a package that says nothing about the guide at all, which is most of them;
     * giving each one an empty descriptor would make `guidanceBySource` a map of blanks. A package
     * that authored a sentence has said something, and a guide that is empty today and says it
     * grows is a coherent thing to ship — it is, in fact, the case the sentence is most useful in.
     */
    if (recipes.length === 0 && topics.length === 0 && standingNote === undefined) continue;
    /**
     * The anchors this guide needs: the ones its steps point at, AND the ones its prose names.
     *
     * CAOS-1916 — the second half is not an optimisation, it is what makes an inline control work
     * at all. Only step anchors used to travel, which is right for keeping an org's unrelated
     * anchors out of a package's guide; but a `{anchor:<role>}` written inside an article's body or
     * a step's sentence is just as much a reference, and without it the token resolved against a
     * map that could not contain it. The visible result was an article that could glyph a control
     * only when some recipe step happened to name the same one, and a package of articles with no
     * recipes that could glyph nothing — the rendering being correct the whole time.
     *
     * Read from the SAME grammar the renderer draws with (`guidance-prose.ts`), so the set of roles
     * sent can never disagree with the set of tokens drawn.
     *
     * ONE THING THIS DOES NOT CHANGE, recorded because review asked the question and the answer is
     * not obvious from here. `anchorByRole` is org-wide and keyed by role alone, so a guide naming a
     * role some OTHER package registered resolves against that package's anchor. That is not new and
     * this does not widen it: an explicit `step.anchor` already resolved through the same map the
     * same way, and it follows from the anchor model's own premise — a role is a shared capability
     * id, deliberately not scoped to its author, so that a customer's own core can serve the same
     * roles. If that is ever decided to be wrong, it is one decision about `anchorByRole` and both
     * paths here change together; neither is a separate case to remember.
     */
    const named = new Set([
      ...recipes.flatMap((r) => r.steps.flatMap((s) => (s.anchor ? [s.anchor] : []))),
      ...recipes.flatMap((r) => r.steps.flatMap((s) => (s.text ? guidanceProseRoles(s.text) : []))),
      ...topics.flatMap((t) => guidanceProseRoles(t.body)),
    ]);
    const anchors = [...named].flatMap((role) => {
      const anchor = anchorByRole.get(role);
      return anchor ? [anchor] : [];
    });
    // CAOS-327 — only sections something is filed under, in the authoring package's reading
    // order. A recipe naming a section the org does not have is left unfiled rather than dropped:
    // the deploy gate already refuses that body, so one arriving here is a retrieve returning
    // something no deploy could have stored, and losing the recipe would be the worse answer.
    // CAOS-150 — a TOPIC is filed under the same CAOS-327 taxonomy a recipe is, so it brings its
    // heading along on the same terms. Without this a guide of articles alone would arrive with no
    // sections at all and read as one undifferentiated list.
    const filed = new Set([
      ...recipes.flatMap((r) => (r.section ? [r.section] : [])),
      ...topics.flatMap((t) => (t.section ? [t.section] : [])),
    ]);
    const sections = [...filed]
      .flatMap((key) => {
        const section = sectionByKey.get(key);
        return section ? [section] : [];
      })
      .sort((a, b) => {
        const ao = a.order ?? Number.MAX_SAFE_INTEGER;
        const bo = b.order ?? Number.MAX_SAFE_INTEGER;
        return ao === bo ? a.key.localeCompare(b.key) : ao - bo;
      });
    // Left ABSENT when unauthored, on the same terms as `topics` below: a package that declares
    // nothing draws no footer, and the renderer has no empty-strip case to handle. `authoredStr`
    // collapses an empty string to `undefined`, which is the right reading here — a note authored
    // as `""` is a note nobody wrote, and the kernel's `.min(1)` refuses it anyway, so one arriving
    // here is a retrieve returning a body no deploy could have stored.
    //
    // `topics` is left ABSENT when there are none, rather than set to an empty array: metadata
    // built before this field read as "no articles", and that is what no articles still means.
    out.set(c.key, {
      recipes,
      anchors,
      sections,
      ...(topics.length > 0 ? { topics } : {}),
      ...(standingNote ? { standingNote } : {}),
    });
  }
  return out;
}

/**
 * CAOS-388 — read ONE page's body into the shape a renderer draws from.
 *
 * The one rule that governs every branch here: a thing the page did not say stays unsaid. `sections`
 * that is not an array is left ABSENT, not turned into an empty list; the same for a section's
 * `items` and a placement's stored values. Filling those in with "none" would be the projection
 * inventing an answer to a question the author never got round to, and the screen would then state it
 * confidently.
 *
 * Within a declared list, a malformed entry is DROPPED rather than half-built: an item with no
 * identity, a field placement naming no field, a component placement naming no component. There is
 * nothing to draw for any of those, and a half-drawn one reads as a piece that is broken rather than
 * one that was never authored properly.
 */
function projectPage(key: string, body: Record<string, unknown> | undefined): PageMeta {
  const rawSections = body?.['sections'];
  const sections = Array.isArray(rawSections)
    ? rawSections
        .map((raw, index) => projectPageSection(raw, index))
        .filter((s): s is PageSectionMeta => s !== null)
    : undefined;
  // CAOS-388 — what feeds the page, in the page's own words. Silence means an object-kind page,
  // which is what the manifest's own default says; anything else has to have been declared.
  const rawDataSource = body?.['dataSource'] as Record<string, unknown> | undefined;
  const kind = pageKindOf(body);

  // CAOS-744 — a controlPlane page's read route + optional rows path. Both-or-nothing: a read with
  // no verb name is not a read at all, so a malformed `noun`/`verb` drops the whole binding rather
  // than feeding the renderer a route it cannot fetch.
  const controlPlaneRead = kind === 'controlPlane' ? projectRouteVerbRef(rawDataSource?.['read']) : undefined;
  const controlPlaneRowsAt = kind === 'controlPlane' ? str(rawDataSource, 'rowsAt') : undefined;

  // CAOS-1767 — a list page's object and its optional saved view. Gated on the kind for the same
  // reason the two lines above are: these positions mean something on this kind and nowhere else, so
  // reading them unconditionally would carry a key off a page that never claimed it. The object is
  // required by the schema, so a list page arriving without one is already malformed — it is read
  // the same way regardless, and an absent value stays absent rather than becoming an empty string
  // the renderer would then look up and fail to find for a second, wrong reason.
  const listObject = kind === 'list' ? str(rawDataSource, 'object') : undefined;
  const listView = kind === 'list' ? str(rawDataSource, 'listView') : undefined;

  // CAOS-744 — page-level actions. Present on any page kind (a caller-scoped action needs no object
  // binding and no roster), so this is read unconditionally rather than gated on `kind`.
  const rawRowActions = body?.['rowActions'];
  const rowActions = Array.isArray(rawRowActions)
    ? rawRowActions.map(projectRowAction).filter((a): a is PageRowActionMeta => a !== null)
    : undefined;

  // The declarations this surface does not draw. Named on the screen rather than dropped: the page
  // said it carries these, and a reader who cannot see them is entitled to know it is the surface's
  // limitation and not their manifest being wrong.
  const undrawn: string[] = [];
  const declaresList = (k: string): boolean => Array.isArray(body?.[k]) && (body[k] as unknown[]).length > 0;
  if (declaresList('relatedLists')) undrawn.push('related lists');
  if (declaresList('compactLayout')) undrawn.push('a highlights band');
  // CAOS-559 — a surface DEFINES its actions inline or PLACES them by key, so either one means
  // "this page says it carries buttons". Checking only `actions` would have let a page that
  // placed them by key go quiet about buttons it does not draw — the same unreported gap this
  // list exists to close. The two are mutually exclusive, so this reports once either way.
  if (declaresList('actions') || declaresList('actionKeys')) undrawn.push('header buttons');

  // CAOS-1908 — the reader's controls for this page's roster. Projected whole rather than key by key:
  // every member is optional and host-interpreted, the kernel has already refused the illegal
  // combinations on both validators, and re-deriving its shape here would be a second place for the
  // vocabulary to drift from the metatype that defines it.
  const rosterControls = projectRosterControls(body?.['rosterControls']);

  return {
    key,
    label: str(body, 'label') ?? key,
    ...(str(body, 'object') ? { object: str(body, 'object')! } : {}),
    kind,
    template: str(body, 'template') ?? 'standard',
    ...(sections ? { sections } : {}),
    ...(controlPlaneRead ? { controlPlaneRead } : {}),
    ...(controlPlaneRowsAt ? { controlPlaneRowsAt } : {}),
    ...(listObject ? { listObject } : {}),
    ...(listView ? { listView } : {}),
    ...(rowActions && rowActions.length > 0 ? { rowActions } : {}),
    ...(str(body, 'rowActionsTarget') ? { rowActionsTarget: str(body, 'rowActionsTarget')! } : {}),
    ...(rosterControls ? { rosterControls } : {}),
    undrawn,
  };
}

/**
 * CAOS-1908 — read a page's `rosterControls`, or `undefined` when it declares none.
 *
 * Shape-checked rather than cast. The kernel's validators have already refused an illegal
 * combination before anything deploys, so this is not a second gate and does not try to be one — but
 * the host also draws metadata it did not author and must not throw on a payload it did not expect,
 * which is why each member is read with its own type test and a value of the wrong type is simply
 * dropped. A dropped control draws as "that control is not offered", which is the same thing the
 * author would see if they had not declared it; a thrown projection takes the whole page down.
 *
 * Returns `undefined` — not an empty object — when nothing usable survives, because
 * {@link renderControlPlaneRoster} branches on presence, and an empty object would send a page with
 * no controls down the collection path and silently change its reading from table to card.
 */
function projectRosterControls(raw: unknown): PageRosterControlsMeta | undefined {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const r = raw as Record<string, unknown>;

  const text = (k: string): string | undefined => (typeof r[k] === 'string' && r[k] !== '' ? (r[k] as string) : undefined);
  const strings = (k: string): readonly string[] | undefined => {
    const v = r[k];
    if (!Array.isArray(v)) return undefined;
    const out = v.filter((x): x is string => typeof x === 'string' && x !== '');
    return out.length > 0 ? out : undefined;
  };
  const labelled = (k: string): readonly { readonly value: string; readonly label: string }[] | undefined => {
    const v = r[k];
    if (!Array.isArray(v)) return undefined;
    const out = v
      .filter((x): x is Record<string, unknown> => x != null && typeof x === 'object')
      .filter((x) => typeof x['value'] === 'string' && typeof x['label'] === 'string')
      .map((x) => ({ value: x['value'] as string, label: x['label'] as string }));
    return out.length > 0 ? out : undefined;
  };
  const tabs = (): readonly PageRosterFilterMeta[] | undefined => {
    const v = r['filters'];
    if (!Array.isArray(v)) return undefined;
    const out = v
      .filter((x): x is Record<string, unknown> => x != null && typeof x === 'object')
      .filter((x) => typeof x['label'] === 'string')
      .map((x) => ({
        label: x['label'] as string,
        // `equals: null` is the "All" tab and is a MEANINGFUL value, so it is carried through rather
        // than treated as absent — dropping it would turn the All tab into one that narrows by
        // nothing while still being drawn, which is the exact mis-authoring the kernel refuses.
        ...('equals' in x && (typeof x['equals'] === 'string' || x['equals'] === null)
          ? { equals: x['equals'] as string | null }
          : {}),
        ...(typeof x['contains'] === 'string' ? { contains: x['contains'] } : {}),
        ...(typeof x['column'] === 'string' ? { column: x['column'] } : {}),
      }));
    return out.length > 0 ? out : undefined;
  };

  const mode = r['defaultMode'];
  const out: PageRosterControlsMeta = {
    ...(text('searchPlaceholder') ? { searchPlaceholder: text('searchPlaceholder')! } : {}),
    ...(strings('searchFields') ? { searchFields: strings('searchFields')! } : {}),
    ...(text('searchEmptyLabel') ? { searchEmptyLabel: text('searchEmptyLabel')! } : {}),
    ...(text('emptyLabel') ? { emptyLabel: text('emptyLabel')! } : {}),
    ...(labelled('sortOptions') ? { sortOptions: labelled('sortOptions')! } : {}),
    ...(labelled('stages') ? { stages: labelled('stages')! } : {}),
    ...(text('stageField') ? { stageField: text('stageField')! } : {}),
    ...(tabs() ? { filters: tabs()! } : {}),
    ...(text('filterField') ? { filterField: text('filterField')! } : {}),
    ...(typeof r['filterDefault'] === 'number' && Number.isInteger(r['filterDefault']) && r['filterDefault'] >= 0
      ? { filterDefault: r['filterDefault'] }
      : {}),
    ...(text('titleField') ? { titleField: text('titleField')! } : {}),
    // CAOS-1912 — the row's destination. Typed on PageRosterControlsMeta is not enough: every member
    // here has to be LIFTED out of the raw body as well, and a key that is only typed reads as
    // `undefined` at render time no matter what the author wrote, which looks like the renderer
    // ignoring the metadata rather than the projection dropping it.
    //
    // The `{id}` requirement is NOT re-checked here. The kernel refuses a template without the token
    // on both the parse path and the precompiled validator, so a deployed page cannot carry one; and
    // if an undeployed body somehow did, `replaceAll` would simply leave the path alone and every row
    // would share one href — a visibly wrong link, not a thrown page. Re-asserting a kernel rule in
    // the host would make the host the second author of the same constraint, which is how the two
    // drift.
    ...(text('detailRoute') ? { detailRoute: text('detailRoute')! } : {}),
    ...(typeof mode === 'string' && (['card', 'table', 'kanban', 'split', 'group'] as const).includes(mode as never)
      ? { defaultMode: mode as PageRosterControlsMeta['defaultMode'] }
      : {}),
  };
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * CAOS-388 — what feeds a page, in the page's own words. Silence means a record page, which is what
 * the manifest's own default says; anything else has to have been declared.
 */
function pageKindOf(body: Record<string, unknown> | undefined): PageMeta['kind'] {
  // CAOS-1767 — `list` joins the named kinds. This function and the record-page binding filter in
  // `kernel/src/deploy/validate.ts` are a PAIR: that filter reproduces this one's rule so the gate
  // and the renderer resolve the same template for the same layout, and its own comment says so.
  // They are edited together, always — a kind named here and not there means the deploy is judged
  // against one blueprint and the screen drawn against another.
  const rawKind = (body?.['dataSource'] as Record<string, unknown> | undefined)?.['kind'];
  return rawKind === 'content' ||
    rawKind === 'intake' ||
    rawKind === 'controlPlane' ||
    rawKind === 'appLanding' ||
    rawKind === 'list'
    ? rawKind
    : 'object';
}

/** CAOS-744 — read a bare (noun, verb) route reference; `null` when either name is missing. */
function projectRouteVerbRef(raw: unknown): RouteVerbRefMeta | null {
  if (raw == null || typeof raw !== 'object') return null;
  const rec = raw as Record<string, unknown>;
  const noun = rec['noun'];
  const verb = rec['verb'];
  return typeof noun === 'string' && noun.length > 0 && typeof verb === 'string' && verb.length > 0
    ? { noun, verb }
    : null;
}

/**
 * CAOS-744 — read one row/page action. Dropped (never half-built) when it names no id/label/verb, or
 * when it carries exactly ONE of `param`/`paramFrom` — that pairing is meaningful (row-scoped vs
 * caller-scoped, see {@link PageRowActionMeta}'s doc) and a lone one of the two names an incomplete
 * binding this projection refuses to guess the other half of.
 */
function projectRowAction(raw: unknown): PageRowActionMeta | null {
  if (raw == null || typeof raw !== 'object') return null;
  const rec = raw as Record<string, unknown>;
  const id = str(rec, 'id');
  const label = str(rec, 'label');
  const verb = projectRouteVerbRef(rec['verb']);
  if (!id || !label) return null;

  // CAOS-1616 — the opening arm. A destination is taken only when BOTH halves are present, on the
  // same rule `to` follows on a record action: half an address is not an address, and guessing the
  // other half is how a link goes somewhere nobody authored. The two arms are mutually exclusive
  // here as they are in the schema, so an action carrying both is refused rather than resolved by
  // precedence — a precedence rule would make which one wins an implementation detail.
  const destination = projectActionDestination(rec['destination']);
  if (destination && verb) return null; // both ⇒ two answers to "what does this do", and no rule
  if (destination) {
    return {
      id,
      label,
      destination,
      ...(str(rec, 'intent') ? { intent: str(rec, 'intent')! } : {}),
      ...(str(rec, 'appliesWhen') ? { appliesWhen: str(rec, 'appliesWhen')! } : {}),
    };
  }
  if (!verb) return null; // neither ⇒ a button that does nothing
  const param = str(rec, 'param');
  const paramFrom = str(rec, 'paramFrom');
  if ((param === undefined) !== (paramFrom === undefined)) return null; // one without the other ⇒ drop
  const rawBody = rec['body'];
  const isLiteral = (v: unknown): v is string | number | boolean | null =>
    typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean' || v === null;
  const body: Record<string, string | number | boolean | null> | undefined =
    rawBody != null && typeof rawBody === 'object' && !Array.isArray(rawBody)
      ? Object.fromEntries(
          Object.entries(rawBody as Record<string, unknown>).filter(
            (entry): entry is [string, string | number | boolean | null] => isLiteral(entry[1]),
          ),
        )
      : undefined;
  const collect = projectCollectedFields(rec['collect']);
  const confirm = projectActionConfirmation(rec['confirm']);
  return {
    id,
    label,
    verb,
    ...(param !== undefined ? { param, paramFrom: paramFrom! } : {}),
    ...(body && Object.keys(body).length > 0 ? { body } : {}),
    ...(collect ? { collect } : {}),
    ...(confirm ? { confirm } : {}),
    ...(str(rec, 'intent') ? { intent: str(rec, 'intent')! } : {}),
    ...(str(rec, 'appliesWhen') ? { appliesWhen: str(rec, 'appliesWhen')! } : {}),
  };
}

/**
 * CAOS-1892 — read the values an action asks for, dropping any that could not be drawn as a control
 * at all, and the whole list when none survives.
 *
 * WHAT IS DROPPED AND WHAT IS NOT, because the two have opposite failure modes. A member with no
 * `path`, no `label` or no `control` is not a partially-authored field, it is not a field: there is
 * nowhere to put the value, nothing to call it, or no way to edit it. Those go, on the same
 * "never half-built" rule {@link projectRowAction} applies to a lone `param`.
 *
 * A PICKLIST WITH NO OPTIONS IS KEPT, deliberately, and this is the asymmetry worth stating. The
 * deploy gate `validateCollectedActionValues` refuses that authoring, so it reaches here only from
 * metadata deployed before the gate existed — and the useful thing to do with it is not to make it
 * vanish. An absent control is indistinguishable from an action that never asked for the value,
 * which is how somebody comes to believe a form is complete; the drawing side keeps it, disables it
 * and explains itself, exactly as CAOS-1616 decided for a row action whose address will not
 * resolve.
 */
function projectCollectedFields(raw: unknown): readonly PageCollectedFieldMeta[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const fields: PageCollectedFieldMeta[] = [];
  for (const entry of raw) {
    if (entry == null || typeof entry !== 'object') continue;
    const rec = entry as Record<string, unknown>;
    const path = str(rec, 'path');
    const label = str(rec, 'label');
    const control = str(rec, 'control');
    if (!path || !label || !control) continue;
    const rawOptions = rec['options'];
    const options = Array.isArray(rawOptions)
      ? rawOptions
          .map((o): { value: string; label: string } | null => {
            if (o == null || typeof o !== 'object') return null;
            const or = o as Record<string, unknown>;
            const value = str(or, 'value');
            const optionLabel = str(or, 'label');
            return value && optionLabel ? { value, label: optionLabel } : null;
          })
          .filter((o): o is { value: string; label: string } => o !== null)
      : undefined;
    fields.push({
      path,
      label,
      control,
      ...(rec['required'] === true ? { required: true } : {}),
      ...(str(rec, 'placeholder') ? { placeholder: str(rec, 'placeholder')! } : {}),
      ...(options && options.length > 0 ? { options } : {}),
    });
  }
  return fields.length > 0 ? fields : undefined;
}

/** CAOS-1892 — read an action's confirmation. Dropped when it carries no message, because a
 *  confirmation with nothing to say is the generic "Are you sure?" the schema exists to refuse. */
function projectActionConfirmation(raw: unknown): PageActionConfirmationMeta | undefined {
  if (raw == null || typeof raw !== 'object') return undefined;
  const rec = raw as Record<string, unknown>;
  const message = str(rec, 'message');
  if (!message) return undefined;
  return {
    message,
    ...(str(rec, 'confirmLabel') ? { confirmLabel: str(rec, 'confirmLabel')! } : {}),
  };
}

/** CAOS-388 — read one section of a page; `null` when there is not enough of it to draw. */
function projectPageSection(raw: unknown, index: number): PageSectionMeta | null {
  if (raw == null || typeof raw !== 'object') return null;
  const rec = raw as Record<string, unknown>;
  const id = str(rec, 'id') ?? `section-${index + 1}`;
  const rawItems = rec['items'];
  const items = Array.isArray(rawItems)
    ? rawItems.map(projectPageItem).filter((i): i is PageItemMeta => i !== null)
    : undefined;
  const columns = rec['columns'];
  const declaredColumns = typeof columns === 'number' && Number.isInteger(columns) && columns >= 1
    ? columns
    : undefined;
  return {
    id,
    // The Page manifest calls the heading `section`; the surface calls it a label, because that is
    // what it is on the screen. Absent stays absent — an untitled block is drawn without a heading
    // rather than with an empty one.
    ...(str(rec, 'section') ?? str(rec, 'label') ? { label: (str(rec, 'section') ?? str(rec, 'label'))! } : {}),
    // CAOS-388 — no invented default. A section that did not say how many columns it wants has not
    // said "two"; the renderer applies the display default and the projection stays silent.
    ...(declaredColumns !== undefined ? { columns: declaredColumns } : {}),
    ...(str(rec, 'region') ? { region: str(rec, 'region')! } : {}),
    ...(items ? { items } : {}),
  };
}

/** CAOS-388 — read one placed item; `null` when it names nothing that could be drawn. */
function projectPageItem(raw: unknown): PageItemMeta | null {
  if (raw == null || typeof raw !== 'object') return null;
  const rec = raw as Record<string, unknown>;
  const id = str(rec, 'id');
  if (!id) return null;
  if (rec['type'] === 'field') {
    const fieldKey = str(rec, 'field');
    if (!fieldKey) return null;
    return {
      kind: 'field',
      id,
      fieldKey,
      field: bareFieldName(fieldKey),
      ...(str(rec, 'uiBehavior') ? { uiBehavior: str(rec, 'uiBehavior')! } : {}),
    };
  }
  if (rec['type'] === 'component') {
    const componentKey = str(rec, 'key');
    if (!componentKey) return null;
    // The stored values live under `inputs` (renamed from `props` in CAOS-387, on both the
    // declaring and placement sides — see ComponentInstanceSchema in kernel/src/meta/page.ts).
    const inputs = rec['inputs'];
    const rawRowActions = rec['rowActions'];
    const rowActions = Array.isArray(rawRowActions)
      ? rawRowActions.map(projectRowAction).filter((a): a is PageRowActionMeta => a !== null)
      : undefined;
    return {
      kind: 'component',
      id,
      key: componentKey,
      // Absent stays absent: a placement that set nothing is not a placement that declared an empty
      // set, even though both end up drawing the component's own defaults.
      ...(inputs != null && typeof inputs === 'object' && !Array.isArray(inputs)
        ? { props: inputs as Record<string, unknown> }
        : {}),
      ...(rowActions && rowActions.length > 0 ? { rowActions } : {}),
      ...(str(rec, 'slot') ? { slot: str(rec, 'slot')! } : {}),
      ...((): { read?: PageReadMeta } => {
        const read = projectPlacedRead(rec['read']);
        return read ? { read } : {};
      })(),
      // CAOS-1576 — the stored report this piece shows a figure for. Read with the same `str`
      // everything else here uses, so an empty or non-string value is absent rather than a fetch of
      // `/api/data/report?report=undefined`.
      ...(str(rec, 'report') ? { report: str(rec, 'report')! } : {}),
      ...((): { write?: PageWriteMeta } => {
        const write = projectPlacedWrite(rec['write']);
        return write ? { write } : {};
      })(),
      ...(Array.isArray(rec['children']) ? { children: projectPlacedChildren(rec['children']) } : {}),
    };
  }
  // CAOS-498 — another page drawn inside this one. Read with the same `str` everything else here
  // uses, so an embed naming nothing is carried as unknown below rather than projected with an
  // empty key and turned into a lookup for the page called "".
  // CAOS-492 — or a ROSTER drawn inside it. The roster arm is read FIRST so that a body carrying
  // both — which neither gate admits, but this projection is handed whatever is installed, including
  // metadata written by a newer kernel than this workspace runs — resolves to the kind that cannot be
  // confused for the other. An embed naming neither still falls through to `unknown` below and is
  // NAMED on the screen, which is the one behaviour this whole function exists to preserve.
  if (rec['type'] === 'embed') {
    const rosterPageKey = str(rec, 'rosterPage');
    if (rosterPageKey) return { kind: 'embed', id, rosterPageKey };
    const pageKey = str(rec, 'page');
    if (pageKey) return { kind: 'embed', id, pageKey };
  }
  // CAOS-388 — an item type this workspace has no renderer for is carried through as unknown so the
  // screen can name it, instead of being dropped where nobody would ever find it.
  return { kind: 'unknown', id, type: str(rec, 'type') ?? '(unnamed)' };
}

/**
 * CAOS-654 — read what is placed inside a placed piece. Text stays text. A piece is read exactly as one
 * placed directly in a section. A FIELD has no meaning inside a piece — a field is read from the
 * record, never held by a piece, and the manifest schema refuses one there — so a workspace handed one
 * anyway carries it as unknown and the screen names it, rather than guessing. Something that names
 * nothing at all (no id) is dropped, as it is from a section.
 *
 * CAOS-498 — an EMBED is treated exactly as a field is here, and for the same reason rather than by
 * analogy: `ComponentInstanceSchema.children` admits text and further placed components and nothing
 * else, so the manifest schema refuses an embed inside a piece just as it refuses a field. A page
 * belongs to a SECTION, not to the inside of a button. A workspace handed one anyway names it
 * instead of drawing a whole page in a slot that was sized for a label.
 */
function projectPlacedChildren(raw: readonly unknown[]): PagePlacedChildMeta[] {
  const out: PagePlacedChildMeta[] = [];
  for (const child of raw) {
    if (typeof child === 'string') {
      out.push(child);
      continue;
    }
    const item = projectPageItem(child);
    if (item === null) continue;
    out.push(
      item.kind === 'field' || item.kind === 'embed'
        ? { kind: 'unknown', id: item.id, type: item.kind }
        : item,
    );
  }
  return out;
}

/**
 * CAOS-324 — Setup's own HOME tab, the one tab the Setup app carries. Everything else Setup does is
 * a section of its console, reached from the left rail.
 */
const SETUP_HOME_TAB: ShellNavTab = {
  key: '',
  // CAOS-378 — a Home destination carries a HOUSE, wherever it appears. A gear here said "settings",
  // which is what the whole app is; it told you nothing about the one place the tab goes.
  label: 'Home',
  icon: 'house',
};

/** CAOS-372 — the My Profile app's one nav-strip entry; its sections are in the console rail. */
const MY_PROFILE_HOME_TAB: ShellNavTab = {
  key: '',
  label: 'Home',
  icon: 'house',
};

